import * as i0 from '@angular/core';
import { EventEmitter, Directive, Output, Input, ViewChild, forwardRef, Component, NgModule } from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS } from '@angular/forms';
import { isAndroid, isChromeOS, isIOS14OrEarlier, isSamsung, isEdge, getCssRuleValue, getCssValue, toNumber, htmlEscape, noop, isString, isNumber, toBoolean, isEqual, processMillis, isChrome, eventToKey, clone, convertDigitsToAscii, isArray, repeat, last, first, isObject, convertDigits, isIOS, padLeft, getTextWidth, getFontMetrics } from '@tubular/util';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { floor, random, sign, max, min, round, abs, trunc, mod, ceil, mod2, div_rd } from '@tubular/math';
import { timer } from 'rxjs';
import * as i1 from '@angular/platform-browser';
import { BrowserModule } from '@angular/platform-browser';
import ttime, { defaultLocale, hasIntlDateTime, DateTime, getStartOfWeek, CalendarType, isDate, Timezone, DateTimeField, utToTaiMillis, getISOFormatDate, newDateTimeFormat, parseISODateTime } from '@tubular/time';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import { addResizeListener, removeResizeListener } from 'detect-resize';
import { debounce } from 'lodash';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

function getXYForTouchEvent(evt, index = 0) {
    const touches = evt.touches;
    if (touches.length <= index)
        return { x: -1, y: -1 };
    const rect = touches[index].target.getBoundingClientRect();
    return { x: touches[index].clientX - rect.left, y: touches[0].clientY - rect.top };
}
function getClientXYForTouchEvent(evt, index = 0) {
    const touches = evt.touches;
    const x = touches[index]?.clientX;
    const y = touches[index]?.clientY;
    if (x != null && y != null)
        return { x, y };
    return { x: -1, y: -1 };
}
function getPageXYForTouchEvent(evt, index = 0) {
    if (index === 0 && evt.pageX != null && evt.pageY)
        return { x: evt.pageX, y: evt.pageY };
    const touches = evt.touches;
    const x = touches[index]?.pageX;
    const y = touches[index]?.pageY;
    if (x != null && y != null)
        return { x, y };
    const result = getXYForTouchEvent(evt, index);
    if (touches.length > index && touches[index].target) {
        const r = touches[index].target.getBoundingClientRect();
        result.x += r.left;
        result.y += r.top;
    }
    return result;
}

const FORWARD_TAB_DELAY = 250;
const FALSE_REPEAT_THRESHOLD = 50;
const KEY_REPEAT_DELAY = 500;
const KEY_REPEAT_RATE = 100;
const FLASH_DURATION = 250;
const LONG_WARNING_DURATION = 2000;
const DIGIT_SWIPE_THRESHOLD = 6;
const LOSE_FOCUS_DELAY = 500;
const MAX_DIGIT_SWIPE = 0.9;
const MIN_DIGIT_SWIPE = 0.33;
const MIN_SWIPE_TIME = 200;
const SWIPE_SMOOTHING_WINDOW = 500;
const NO_SELECTION = -1;
const SPIN_UP = -2;
const SPIN_DOWN = -3;
const alternateClipboard = navigator.clipboard == null || isAndroid() || isChromeOS() || isIOS14OrEarlier() || isSamsung();
const checkForRepeatedKeyTimestamps = isIOS14OrEarlier();
const disableContentEditable = isEdge();
const useHiddenInput = isAndroid() || isChromeOS();
function getBackgroundColor(className, defaultColor, darkMode = false) {
    const outer = document.createElement('div');
    const elem = document.createElement('div');
    if (darkMode)
        outer.classList.add('tbw-dark-mode');
    document.body.appendChild(outer);
    elem.classList.add(className);
    outer.appendChild(elem);
    let result = getCssRuleValue(elem, 'background-color');
    document.body.removeChild(outer);
    if (result === 'transparent' || result === 'rgba(0, 0, 0, 0)')
        result = defaultColor;
    return result;
}
function getBackgroundLevel(elem) {
    if (!elem)
        return 255;
    const color = getCssValue(elem, 'background-color');
    if (color === 'transparent')
        return elem.parentElement ? getBackgroundLevel(elem.parentElement) : 255;
    const [r, g, b, a] = (/(\d+)\b.*?\b(\d+).*?\b(\d+)(?:.*?\b(\d+))?/.exec(color) ?? [0, 0, 0, 0, 0])
        .map(n => toNumber(n, 255)).slice(1);
    if (a === 0)
        return elem.parentElement ? getBackgroundLevel(elem.parentElement) : 255;
    let level = r * 0.21 + g * 0.72 + b * 0.07;
    if (a != null && a !== 255 && elem.parentElement)
        level = (a * level + (255 - a) * getBackgroundLevel(elem.parentElement)) / 255;
    return level;
}
const CONFIRM_BACKGROUND = 'tbw-confirm-background';
const DISABLED_ARROW_COLOR = 'tbw-disabled-arrow-color';
const DISABLED_BACKGROUND = 'tbw-disabled-background';
const DISABLED_TEXT = 'tbw-disabled-text';
const ERROR_BACKGROUND = 'tbw-error-background';
const INDICATOR_TEXT = 'tbw-indicator-text';
const NORMAL_BACKGROUND = 'tbw-normal-background';
const NORMAL_TEXT = 'tbw-normal-text';
// const SELECTED_BACKGROUND  = 'tbw-selected-background';
const SELECTED_TEXT = 'tbw-selected-text';
const VIEW_ONLY_TEXT = 'tbw-view-only-text';
const VIEW_ONLY_BACKGROUND = 'tbw-view-only-background';
const WARNING_BACKGROUND = 'tbw-warning-background';
const DOT_DOT_TYPING_INTERVAL = 500;
class DynamicColor {
    constructor(key, defaultColor) {
        this.key = key;
        DynamicColor.colorMap[key] = defaultColor;
    }
    static update(key, darkMode = false) {
        DynamicColor.colorMap[key] = getBackgroundColor(key, DynamicColor.colorMap[key], darkMode);
    }
    toString() {
        return DynamicColor.colorMap[this.key];
    }
}
DynamicColor.colorMap = {};
const BACKGROUND_ANIMATIONS = trigger('displayState', [
    state('error', style({ backgroundColor: new DynamicColor(ERROR_BACKGROUND, '#F67') })),
    state('normal', style({ backgroundColor: 'transparent' })),
    state('refresh', style({ backgroundColor: 'rgba(0, 0, 0, 0.01)' })),
    state('confirm', style({ backgroundColor: new DynamicColor(CONFIRM_BACKGROUND, '#6C6') })),
    state('warning', style({ backgroundColor: new DynamicColor(WARNING_BACKGROUND, '#FC6') })),
    transition('normal => error', animate(FLASH_DURATION)),
    transition('error => normal', animate(FLASH_DURATION)),
    transition('normal => confirm', animate(FLASH_DURATION)),
    transition('confirm => normal', animate(FLASH_DURATION)),
    transition('warning => error', animate(FLASH_DURATION)),
    transition('error => warning', animate(FLASH_DURATION)),
]);
function getThePoint(evt) {
    if (evt.pageX != null)
        return { x: evt.pageX, y: evt.pageY };
    else {
        return getPageXYForTouchEvent(evt);
    }
}
function isNilOrBlank(v) {
    return v == null || v === '';
}
let This$1;
// @dynamic
class DigitSequenceEditorDirective {
    constructor(zone, cdr, sanitizer) {
        this.zone = zone;
        this.cdr = cdr;
        this.sanitizer = sanitizer;
        // Template accessibility
        this.disableContentEditable = disableContentEditable;
        this.htmlEscape = htmlEscape;
        this.SPIN_DOWN = SPIN_DOWN;
        this.SPIN_UP = SPIN_UP;
        // ControlValueAccessor/Validator-related fields
        this.afterViewInit = false;
        this.pendingValueChange = false;
        this.changed = noop;
        this._disabled = false;
        this._disableMobileKeyboard = false;
        this._fakeUrl = false;
        this.lastValue = null;
        this.touched = noop;
        this.valid = true;
        this._validateAll = false;
        this._value = null;
        this._wideSpinner = false;
        this.valueChange = new EventEmitter();
        this.activeSpinner = NO_SELECTION;
        this.getCharFromInputEvent = false;
        this.hasHiddenInputFocus = false;
        this.hasWrapperFocus = false;
        this.ignoreFocus = 0;
        this.initialTouchTime = 0;
        this.lastDelta = 1;
        this._position = { x: 0, y: 0 };
        this.touchDeltaY = 0;
        this.touchDeltaYs = [];
        this.touchDeltaTimes = [];
        this._blank = false;
        this._cursorRate = null;
        this.darkMode = false;
        this._floating = false;
        this.lastDecimalTime = 0;
        this.lastTabTime = 0;
        this.letterDecrement = 'z';
        this.letterIncrement = 'a';
        this.selectionHidden = false;
        this._showCloser = false;
        this.showFocus = false;
        this.swipeIndex = -1;
        this._tabindex = '0';
        this._viewOnly = false;
        this.baselineShift = '0';
        this.buttons = [];
        // eslint-disable-next-line @angular-eslint/no-output-native
        this.close = new EventEmitter();
        this.digitHeight = 17;
        this.displayItems = [];
        this.displayState = 'normal';
        this.floatZ = 1;
        this.hasFocus = false;
        this.headerDx = 0;
        this.headerDy = 0;
        this.headerX = 0;
        this.headerY = 0;
        this.items = [];
        this.rtl = false;
        this.selection = 0;
        this.showPasteInput = false;
        this.smoothedDeltaY = 0;
        this.useAlternateTouchHandling = false;
        this.positionChange = new EventEmitter();
        This$1.instances.add(this);
        if (!This$1.mutationObserver) {
            This$1.mutationObserver = new MutationObserver(mutations => {
                let modeChanged = false;
                for (const mutation of mutations) {
                    const wasDark = /\btbw-dark-mode\b/.test(mutation.oldValue || '');
                    const isDark = /\btbw-dark-mode\b/.test(mutation.target?.className || '');
                    if (wasDark !== isDark) {
                        modeChanged = true;
                        break;
                    }
                }
                if (modeChanged)
                    setTimeout(() => This$1.instances.forEach(instance => instance.checkDarkMode(true)));
            });
            This$1.mutationObserver.observe(document.body, { subtree: true, attributeFilter: ['class'], attributeOldValue: true });
        }
        if (This$1.instances.size === 1) {
            document.addEventListener('mousedown', This$1.mouseDownOrTouch);
            document.addEventListener('touchstart', This$1.mouseDownOrTouch);
            document.addEventListener('mouseup', This$1.headerDragEnd);
            document.addEventListener('touchend', This$1.headerDragEnd);
            document.addEventListener('touchcancel', This$1.headerDragEnd);
            window.addEventListener('resize', This$1.assureAllFullyOnScreen);
            zone.runOutsideAngular(() => {
                document.addEventListener('mousemove', This$1.headerDrag);
                document.addEventListener('touchmove', This$1.headerDrag, { passive: false });
            });
        }
    }
    static setDefaultCursorRate(value) {
        this.defaultCursorRate = value && isString(value) ? value : (isNumber(value) ? value + 'ms' : null);
    }
    // Angular lifecycle
    ngOnInit() {
        this.wrapper = this.wrapperRef.nativeElement;
        this.emSizer = this.emSizerRef.nativeElement;
        this.wrapper.addEventListener('copy', evt => {
            evt.preventDefault();
            const text = this.getClipboardText();
            if (text) {
                evt.clipboardData.setData('text/plain', text);
                this.confirmFlash();
            }
        });
        this.wrapper.addEventListener('paste', evt => {
            evt.preventDefault();
            this.doPaste(evt.clipboardData.getData('text/plain'));
        });
        this.createDigits();
        this.createButtons();
        this.createDisplayOrder();
        this.createHiddenInput();
    }
    ngAfterViewInit() {
        this.checkDarkMode();
        setTimeout(() => {
            this.afterViewInit = true;
            if (this.pendingValueChange) {
                this.pendingValueChange = false;
                this.valueHasChanged(true);
            }
        });
    }
    ngOnDestroy() {
        this.stopKeyTimer();
        this.stopClickTimer();
        This$1.instances.delete(this);
        if (This$1.mutationObserver && This$1.instances.size === 0) {
            This$1.mutationObserver.disconnect();
            This$1.mutationObserver = undefined;
        }
        if (This$1.instances.size === 0) {
            document.removeEventListener('mousedown', This$1.mouseDownOrTouch);
            document.removeEventListener('touchstart', This$1.mouseDownOrTouch);
            document.removeEventListener('mousemove', This$1.headerDrag);
            document.removeEventListener('touchmove', This$1.headerDrag);
            document.removeEventListener('mouseup', This$1.headerDragEnd);
            document.removeEventListener('touchend', This$1.headerDragEnd);
            document.removeEventListener('touchcancel', This$1.headerDragEnd);
            window.removeEventListener('resize', This$1.assureAllFullyOnScreen);
        }
    }
    // ControlValueAccessor interface
    writeValue(obj) {
        this.value = obj;
    }
    registerOnChange(fn) {
        this.changed = fn;
    }
    registerOnTouched(fn) {
        this.touched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this.adjustState();
    }
    // ControlValueAccessor support
    get disabled() { return this._disabled; }
    set disabled(value) {
        if (isString(value))
            value = toBoolean(value, false, true);
        this._disabled = value;
        this.adjustState();
    }
    onBlur() {
        this.touched();
    }
    get value() {
        return this._value;
    }
    set value(value) {
        if (isNilOrBlank(value) && isNilOrBlank(this._value))
            return;
        let forceChange = false;
        if (value === undefined) {
            value = null;
            forceChange = true;
        }
        if (this._value !== value) {
            this._value = value;
            this.valueHasChanged();
        }
        else if (forceChange)
            this.valueHasChanged(true);
    }
    valueHasChanged(forceChange = false) {
        if (forceChange || this.lastValue !== this.value) {
            this.lastValue = this.value;
            if (this.afterViewInit) {
                this.reportValueChange();
                this.valueChange.emit(this.value);
            }
            else
                this.pendingValueChange = true;
        }
    }
    reportValueChange() {
        this.changed(this.value);
    }
    // Validator interface
    validate(control) {
        const validation = this.validateImpl(this._value, control);
        this.valid = !validation;
        return validation;
    }
    validateImpl(_value, _control) {
        return null;
    }
    // DigitSequenceEditorDirective specifics
    get cursorRate() { return this._cursorRate || DigitSequenceEditorDirective.defaultCursorRate; }
    set cursorRate(value) {
        this._cursorRate = value && isString(value) ? value : (isNumber(value) ? value + 'ms' : value);
    }
    get blank() { return this._blank; }
    set blank(newValue) {
        if (isString(newValue))
            newValue = toBoolean(newValue, false, true);
        this._blank = newValue;
    }
    get floating() { return this._floating; }
    set floating(newValue) {
        if (isString(newValue))
            newValue = toBoolean(newValue, false, true);
        this._floating = newValue;
    }
    get tabindex() { return this._tabindex; }
    set tabindex(newValue) {
        if (this._tabindex !== newValue) {
            this._tabindex = newValue;
            this.adjustState();
        }
    }
    get disableMobileKeyboard() { return this._disableMobileKeyboard; }
    set disableMobileKeyboard(newValue) {
        if (isString(newValue))
            newValue = toBoolean(newValue, false, true);
        if (this._disableMobileKeyboard !== newValue) {
            this._disableMobileKeyboard = newValue;
            this.adjustState();
        }
    }
    get viewOnly() { return this._viewOnly; }
    set viewOnly(newValue) {
        if (isString(newValue))
            newValue = toBoolean(newValue, false, true);
        this._viewOnly = newValue;
        this.adjustState();
    }
    get inputOff() { return this._disabled || this._viewOnly || this._floating || this._disableMobileKeyboard; }
    get validateAll() { return this._validateAll; }
    set validateAll(newValue) {
        if (isString(newValue))
            newValue = toBoolean(newValue, false, true);
        if (this._validateAll !== newValue) {
            this._validateAll = newValue;
            if (newValue && !this.valid)
                this.reportValueChange();
        }
    }
    get position() { return this._position; }
    ;
    set position(newValue) {
        if (!isEqual(this._position, newValue)) {
            this.headerX = newValue?.x ?? 0;
            this.headerY = newValue?.y ?? 0;
            this._position = { x: this.headerX, y: this.headerY };
            this.positionChange.emit(this._position);
            setTimeout(() => this.assureFullyOnScreen());
        }
    }
    get hasHiddenInput() { return !!this.hiddenInput; }
    get showCloser() { return this._showCloser; }
    set showCloser(newValue) {
        if (isString(newValue))
            newValue = toBoolean(newValue, false, true);
        this._showCloser = newValue;
    }
    doCloseAction() {
        this.close.emit();
    }
    get fakeUrl() { return this._fakeUrl; }
    set fakeUrl(newValue) {
        if (isString(newValue))
            newValue = toBoolean(newValue, false, true);
        if (this._fakeUrl !== newValue) {
            this._fakeUrl = newValue;
            if (this.hiddenInput) {
                setTimeout(() => {
                    this.hiddenInput.remove();
                    this.createHiddenInput();
                });
            }
        }
    }
    get wideSpinner() { return this._wideSpinner; }
    set wideSpinner(newValue) {
        if (isString(newValue))
            newValue = toBoolean(newValue, false, true);
        this._wideSpinner = newValue;
    }
    createButtons() {
        this.buttons = [];
        for (let i = 1; i <= 10; ++i)
            this.buttons.push({ key: (i % 10).toString(), label: (i % 10).toString() });
        this.buttons.push({ key: 'Copy', html: this.sanitizer.bypassSecurityTrustHtml(
            /* eslint-disable max-len */
            `<svg viewBox="0 0 512 512" width="1em" height="1em" style="position: relative; left: 0.05em; top: 0.08em;">
  <g fill="currentColor" style="" transform="matrix(1.307452, 0, 0, 1.307452, -79.275406, -79.4646)">
    <polygon points="304,96 288,96 288,176 368,176 368,160 304,160  "/>
    <path d="M325.3,64H160v48h-48v336h240v-48h48V139L325.3,64z M336,432H128V128h32v272h176V432z M384,384H176V80h142.7l65.3,65.6V384   z"/>
  </g>
</svg>`) });
        this.buttons.push({ key: 'Paste', html: this.sanitizer.bypassSecurityTrustHtml(`<svg viewBox="0 0 512 512" width="1em" height="1em" style="position: relative; left: 0.02em; top: 0.1em;">
  <g fill="currentColor">
    <g>
      <path d="M160,160h192c-1.7-20-9.7-35.2-27.9-40.1c-0.4-0.1-0.9-0.3-1.3-0.4c-12-3.4-20.8-7.5-20.8-20.7V78.2    c0-25.5-20.5-46.3-46-46.3c-25.5,0-46,20.7-46,46.3v20.6c0,13.1-8.8,17.2-20.8,20.6c-0.4,0.1-0.9,0.4-1.4,0.5    C169.6,124.8,161.9,140,160,160z M256,64.4c7.6,0,13.8,6.2,13.8,13.8c0,7.7-6.2,13.8-13.8,13.8c-7.6,0-13.8-6.2-13.8-13.8    C242.2,70.6,248.4,64.4,256,64.4z"/>
      <path d="M404.6,63H331v14.5c0,10.6,8.7,18.5,19,18.5h37.2c6.7,0,12.1,5.7,12.4,12.5l0.1,327.2c-0.3,6.4-5.3,11.6-11.5,12.1    l-264.4,0.1c-6.2-0.5-11.1-5.7-11.5-12.1l-0.1-327.3c0.3-6.8,5.9-12.5,12.5-12.5H162c10.3,0,19-7.9,19-18.5V63h-73.6    C92.3,63,80,76.1,80,91.6V452c0,15.5,12.3,28,27.4,28H256h148.6c15.1,0,27.4-12.5,27.4-28V91.6C432,76.1,419.7,63,404.6,63z"/>
    </g>
    <rect height="16" width="112" x="144" y="192"/><rect height="16" width="160" x="144" y="288"/>
    <rect height="16" width="129" x="144" y="384"/><rect height="16" width="176" x="144" y="336"/>
    <rect height="16" width="208" x="144" y="240"/>
  </g>
</svg>`) });
        /* eslint-enable max-len */
    }
    getDigits(index, count, defaultValue = 0) {
        if (index < 0)
            return defaultValue;
        let value = 0;
        for (let i = 0; i < count; ++i)
            value = value * 10 + this.items[index + i].value;
        return value;
    }
    setDigits(index, count, value, field = 'value') {
        if (index < 0)
            return;
        for (let i = count - 1; i >= 0; --i) {
            const digit = value % 10;
            this.items[index + i][field] = floor(digit);
            value = (value - digit) / 10;
        }
    }
    filterDisplayChars(value) {
        if (isNumber(value))
            return value;
        else
            return value.replace(/\u200F/g, '');
    }
    createDisplayOrder() {
        this.items.forEach((item, i) => item.index = i);
        if (!this.rtl) {
            this.displayItems = this.items.filter(i => !i.hidden);
            return;
        }
        const deferred = [];
        let ltr = false;
        this.displayItems = [];
        this.items.forEach(item => {
            if (item.hidden)
                return;
            else if (item.digit || item.sign || (ltr && item.bidi)) {
                ltr = true;
                deferred.push(item);
            }
            else {
                ltr = false;
                this.displayItems.push(...deferred.reverse());
                deferred.length = 0;
                this.displayItems.push(item);
            }
        });
        this.displayItems.push(...deferred.reverse());
    }
    createHiddenInput() {
        if (!useHiddenInput)
            return;
        this.hiddenInput = document.createElement('input');
        this.hiddenInput.name = 'hidden-' + random(0, 99999999);
        this.hiddenInput.inputMode = this.inputOff ? 'none' : this._fakeUrl ? 'url' : 'decimal';
        this.hiddenInput.pattern = this._fakeUrl ? '.*' : '[-+.0-9]*';
        this.hiddenInput.autocomplete = 'new-password';
        this.hiddenInput.setAttribute('autocapitalize', 'off');
        this.hiddenInput.setAttribute('autocomplete', 'off');
        this.hiddenInput.setAttribute('autocorrect', 'off');
        this.hiddenInput.setAttribute('spellcheck', 'false');
        this.hiddenInput.setAttribute('tabindex', this.disabled ? '-1' : this.tabindex);
        this.hiddenInput.style.position = 'absolute';
        this.hiddenInput.style.opacity = '0';
        this.hiddenInput.style['caret-color'] = 'transparent';
        this.hiddenInput.style['pointer-events'] = 'none';
        this.hiddenInput.style.left = '0';
        this.hiddenInput.style.top = '-6px';
        this.hiddenInput.addEventListener('keydown', event => this.onKeyDown(event));
        this.hiddenInput.addEventListener('keypress', event => this.onKeyPress(event));
        this.hiddenInput.addEventListener('keyup', () => this.onKeyUp());
        this.hiddenInput.addEventListener('input', () => this.onInput());
        this.hiddenInput.addEventListener('focus', () => this.onHiddenInputFocus(true));
        this.hiddenInput.addEventListener('blur', () => this.onHiddenInputFocus(false));
        this.wrapper.parentElement.appendChild(this.hiddenInput);
        this.wrapper.setAttribute('tabindex', '-1');
        this.prepHiddenInput(false);
    }
    getAlignmentForItem(item) {
        return item.digit ? 'center' : 'flex-start';
    }
    getClassForItem(item) {
        if (item.monospaced && item.indicator)
            return 'tbw-dse-mono-indicator-font';
        else if (item.indicator)
            return 'tbw-dse-indicator-font';
        else if (item.monospaced)
            return 'tbw-dse-mono-font';
        else
            return null;
    }
    getStyleForItem(item, heightOffset) {
        const itemStyle = {
            top: 'calc(' + (this.swipeable(item, 0) ? (this.smoothedDeltaY + heightOffset) : 0) +
                'px + ' + (item.deltaY || 0) + 'em)'
        };
        if (item.maxChars) {
            let value;
            if (heightOffset < 0)
                value = item.alt_swipeAbove || item.swipeAbove;
            else if (heightOffset > 0)
                value = item.alt_swipeBelow || item.swipeBelow;
            else
                value = this.filterDisplayChars(item.alt_value || item.value).toString();
            const ratio = item.maxChars / value.toString().length;
            if (ratio < 1) {
                itemStyle.transform = `scale(${floor(ratio * 100) / 100}, 1)`;
                itemStyle.width = '1px';
            }
        }
        return itemStyle;
    }
    getStaticBackgroundColor() {
        if (this._disabled)
            return DISABLED_BACKGROUND;
        else if (this._viewOnly)
            return VIEW_ONLY_BACKGROUND;
        else
            return NORMAL_BACKGROUND;
    }
    isSelected(item, index = item?.index) {
        const showFocus = (this.showFocus || !!this.focusStretchTimer);
        return (!this._disabled && showFocus && !this.selectionHidden &&
            ((item && index === this.selection) || (!item && this.activeSpinner === index)));
    }
    getCursorMode(item, index = item?.index) {
        if (!this.isSelected(item, index))
            return '';
        else if (this.cursorRate === '0ms')
            return 'tbw-dse-steady-cursor';
        else
            return 'tbw-dse-blinking-cursor';
    }
    getBackgroundColorForItem(item, index = item?.index) {
        if (this.isSelected(item, index))
            return index === SPIN_UP || index === SPIN_DOWN ? 'tbw-spinner-fill-active' : 'tbw-transparent';
        else if (!this._disabled && !this.viewOnly && (index === SPIN_UP || index === SPIN_DOWN))
            return 'tbw-spinner-fill';
        else
            return 'tbw-transparent';
    }
    getColorForItem(item, index = item?.index) {
        const showFocus = (this.showFocus || !!this.focusStretchTimer);
        if (this._disabled)
            return DISABLED_TEXT;
        else if (item && this._viewOnly)
            return VIEW_ONLY_TEXT;
        else if (this._viewOnly)
            return DISABLED_ARROW_COLOR;
        else if (item && item.indicator)
            return INDICATOR_TEXT;
        else if (index === this.selection && showFocus && !this.selectionHidden)
            return SELECTED_TEXT;
        else
            return NORMAL_TEXT;
    }
    canSwipe(item) {
        return item?.editable && !item.indicator;
    }
    swipeable(item, delta) {
        if (this.swipeIndex < 0)
            return false;
        const nextValue = this.smoothedDeltaY < 0 ? item.swipeBelow : item.swipeAbove;
        return item.index === this.swipeIndex ||
            (sign(delta || this.smoothedDeltaY) === sign(this.smoothedDeltaY) && nextValue != null && item.value !== nextValue);
    }
    createSwipeValues(index) {
        this.roll(1, index, false);
        this.roll(-1, index, false);
        for (let i = 0; i < this.items.length; ++i) {
            const item = this.items[i];
            if (i !== index && !item.divider && !item.static &&
                item.value === item.swipeAbove && item.value === item.swipeBelow)
                item.swipeAbove = item.swipeBelow = null;
        }
    }
    roll(_sign, _sel = this.selection, _updateValue = true) { }
    returnFalse() {
        return false;
    }
    confirmFlash() {
        if (this.confirmTimer)
            return;
        this.displayState = 'confirm';
        this.confirmTimer = timer(FLASH_DURATION).subscribe(() => {
            this.confirmTimer = undefined;
            this.displayState = 'normal';
        });
    }
    warningFlash(longer = false) {
        if (this.warningTimer)
            return;
        if (!this.errorTimer)
            this.displayState = 'warning';
        this.warningTimer = timer(longer ? LONG_WARNING_DURATION : FLASH_DURATION).subscribe(() => {
            this.warningTimer = undefined;
            this.displayState = (this.errorTimer ? 'error' : 'normal');
        });
    }
    errorFlash() {
        if (this.errorTimer)
            return;
        this.displayState = 'error';
        this.errorTimer = timer(FLASH_DURATION).subscribe(() => {
            this.errorTimer = undefined;
            this.displayState = (this.warningTimer ? 'warning' : 'normal');
        });
    }
    stopKeyTimer() {
        if (this.keyTimer) {
            this.keyTimer.unsubscribe();
            this.keyTimer = undefined;
        }
    }
    stopClickTimer() {
        if (this.clickTimer) {
            this.activeSpinner = NO_SELECTION;
            this.clickTimer.unsubscribe();
            this.clickTimer = undefined;
        }
    }
    onButtonKey(key) {
        this.onButtonFocus();
        this.onKey(key);
    }
    onButtonFocus() {
        if (this.focusStretchTimer)
            clearTimeout(this.focusStretchTimer);
        this.focusStretchTimer = setTimeout(() => this.focusStretchTimer = undefined, LOSE_FOCUS_DELAY);
        this.wrapper.focus();
    }
    onMouseDown(index, evt) {
        if (this._disabled || this.viewOnly || (evt?.button ?? 0) !== 0)
            return;
        else if (evt)
            evt.stopPropagation();
        if (this.items[index]?.spinner && evt?.target) {
            const r = evt.target.getBoundingClientRect();
            if (this.wideSpinner) {
                const x = (evt.clientX ?? getClientXYForTouchEvent(evt).x);
                if (x < r.left + r.width / 2)
                    index = SPIN_UP;
                else
                    index = SPIN_DOWN;
            }
            else {
                const y = (evt.clientY ?? getClientXYForTouchEvent(evt).y);
                if (y < r.top + r.height / 2)
                    index = SPIN_UP;
                else
                    index = SPIN_DOWN;
            }
        }
        if (!this.checkSpinAction(index))
            this.updateSelection(index);
    }
    onMouseUp(evt) {
        if (this._disabled || this.viewOnly)
            return;
        else if (evt)
            evt.stopPropagation();
        if (this.clickTimer) {
            this.stopClickTimer();
            this.onSpin(this.lastDelta);
        }
    }
    onMouseLeave() {
        this.stopClickTimer();
    }
    headerDragStart(evt) {
        const pt = getThePoint(evt);
        This$1.dragee = this;
        This$1.dragStartsActive = document.body.matches(':active');
        this.headerDx = this.headerDy = 0;
        This$1.headerStartX = pt.x;
        This$1.headerStartY = pt.y;
        this.wrapper.focus();
        evt.preventDefault();
    }
    static mouseDownOrTouch(evt) {
        if (evt.type === 'touchstart')
            This$1.touchHasOccurred = true;
        if (This$1.pasteable?.showPasteInput && evt.target !== This$1.pasteable.pasteInput) {
            This$1.pasteable.showPasteInput = false;
            This$1.pasteable = undefined;
        }
    }
    static headerDrag(evt) {
        if (This$1.dragStartsActive && !document.body.matches(':active'))
            This$1.headerDragEnd(evt);
        else if (This$1.dragee) {
            const pt = getThePoint(evt);
            This$1.dragee.headerDx = pt.x - This$1.headerStartX;
            This$1.dragee.headerDy = pt.y - This$1.headerStartY;
            This$1.dragee.assureFullyOnScreen();
            evt.preventDefault();
            This$1.dragee.cdr.detectChanges();
        }
    }
    static headerDragEnd(evt) {
        if (This$1.dragee) {
            This$1.dragee.headerX += This$1.dragee.headerDx;
            This$1.dragee.headerY += This$1.dragee.headerDy;
            This$1.dragee.headerDx = This$1.dragee.headerDy = 0;
            This$1.dragee.assureFullyOnScreen();
            This$1.dragee.positionChange.emit(This$1.dragee.position);
            This$1.dragee = undefined;
            evt.preventDefault();
        }
    }
    static assureAllFullyOnScreen() {
        This$1.instances?.forEach(dse => dse.assureFullyOnScreen());
    }
    assureFullyOnScreen() {
        if (!this.floating)
            return;
        const r = this.wrapper.parentElement?.getBoundingClientRect();
        if (!r)
            return;
        const x = this.headerX + this.headerDx;
        const y = this.headerY + this.headerDy;
        const winWidth = max(window.innerWidth, document.documentElement.clientWidth);
        const winHeight = min(window.innerHeight, document.documentElement.clientHeight);
        let dx;
        let dy;
        if (x < 0)
            this.headerX -= x;
        else if (r.width < winWidth && (dx = x + r.width - winWidth) > 0)
            this.headerX -= dx;
        if (y < 0)
            this.headerY -= y;
        else if (r.height < winHeight && (dy = y + r.height - winHeight) > 0)
            this.headerY -= dy;
        this.position = { x: this.headerX, y: this.headerY };
    }
    onTouchStart(index, evt) {
        This$1.dragStartsActive = document.body.matches(':active');
        if (this._disabled || this.viewOnly)
            return;
        if (evt.cancelable)
            evt.preventDefault();
        if (!this.hasFocus && this.wrapper.focus)
            this.wrapper.focus();
        const target = this.wrapper.querySelector('.dse-item-' + index);
        if (target)
            this.digitHeight = target.getBoundingClientRect().height;
        else
            this.digitHeight = round(this.wrapper.getBoundingClientRect().height * 0.734);
        this.clearDeltaYSwiping();
        this.initialTouchTime = processMillis();
        if (this.canSwipe(this.items[index])) {
            this.createSwipeValues(index);
            this.swipeIndex = index;
        }
        if (this.useAlternateTouchHandling)
            this.onTouchStartAlternate(index, evt);
        else
            this.onTouchStartDefault(index, evt);
    }
    onTouchMove(evt) {
        if (this._disabled || this.viewOnly)
            return;
        else if (This$1.dragStartsActive && !document.body.matches(':active')) {
            this.onTouchEnd(evt);
            return;
        }
        if (evt.cancelable) {
            evt.preventDefault();
            evt.stopPropagation();
        }
        if (this.selection >= 0 && this.firstTouchPoint) {
            const pt = getPageXYForTouchEvent(evt);
            this.touchDeltaY = pt.y - this.firstTouchPoint.y;
            this.updateDeltaYSmoothing();
        }
    }
    onTouchEnd(evt) {
        const lastDeltaY = this.touchDeltaY;
        if (this._disabled || this.viewOnly)
            return;
        if (evt.cancelable)
            evt.preventDefault();
        this.onMouseUp(null);
        if (this.swipeIndex >= 0 && this.firstTouchPoint) {
            if (abs(lastDeltaY) >= max(this.digitHeight * MIN_DIGIT_SWIPE, DIGIT_SWIPE_THRESHOLD)) {
                if (lastDeltaY > 0) {
                    if (this.items[this.selection].swipeAbove != null)
                        this.increment();
                    else
                        this.errorFlash();
                }
                else if (this.items[this.selection].swipeBelow != null)
                    this.decrement();
                else
                    this.errorFlash();
            }
        }
        if (this.swipeIndex >= 0) {
            this.clearDeltaYSwiping();
        }
    }
    onTouchStartDefault(index, evt) {
        this.firstTouchPoint = getPageXYForTouchEvent(evt);
        this.onMouseDown(index, evt);
    }
    onTouchStartAlternate(_index, _event) { }
    updateSelection(newSelection) {
        if (this.selection !== newSelection && this.items[newSelection]?.editable) {
            if (this.selection >= 0)
                this.items[this.selection].selected = false;
            this.selection = newSelection;
            if (this.focusTimer) {
                clearTimeout(this.focusTimer);
                this.focusTimer = undefined;
                this.showFocus = this.hasFocus;
            }
            if (this.selection > 0)
                this.items[this.selection].selected = true;
        }
    }
    checkSpinAction(index) {
        if ((index === SPIN_UP || index === SPIN_DOWN) && !this.clickTimer) {
            this.activeSpinner = index;
            this.lastDelta = (index === SPIN_UP ? 1 : -1);
            this.clickTimer = timer(KEY_REPEAT_DELAY, KEY_REPEAT_RATE).subscribe(() => {
                this.onSpin(this.lastDelta);
            });
            return true;
        }
        return false;
    }
    onFocus(value, evt) {
        if (this.ignoreFocus > 0 || (value && this.viewOnly))
            return;
        if (this.hasWrapperFocus !== value) {
            this.hasWrapperFocus = value;
            if (value) {
                if (this.hiddenInput && !this.hiddenInput.disabled)
                    this.hiddenInput.focus();
                else if (isChrome() && evt.relatedTarget !== this.wrapper) {
                    // For some reason Chrome (and only Chrome) requires me to play this silly blur/refocus game
                    // in order to make clipboard copy work reliably.
                    this.blurAndRefocusHack();
                }
            }
            this.checkFocus();
        }
    }
    blurAndRefocusHack() {
        if (this.ignoreFocus > 0)
            return;
        const elem = this.hiddenInput ?? this.wrapper;
        ++this.ignoreFocus;
        setTimeout(() => {
            elem.blur();
            setTimeout(() => { --this.ignoreFocus; elem.focus(); });
        });
    }
    prepHiddenInput(repeatAfterTimeout = true) {
        if (!this.hiddenInput)
            return;
        this.hiddenInput.value = '››‹‹';
        this.hiddenInput.setSelectionRange(2, 2);
        if (repeatAfterTimeout)
            setTimeout(() => this.prepHiddenInput(false));
    }
    onHiddenInputFocus(value) {
        if (value && this.viewOnly)
            return;
        if (this.hasHiddenInputFocus !== value) {
            if (value)
                this.prepHiddenInput();
            this.hasHiddenInputFocus = value;
            this.checkFocus();
        }
    }
    hasAComponentInFocus() {
        return this.hasWrapperFocus || this.hasHiddenInputFocus;
    }
    checkFocus() {
        const newFocus = this.hasAComponentInFocus();
        if (this.hasFocus !== newFocus) {
            this.hasFocus = newFocus;
            if (this.focusTimer) {
                clearTimeout(this.focusTimer);
                this.focusTimer = undefined;
            }
            if (newFocus) {
                this.focusTimer = setTimeout(() => {
                    this.focusTimer = undefined;
                    this.showFocus = this.hasFocus;
                }, 250);
                this.gainedFocus();
            }
            else {
                this.showFocus = false;
                this.lostFocus();
            }
        }
    }
    gainedFocus() { }
    lostFocus() { }
    onKeyDown(evt) {
        this.prepHiddenInput();
        const key = eventToKey(evt);
        // For some strange reason, iOS external mobile keyboards (at least one Logitech model, and one Apple model)
        // are sometimes generating two keydown events for one single keypress, both events with the same timestamp,
        // very close timestamps, or even a later-arriving event with an earlier timestamp than the first. We need to
        // reject the repeated event.
        //
        // On the other hand, one Android external keyboard I've tested with sends the same timestamp multiple times
        // for legitimately separate keystrokes, so repeated timestamps have to be expected and allowed there.
        //
        if (checkForRepeatedKeyTimestamps &&
            (abs(evt.timeStamp - This$1.lastKeyTimestamp) <= FALSE_REPEAT_THRESHOLD && key === This$1.lastKeyKey)) {
            evt.preventDefault();
            return false;
        }
        // With Android many on-screen keyboard key events carry no useful information about the key that was
        // pressed. They instead match the following test, and we have to grab a character out of the hidden
        // input field to find out what was actually typed in.
        // noinspection JSDeprecatedSymbols (for `keyCode`)
        if (this.hiddenInput && key === 'Unidentified' && evt.keyCode === 229) {
            this.getCharFromInputEvent = true;
            This$1.lastKeyTimestamp = evt.timeStamp;
            return true;
        }
        if (/^[xcv]$/i.test(key) && (evt.ctrlKey || evt.metaKey) && !evt.altKey) {
            evt.preventDefault();
            if (key.toLowerCase() === 'v')
                this.doPaste();
            else
                this.doCopy();
            return false;
        }
        if (key === 'Tab')
            this.lastTabTime = performance.now();
        // After this point, most modifiers don't make sense, and no function keys are used.
        if (key === 'Tab' || evt.altKey || evt.ctrlKey || evt.metaKey || /^F\d+$/.test(key))
            return true;
        // If the built-in auto-repeat is in effect, ignore keystrokes that come along until that auto-repeat ends.
        if (!this.keyTimer && key !== 'Shift') {
            this.onKey(key);
            this.keyTimer = timer(KEY_REPEAT_DELAY, KEY_REPEAT_RATE).subscribe(() => this.onKey(key));
        }
        evt.preventDefault();
        This$1.lastKeyTimestamp = evt.timeStamp;
        This$1.lastKeyKey = key;
        return false;
    }
    doPaste(text) {
        if (text)
            this.applyPastedText(text);
        else if (alternateClipboard) {
            this.showPasteInput = !this.showPasteInput;
            if (this.showPasteInput) {
                This$1.pasteable = this;
                this.pasteInput = this.wrapper.parentElement.querySelector('input[name="paste-input"]');
                if (this.pasteInput) {
                    this.pasteInput.value = '';
                    this.pasteInput.focus();
                    setTimeout(() => this.pasteInput.focus());
                }
            }
            else
                This$1.pasteable = undefined;
        }
        else if (navigator.clipboard)
            navigator.clipboard.readText().then(txt => this.applyPastedText(txt));
        else
            this.errorFlash();
    }
    onPasteInput() {
        this.pasteInput = this.wrapper.parentElement.querySelector('input[name="paste-input"]');
        if (this.pasteInput?.value)
            this.applyPastedText(this.pasteInput.value);
        this.onPasteBlur();
    }
    onPasteBlur() {
        setTimeout(() => {
            this.showPasteInput = false;
            This$1.pasteable = undefined;
            this.wrapper.focus();
            if (this.pasteInput)
                this.pasteInput.value = '';
        });
    }
    applyPastedText(_text) {
        // Default implementation does nothing.
    }
    doCopy() {
        const text = this.getClipboardText();
        if (text) {
            if (alternateClipboard) {
                const elem = document.createElement('input');
                elem.setAttribute('style', 'position: absolute; opacity: 0');
                elem.setAttribute('inputmode', 'none');
                this.wrapper.appendChild(elem);
                elem.value = text;
                elem.select();
                document.execCommand('copy');
                setTimeout(() => {
                    this.wrapper.removeChild(elem);
                    this.wrapper.focus();
                });
            }
            else if (navigator.clipboard)
                navigator.clipboard.writeText(text).finally(noop);
            else
                this.errorFlash();
            this.confirmFlash();
        }
    }
    getClipboardText() {
        // Default implementation does nothing.
        return null;
    }
    onKeyUp() {
        this.stopKeyTimer();
        return true;
    }
    onKeyPress(evt) {
        this.prepHiddenInput();
        const key = eventToKey(evt);
        if (key === 'Tab')
            this.lastTabTime = performance.now();
        if (key === 'Tab' || evt.altKey || evt.ctrlKey || evt.metaKey || /^F\d+$/.test(key))
            return true;
        evt.preventDefault();
        return key === 'Backspace' || key === 'Delete';
    }
    onInput() {
        if (this.getCharFromInputEvent) {
            const currInput = this.hiddenInput.value?.replace(/[›‹]/g, '');
            if (currInput && currInput.length > 0)
                this.onKey(currInput.charAt(0));
        }
    }
    onKey(key) {
        if (this._disabled || this.viewOnly || (!this.hasFocus && !this.floating) || !this.items[this.selection]?.editable)
            return;
        let advance = false;
        if (key === '-' || key.toLowerCase() === this.letterDecrement)
            key = 'ArrowDown';
        else if (key === '+' || key === '=' || key.toLowerCase() === this.letterIncrement)
            key = 'ArrowUp';
        else if (key === '0' && isString(this.items[this.selection].value)) {
            key = 'ArrowUp';
            advance = true;
        }
        const now = processMillis();
        switch (key) {
            case 'ArrowUp':
                this.increment();
                break;
            case 'ArrowDown':
                this.decrement();
                break;
            case 'Backspace':
                this.cursorBackward();
                break;
            case 'ArrowLeft':
                this.cursorLeft();
                break;
            case 'ArrowRight':
                this.cursorRight();
                break;
            case 'Copy': // Pseudo key
                this.doCopy();
                break;
            case 'Paste': // Pseudo key
                this.doPaste();
                break;
            case '.':
            case ',':
                if (now < this.lastDecimalTime + DOT_DOT_TYPING_INTERVAL)
                    this.cursorForward(); // Otherwise, quietly ignore.
                this.lastDecimalTime = now;
                break;
            case ' ':
            case '*':
            case '#':
            case 'Enter':
            case 'Return':
                this.cursorForward();
                break;
            default:
                if (key && key.length === 1) {
                    this.restartCursorAnimation();
                    this.digitTyped(key.charCodeAt(0), key);
                }
        }
        if (advance)
            this.cursorForward();
    }
    onSpin(delta) {
        if (this._disabled || this.viewOnly)
            return;
        if (delta > 0)
            this.increment();
        else if (delta < 0)
            this.decrement();
    }
    cursorLeft(checkRtl = true) {
        if (checkRtl && this.rtl) {
            this.cursorRight(false);
            return;
        }
        this.cursorBackward(this.displayItems);
    }
    cursorRight(checkRtl = true) {
        if (checkRtl && this.rtl) {
            this.cursorLeft(false);
            return;
        }
        this.cursorForward(this.displayItems);
    }
    cursorBackward(items = this.items) {
        let newSelection = NO_SELECTION;
        const selection = items.findIndex(i => i.index === this.selection);
        const start = (selection >= 0 ? selection : items.length);
        for (let i = start - 1; i >= 0; --i) {
            if (items[i].editable && !items[i].hidden) {
                newSelection = items[i].index;
                break;
            }
        }
        this.setSelection(newSelection);
    }
    cursorForward(items = this.items) {
        let newSelection = -1;
        const selection = items.findIndex(i => i.index === this.selection);
        const start = (selection >= 0 ? selection : -1);
        for (let i = start + 1; i < items.length; ++i) {
            if (items[i].editable && !items[i].hidden) {
                newSelection = items[i].index;
                break;
            }
        }
        this.setSelection(newSelection);
    }
    setSelection(newSelection) {
        if (newSelection >= 0) {
            if (this.selection >= 0)
                this.items[this.selection].selected = false;
            this.selection = newSelection;
            this.items[this.selection].selected = true;
        }
    }
    restartCursorAnimation() {
        const cursor = this.wrapper.querySelector('.tbw-dse-blinking-cursor');
        if (cursor) {
            cursor.style.animation = 'none';
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions, chai-friendly/no-unused-expressions
            cursor.offsetHeight; /* trigger reflow */
            cursor.style.animation = null;
        }
    }
    increment() {
        this.restartCursorAnimation();
        this.roll(1);
    }
    decrement() {
        this.restartCursorAnimation();
        this.roll(-1);
    }
    digitTyped(charCode, _key) {
        if (48 <= charCode && charCode < 58) {
            this.items[this.selection].value = charCode - 48;
            this.cursorForward();
        }
    }
    checkDarkMode(_immediate = false) {
        const lastMode = this.darkMode;
        this.darkMode = (getBackgroundLevel(this.wrapper?.parentElement) < 128);
        if (this.darkMode !== lastMode) {
            DynamicColor.update(ERROR_BACKGROUND, this.darkMode);
            DynamicColor.update(CONFIRM_BACKGROUND, this.darkMode);
            DynamicColor.update(WARNING_BACKGROUND, this.darkMode);
            const currentState = this.displayState;
            setTimeout(() => {
                this.displayState = 'refresh';
                setTimeout(() => this.displayState = currentState);
            });
        }
    }
    adjustState() {
        if (this.hiddenInput) {
            const disabled = (this._floating || this._disabled || this._viewOnly || this._disableMobileKeyboard);
            this.hiddenInput.setAttribute('tabindex', this.disabled ? '-1' : this.tabindex);
            this.hiddenInput.inputMode = this.inputOff ? 'none' : this._fakeUrl ? 'url' : 'decimal';
            this.hiddenInput.disabled = disabled;
            this.hiddenInput.readOnly = disabled;
        }
    }
    updateDeltaYSmoothing() {
        const now = processMillis();
        this.touchDeltaTimes = this.touchDeltaTimes.filter((time, i) => {
            if (time < now + SWIPE_SMOOTHING_WINDOW) {
                this.touchDeltaYs.splice(i, 1);
                return false;
            }
            return true;
        });
        this.touchDeltaTimes.push(now);
        this.touchDeltaYs.push(this.touchDeltaY);
        if (now < this.initialTouchTime + MIN_SWIPE_TIME)
            this.smoothedDeltaY = 0;
        else
            this.smoothedDeltaY = max(min(this.touchDeltaYs.reduce((sum, y) => sum + y) / this.touchDeltaYs.length, this.digitHeight * MAX_DIGIT_SWIPE), -this.digitHeight * MAX_DIGIT_SWIPE);
    }
    clearDeltaYSwiping() {
        this.smoothedDeltaY = 0;
        this.touchDeltaTimes.length = 0;
        this.touchDeltaY = 0;
        this.touchDeltaTimes.length = 0;
        this.touchDeltaYs.length = 0;
        this.swipeIndex = -1;
        this.items.forEach(item => item.swipeAbove = item.swipeBelow = null);
    }
}
// DigitSequenceEditorDirective specifics
DigitSequenceEditorDirective.defaultCursorRate = null;
DigitSequenceEditorDirective.headerStartX = 0;
DigitSequenceEditorDirective.headerStartY = 0;
DigitSequenceEditorDirective.instances = new Set();
DigitSequenceEditorDirective.lastKeyTimestamp = 0;
DigitSequenceEditorDirective.lastKeyKey = '';
DigitSequenceEditorDirective.dragStartsActive = false;
DigitSequenceEditorDirective.touchHasOccurred = false;
DigitSequenceEditorDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: DigitSequenceEditorDirective, deps: [{ token: i0.NgZone }, { token: i0.ChangeDetectorRef }, { token: i1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Directive });
DigitSequenceEditorDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: DigitSequenceEditorDirective, inputs: { floatZ: "floatZ", disabled: "disabled", cursorRate: "cursorRate", blank: "blank", floating: "floating", tabindex: "tabindex", disableMobileKeyboard: "disableMobileKeyboard", viewOnly: "viewOnly", validateAll: "validateAll", position: "position", showCloser: "showCloser", fakeUrl: "fakeUrl", wideSpinner: "wideSpinner" }, outputs: { valueChange: "valueChange", close: "close", positionChange: "positionChange" }, viewQueries: [{ propertyName: "emSizerRef", first: true, predicate: ["emSizer"], descendants: true, static: true }, { propertyName: "wrapperRef", first: true, predicate: ["wrapper"], descendants: true, static: true }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: DigitSequenceEditorDirective, decorators: [{
            type: Directive
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: i0.ChangeDetectorRef }, { type: i1.DomSanitizer }]; }, propDecorators: { valueChange: [{
                type: Output
            }], close: [{
                type: Output
            }], floatZ: [{
                type: Input
            }], emSizerRef: [{
                type: ViewChild,
                args: ['emSizer', { static: true }]
            }], wrapperRef: [{
                type: ViewChild,
                args: ['wrapper', { static: true }]
            }], disabled: [{
                type: Input
            }], cursorRate: [{
                type: Input
            }], blank: [{
                type: Input
            }], floating: [{
                type: Input
            }], tabindex: [{
                type: Input
            }], disableMobileKeyboard: [{
                type: Input
            }], viewOnly: [{
                type: Input
            }], validateAll: [{
                type: Input
            }], positionChange: [{
                type: Output
            }], position: [{
                type: Input
            }], showCloser: [{
                type: Input
            }], fakeUrl: [{
                type: Input
            }], wideSpinner: [{
                type: Input
            }] } });
This$1 = DigitSequenceEditorDirective;

var AngleStyle;
(function (AngleStyle) {
    AngleStyle[AngleStyle["DD"] = 0] = "DD";
    AngleStyle[AngleStyle["DD_MM"] = 1] = "DD_MM";
    AngleStyle[AngleStyle["DD_MM_SS"] = 2] = "DD_MM_SS";
    AngleStyle[AngleStyle["DDD"] = 3] = "DDD";
    AngleStyle[AngleStyle["DDD_MM"] = 4] = "DDD_MM";
    AngleStyle[AngleStyle["DDD_MM_SS"] = 5] = "DDD_MM_SS";
})(AngleStyle || (AngleStyle = {}));
// @dynamic
class AngleEditorComponent extends DigitSequenceEditorDirective {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(zone, cdr, sanitizer) {
        // But this isn't useless! It changes access from protected to public.
        super(zone, cdr, sanitizer);
        this.angle = 0;
        this.angleDivisor = 1;
        this.decimals = 0;
        this.flipSign = false;
        this.intAngle = 0;
        this.leftDigits = 3;
        this._max = null;
        this._min = null;
        this._options = {};
        this.outOfRange = false;
        this.wrapAround = true;
        this.compassIndex = -1;
        this.decimalIndex = -1;
        this.degreeIndex = -1;
        this.minuteIndex = -1;
        this.secondIndex = -1;
        this.signIndex = -1;
    }
    validateImpl(_value, _control) {
        if (this.outOfRange) {
            if (this.value < this.minAngle)
                return { min: { min: this.min } };
            else if (this.value > this.maxAngle)
                return { max: { max: this.max } };
        }
        return null;
    }
    applyPastedText(text) {
        const newValue = this.parseText(text);
        if (newValue == null || isNaN(newValue) || newValue < this.minAngle || newValue > this.maxAngle)
            this.errorFlash();
        else {
            this.value = newValue;
            this.confirmFlash();
        }
    }
    getClipboardText() {
        return this.getValueAsText();
    }
    get options() { return this._options; }
    set options(newValue) {
        if (!isEqual(this._options, newValue)) {
            const saveAngle = this.angle;
            this._options = clone(newValue);
            this.createDigits();
            this.createDisplayOrder();
            this.intAngle = saveAngle * this.angleDivisor;
            this.updateDigits();
        }
    }
    writeValue(newValue) {
        this.setValue(newValue);
    }
    get value() { return this.angle; }
    set value(newValue) {
        this.setValue(newValue, true);
    }
    setValue(newValue, doCallback = false) {
        if (newValue == null)
            return;
        if (this.angle !== newValue) {
            this.angle = newValue;
            this.setIntAngle(round(newValue * this.angleDivisor), doCallback);
        }
    }
    setIntAngle(newValue, doCallback = false) {
        if (this.intAngle !== newValue) {
            const wasOutOfRange = this.outOfRange;
            this.intAngle = newValue;
            this.updateDigits();
            if (doCallback || (this.validateAll && !wasOutOfRange && this.outOfRange))
                this.reportValueChange();
        }
    }
    get min() { return this._min; }
    set min(value) {
        if (this._min !== value) {
            const before = this.validateImpl(this.value);
            this._min = (value == null || value === '' ? null : toNumber(value));
            setTimeout(() => {
                this.updateDigits();
                const after = this.validateImpl(this.value);
                if (!isEqual(before, after))
                    this.valueHasChanged(true);
            });
        }
    }
    get minAngle() {
        if (this._min != null)
            return this._min;
        else if (this.signIndex < 0 && this.compassIndex < 0)
            return 0;
        else
            return this.leftDigits < 3 ? -90 : -180;
    }
    get max() { return this._max; }
    set max(value) {
        if (this._max !== value) {
            const before = this.validateImpl(this.value);
            this._max = (value == null || value === '' ? null : toNumber(value));
            this.outOfRange = false;
            setTimeout(() => {
                this.updateDigits();
                const after = this.validateImpl(this.value);
                if (!isEqual(before, after))
                    this.valueHasChanged(true);
            });
        }
    }
    get maxAngle() {
        if (this._max != null)
            return this._max;
        else if (this.signIndex < 0 && this.compassIndex < 0)
            return this.leftDigits < 3 ? 90 : 360 - Number.EPSILON * 1000;
        else
            return this.leftDigits < 3 ? 90 : 180 - (this.wrapAround ? Number.EPSILON * 1000 : 0);
    }
    createDigits() {
        this.items.length = 0;
        this.compassIndex = -1;
        this.decimalIndex = -1;
        this.degreeIndex = -1;
        this.minuteIndex = -1;
        this.secondIndex = -1;
        this.signIndex = -1;
        this.angleDivisor = 1;
        const steps = [];
        const opts = this._options;
        const locale = opts.locale || defaultLocale;
        const decimal = opts.decimal ||
            (hasIntlDateTime && convertDigitsToAscii(Intl.NumberFormat(locale).format(1.2)).replace(/\d/g, '').charAt(0)) || '.';
        const style = opts.angleStyle ?? AngleStyle.DDD_MM_SS;
        this.wrapAround = opts.wrapAround ?? (!opts.unsigned && style >= AngleStyle.DDD);
        if (isArray(opts.compass))
            this.compassPoints = opts.compass;
        else if (style < AngleStyle.DDD)
            this.compassPoints = ['S', 'N'];
        else
            this.compassPoints = ['W', 'E'];
        this.compassKeys = [];
        const pts = this.compassPoints;
        for (let i = 0; i < pts[0].length && pts[1].length; ++i) {
            if (pts[0].charAt(i) !== pts[1].charAt(i)) {
                this.compassKeys = [pts[0].charAt(i).toLocaleLowerCase(locale), pts[1].charAt(i).toLocaleLowerCase(locale)];
                break;
            }
        }
        const addDigits = (n, format) => repeat(n, () => this.items.push({ value: 0, digit: true, editable: true, format }));
        if (!opts.compass && !opts.unsigned)
            steps.push('sign');
        if (style < AngleStyle.DDD) {
            this.leftDigits = 2;
            steps.push('deg2', 'dmark');
        }
        else {
            this.leftDigits = 3;
            steps.push('deg3', 'dmark');
        }
        if ([AngleStyle.DD_MM, AngleStyle.DD_MM_SS, AngleStyle.DDD_MM, AngleStyle.DDD_MM_SS].includes(style)) {
            this.angleDivisor *= 60;
            steps.push('min', 'mmark');
        }
        if ([AngleStyle.DD_MM_SS, AngleStyle.DDD_MM_SS].includes(style)) {
            this.angleDivisor *= 60;
            steps.push('sec', 'smark');
        }
        if (opts.decimalPrecision > 0) {
            this.decimals = opts.decimalPrecision;
            this.angleDivisor *= 10 ** this.decimals;
            steps.splice(-1, 0, 'dec');
        }
        else
            this.decimals = 0;
        if (!!opts.compass)
            steps.push('comp');
        for (const step of steps) {
            const i = this.items.length;
            switch (step) {
                case 'sign':
                    this.signIndex = i;
                    this.items.push({ value: '+', editable: true, monospaced: true, sign: true, sizer: '-\n+' });
                    break;
                case 'deg2':
                    this.degreeIndex = i;
                    addDigits(2);
                    break;
                case 'deg3':
                    this.degreeIndex = i;
                    addDigits(3);
                    break;
                case 'dmark':
                    this.items.push({ value: opts.degreeMark ?? '°', static: true });
                    break;
                case 'min':
                    this.minuteIndex = i;
                    addDigits(2);
                    break;
                case 'mmark':
                    this.items.push({ value: opts.minuteMark ?? '’', static: true });
                    break;
                case 'sec':
                    this.secondIndex = i;
                    addDigits(2);
                    break;
                case 'smark':
                    this.items.push({ value: opts.secondMark ?? '”', static: true });
                    break;
                case 'dec':
                    this.items.push({ value: opts.decimal ?? decimal, static: true });
                    this.decimalIndex = i + 1;
                    addDigits(opts.decimalPrecision);
                    break;
                case 'comp':
                    this.compassIndex = i;
                    this.items.push({ value: opts.compass[1], editable: true, monospaced: true, sign: true,
                        sizer: this.compassPoints.join('\n') });
                    break;
            }
        }
        this.items.push({ divider: true });
        this.items.push({ spinner: true });
        this.updateDigits();
    }
    getClassForItem(item) {
        let qlass = super.getClassForItem(item) ?? '';
        if (this.outOfRange && item.editable)
            qlass += ' tbw-dse-bad-value';
        return qlass?.trim() || null;
    }
    updateDigits(intAngle, delta = 0, selection = -1) {
        const programmatic = (intAngle === undefined);
        intAngle = (intAngle === undefined ? this.intAngle : intAngle);
        let angle = intAngle / this.angleDivisor;
        const field = delta === 0 ? 'value' : delta < 0 ? 'swipeBelow' : 'swipeAbove';
        if (angle < this.minAngle || angle > this.maxAngle) {
            if (delta === 0)
                this.outOfRange = true;
            if (delta !== 0 && selection >= 0)
                this.items[selection][field] = '\xA0';
            if (!programmatic) {
                angle = (angle < this.minAngle ? this.minAngle : this.maxAngle);
                intAngle = trunc(angle * this.angleDivisor);
                timer(0).subscribe(() => {
                    this.errorFlash();
                    this.setIntAngle(intAngle);
                    this.updateDigits();
                });
                return;
            }
        }
        else if (delta === 0)
            this.outOfRange = false;
        const dp = this.decimals;
        let d;
        let m;
        let s;
        let frac = abs(intAngle);
        if (dp > 0) {
            s = floor(frac / 10 ** dp);
            frac = frac % 10 ** dp;
        }
        else {
            s = frac;
            frac = 0;
        }
        if (this.secondIndex >= 0) {
            m = floor(s / 60);
            s = s % 60;
        }
        else {
            m = s;
            s = 0;
        }
        if (this.minuteIndex >= 0) {
            d = floor(m / 60);
            m = m % 60;
        }
        else {
            d = m;
            m = 0;
        }
        this.setDigits(this.degreeIndex, this.leftDigits, d, field);
        this.setDigits(this.minuteIndex, 2, m, field);
        this.setDigits(this.secondIndex, 2, s, field);
        this.setDigits(this.decimalIndex, dp, frac, field);
        if (this.signIndex >= 0) {
            if (this.flipSign && intAngle === 0)
                this.items[this.signIndex][field] = (this.items[this.signIndex][field] === '-' ? '+' : '-');
            else
                this.items[this.signIndex][field] = (intAngle < 0 ? '-' : '+');
        }
        else if (this.compassIndex >= 0) {
            if (this.flipSign && intAngle === 0)
                this.items[this.compassIndex][field] = (this.items[this.compassIndex][field] === this.compassPoints[0] ?
                    this.compassPoints[1] : this.compassPoints[0]);
            else
                this.items[this.compassIndex][field] = this.compassPoints[intAngle < 0 ? 0 : 1];
        }
    }
    getIntAngleFromDigits() {
        let intAngle = this.getDigits(this.decimalIndex, this.decimals);
        const div = this.angleDivisor;
        let sign = 1;
        intAngle += this.getDigits(this.degreeIndex, this.leftDigits) * div;
        intAngle += this.getDigits(this.minuteIndex, 2) * (div / 60);
        intAngle += this.getDigits(this.secondIndex, 2) * (div / 3600);
        if (this.signIndex >= 0 && this.items[this.signIndex].value === '-' ||
            this.compassIndex >= 0 && this.items[this.compassIndex].value === this.compassPoints[0])
            sign = -1;
        return sign * intAngle;
    }
    roll(sign, sel = this.selection, updateAngle = true) {
        let change = 0;
        let intAngle = this.intAngle;
        const div = this.angleDivisor;
        if (this.outOfRange && intAngle / this.angleDivisor < this.minAngle)
            change = trunc(this.minAngle * this.angleDivisor - intAngle) * sign;
        else if (this.outOfRange && intAngle / this.angleDivisor > this.maxAngle)
            change = trunc(this.maxAngle * this.angleDivisor - intAngle) * sign;
        else if (sel === this.signIndex || sel === this.compassIndex) {
            this.flipSign = (intAngle === 0);
            change = -sign * intAngle * 2;
        }
        else if (this.degreeIndex <= sel && sel < this.degreeIndex + this.leftDigits)
            change = 10 ** (this.degreeIndex - sel + this.leftDigits - 1) * div;
        else if (this.minuteIndex <= sel && sel < this.minuteIndex + 2)
            change = 10 ** (this.minuteIndex - sel + 1) * (div / 60);
        else if (this.secondIndex <= sel && sel < this.secondIndex + 2)
            change = 10 ** (this.secondIndex - sel + 1) * (div / 3600);
        else if (this.decimalIndex <= sel && sel < this.decimalIndex + this.decimals)
            change = 10 ** (this.decimalIndex - sel + this.decimals - 1);
        intAngle = intAngle + sign * change;
        if (intAngle / this.angleDivisor < this.minAngle || intAngle / this.angleDivisor > this.maxAngle) {
            if (this.wrapAround) {
                if (this.signIndex < 0 && this.compassIndex < 0)
                    intAngle = mod(intAngle, ceil(this.maxAngle * this.angleDivisor));
                else
                    intAngle = mod2(intAngle, ceil(this.maxAngle * this.angleDivisor * 2));
                if (updateAngle)
                    this.warningFlash(true);
            }
            else {
                if (updateAngle)
                    this.errorFlash();
                return;
            }
        }
        if (updateAngle && change !== 0) {
            this.angle = intAngle / this.angleDivisor;
            this.setIntAngle(intAngle, true);
        }
        else
            this.updateDigits(intAngle, updateAngle ? 0 : sign);
        this.flipSign = false;
    }
    onKey(key) {
        const keyLc = key.toLocaleLowerCase(this._options.locale);
        const editable = !this.disabled && !this.viewOnly;
        if (editable &&
            ((this.selection === this.signIndex && ' -+='.includes(key)) ||
                (this.selection === this.compassIndex && (key === '1' || key === '2' || this.compassKeys.includes(keyLc)))))
            this.digitTyped(keyLc.charCodeAt(0), keyLc);
        else
            super.onKey(key);
    }
    digitTyped(charCode, key) {
        const i = this.items;
        const sel = this.selection;
        const origValue = i[sel].value;
        let newValue = origValue;
        if (sel === this.compassIndex) {
            const [neg, pos] = this.compassPoints;
            if (i[this.compassIndex].value === neg && (key === this.compassKeys[1] || key === '1'))
                newValue = pos;
            else if (i[this.compassIndex].value === pos && (key === this.compassKeys[0] || key === '2'))
                newValue = neg;
            else {
                if ('12'.indexOf(key) < 0 && !this.compassKeys.includes(key))
                    this.errorFlash();
                return;
            }
        }
        else if (sel === this.signIndex) {
            if (key === ' ' || key === '+' || key === '=')
                newValue = '+';
            else if (key === '-')
                newValue = '-';
            else {
                if (' +=-'.indexOf(key) < 0)
                    this.errorFlash();
                return;
            }
        }
        else if (48 <= charCode && charCode < 58)
            newValue = charCode - 48;
        else {
            this.errorFlash();
            return;
        }
        if (newValue === origValue && !this.outOfRange) {
            this.cursorForward();
            return;
        }
        if (((sel === this.minuteIndex || sel === this.secondIndex) && newValue > 5) ||
            (sel === this.degreeIndex && this.leftDigits > 2 && newValue > (this.wrapAround ? 1 : 3))) {
            this.errorFlash();
            return;
        }
        i[sel].value = newValue;
        let newIntAngle = this.getIntAngleFromDigits();
        const angle = newIntAngle / this.angleDivisor;
        if (angle < this.minAngle || angle > this.maxAngle) {
            if (!this.outOfRange && sel !== this.degreeIndex && sel !== this.minuteIndex && sel !== this.secondIndex) {
                i[sel].value = origValue;
                this.errorFlash();
                return;
            }
            else if (angle < this.minAngle)
                newIntAngle = trunc(this.minAngle * this.angleDivisor);
            else
                newIntAngle = trunc(this.maxAngle * this.angleDivisor);
            this.updateDigits(newIntAngle);
        }
        this.angle = newIntAngle / this.angleDivisor;
        this.setIntAngle(newIntAngle, true);
        this.cursorForward();
    }
    getValueAsText() {
        if (this._options.copyDecimal ||
            this._options.angleStyle === AngleStyle.DD || this._options.angleStyle === AngleStyle.DDD)
            return this.value.toString();
        else
            return this.items.map(item => (item.value ?? '').toString()).join('');
    }
    parseText(text) {
        text = text.trim().toLowerCase().replace(',', '.').replace(/[\u2212\uFE63\uFF0D]/g, '-').replace(/[\uFE62\uFF0B]/g, '+');
        if (/^[-+.\d]+°?$/.test(text))
            return toNumber(text.replace('°', ''), NaN);
        else {
            let sign = text.startsWith('-') ? -1 : 1;
            text = text.replace(/^[-+]/, '');
            if (text.endsWith(this.compassPoints[0].toLowerCase())) {
                sign = -1;
                text = text.slice(0, -this.compassPoints[0].length).trim();
            }
            else if (text.endsWith(this.compassKeys[0])) {
                sign = -1;
                text = text.slice(0, -1).trim();
            }
            else if (text.endsWith(this.compassPoints[1].toLowerCase()))
                text = text.slice(0, -this.compassPoints[0].length).trim();
            else if (text.endsWith(this.compassKeys[1]))
                text = text.slice(0, -1).trim();
            if (/^[., °"“”`'‘’dms\d]+$/.test(text)) {
                const parts = text.split(/([\D+])/).map(part => part.trim());
                const values = [];
                let hasDecimal = false;
                for (let i = 0; i < parts.length; ++i) {
                    const part = parts[i];
                    if (i % 2 === 0) {
                        if (hasDecimal) {
                            values[values.length - 1] = toNumber((last(values) ?? '0') + '.' + part);
                            break;
                        }
                        else
                            values.push(toNumber(part));
                    }
                    else if (part === '.')
                        hasDecimal = true;
                }
                return sign * (values[0] + (values[1] ?? 0) / 60 + (values[2] ?? 0) / 3600);
            }
        }
        return null;
    }
}
AngleEditorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: AngleEditorComponent, deps: [{ token: i0.NgZone }, { token: i0.ChangeDetectorRef }, { token: i1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Component });
AngleEditorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.3", type: AngleEditorComponent, selector: "tbw-angle-editor", inputs: { options: "options", min: "min", max: "max" }, providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AngleEditorComponent), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => AngleEditorComponent), multi: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"tbw-dse-palette\"\n    [class.tbw-dse-floating]=\"floating\"\n    [style.left]=\"(this.headerX + this.headerDx) + 'px'\"\n    [style.top]=\"(this.headerY + this.headerDy) + 'px'\"\n    [style.z-index]=\"floating ? floatZ : 'auto'\">\n  <div *ngIf=\"floating\"\n      class=\"tbw-dse-header\"\n      (mousedown)=\"headerDragStart($event)\"\n      (touchstart)=\"headerDragStart($event)\">&nbsp;\n    <span *ngIf=\"showCloser\" class=\"tbw-dse-closer\"\n        (click)=\"doCloseAction()\" (touchstart)=\"doCloseAction()\">\n      <svg viewBox=\"6 6 12 12\" fill=\"currentColor\" stroke=\"currentColor\" width=\"0.4em\" height=\"0.4em\"\n          stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\">\n        <line x1=\"18\" y1=\"6\" x2=\"6\" y2=\"18\"></line>\n        <line x1=\"6\" y1=\"6\" x2=\"18\" y2=\"18\"></line>\n      </svg>\n    </span>\n  </div>\n  <div #wrapper class=\"tbw-dse-wrapper\"\n      [class.tbw-normal-background]=\"!disabled && !viewOnly\"\n      [class.tbw-disabled-background]=\"disabled\"\n      [class.tbw-view-only-background]=\"viewOnly\"\n      [class.tbw-dse-no-focus]=\"disabled\"\n      [class.tbw-dse-explicit-focus-outline]=\"hasFocus && !floating\"\n      [class.tbw-dse-clear-focus-outline]=\"!hasFocus\"\n      [class.tbw-dse-dse-rtl]=\"rtl\"\n      [class.tbw-dse-floating]=\"floating\"\n      [tabindex]=\"inputOff || hasHiddenInput ? '-1' : tabindex\"\n      [attr.contenteditable]=\"!inputOff && !disableContentEditable\"\n      [attr.inputmode]=\"inputOff ? 'none' : fakeUrl ? 'url' : 'decimal'\"\n      autocomplete=\"off\" autocapitalize=\"off\" autocorrect=\"off\"\n      (keydown)=\"onKeyDown($event)\" (keyup)=\"onKeyUp()\" (keypress)=\"onKeyPress($event)\"\n      (focus)=\"onFocus(true, $event)\" (blur)=\"onFocus(false, $event)\"\n      [ngClass]=\"getStaticBackgroundColor()\"\n      (mouseleave)=\"onMouseLeave()\"\n      (mouseup)=\"onMouseUp($event)\"\n      (touchmove)=\"onTouchMove($event)\"\n      (touchend)=\"onTouchEnd($event)\">\n    <div class=\"tbw-dse-state-indicator\" [@displayState]=\"displayState\">&nbsp;</div>\n    <div *ngFor=\"let item of displayItems\"\n        class=\"tbw-dse-digit dse-item-{{item.index}}\"\n        [ngClass]=\"(getClassForItem(item) || '') + ' ' + (getColorForItem(item) || '')\"\n        [class.tbw-dse-spinner]=\"item.spinner\"\n        contenteditable=\"false\"\n        [style.align-items]=\"getAlignmentForItem(item)\"\n        [style.color]=\"blank && (item.editable || item.indicator) ? 'transparent' : undefined\"\n        [style.cursor]=\"(item.editable || item.spinner) && !disabled && !viewOnly ? 'pointer' : 'default'\"\n        [style.line-height]=\"lineHeight\"\n        [class.tbw-dse-fade]=\"item.fade\"\n        [style.top]=\"item.divider || item.spinner ? undefined : baselineShift\"\n        [ngStyle]=\"item.width ? { width: item.width, 'white-space': 'nowrap', overflow: 'visible' } : null\"\n        (mousedown)=\"onMouseDown(item.index, $event)\"\n        (mouseup)=\"onMouseUp($event)\"\n        (contextmenu)=\"returnFalse()\"\n        (touchstart)=\"onTouchStart(item.index, $event)\">\n      <div class=\"tbw-dse-backgrounder\"\n          [ngClass]=\"getBackgroundColorForItem(item)\"\n          [style.bottom]=\"item.spinner || item.divider ? '0' : baselineShift\">&nbsp;</div>\n      <span *ngIf=\"swipeable(item, 1)\"\n          class=\"tbw-dse-draggable-extra\"\n          [ngStyle]=\"getStyleForItem(item, -digitHeight)\"\n      >{{(item.alt_swipeAbove || item.swipeAbove) ?? '\\u00A0'}}</span>\n      <span *ngIf=\"swipeable(item, -1)\"\n          class=\"tbw-dse-draggable-extra\"\n          [ngStyle]=\"getStyleForItem(item, digitHeight)\"\n      >{{(item.alt_swipeBelow || item.swipeBelow) ?? '\\u00A0'}}</span>\n      <span *ngIf=\"!item.spinner && !item.divider; else dividerOrSpinner\"\n          class=\"tbw-dse-draggable dse-item-{{item.index}} {{getCursorMode(item)}}\"\n          [style.animation-duration]=\"isSelected(item) && cursorRate\"\n          [class.tbw-dse-sized]=\"!!item.sizer\"\n          [ngStyle]=\"getStyleForItem(item, 0)\"\n      >{{filterDisplayChars(item.alt_value || item.value)}}</span>\n      <span *ngIf=\"item.sizer\"\n          class=\"tbw-dse-sizer\"\n      >{{item.sizer}}</span>\n      <ng-template #dividerOrSpinner>\n        <div *ngIf=\"item.divider\" class=\"tbw-dse-divider\">&nbsp;</div>\n        <div *ngIf=\"item.spinner\"\n            class=\"tbw-dse-spinner-layout\"\n            [class.tbw-dse-wide-spinner]=\"wideSpinner\"\n            (mousedown)=\"onMouseDown(item.index, $event)\"\n            (touchstart)=\"onTouchStart(item.index, $event)\">\n          <svg width=\"0.574em\" height=\"0.5em\" viewBox=\"0 0 353 307.3\">\n            <g>\n              <path (mousedown)=\"onMouseDown(SPIN_UP, $event)\"\n                  (touchstart)=\"onTouchStart(SPIN_UP, $event)\"\n                  [ngClass]=\"(getBackgroundColorForItem(null, SPIN_UP) || '') + ' ' + (getColorForItem(null, SPIN_UP) || '')\"\n                  d=\"M 34.1 250.8 L 318.4 250.8 C 336 250.8 334.5 222.4 332.7 208.5 C 332.1 204.2 329.2 197.5 326.6 193.8 L 196.7 28.9 C 185.5 8.6 166.8 8.6 155.9 28.9 L 25.7 193.8 C 23.1 197.5 20.4 204.1 19.9 208.5 C 17.9 222.4 16.5 250.8 34.1 250.8 Z\"/>\n            </g>\n          </svg>\n          <svg width=\"0.574em\" height=\"0.5em\" viewBox=\"0 307.3 353 307.3\">\n            <g>\n              <path (mousedown)=\"onMouseDown(SPIN_DOWN, $event)\"\n                  (touchstart)=\"onTouchStart(SPIN_DOWN, $event)\"\n                  [ngClass]=\"(getBackgroundColorForItem(null, SPIN_DOWN) || '') + ' ' + (getColorForItem(null, SPIN_DOWN) || '')\"\n                  d=\"M 25.6 419.7 L 155.6 585.1 C 167 605 185.5 605 196.9 585.1 L 327 419.7 C 329.5 416.1 332.2 409.6 332.8 405.3 C 334.6 391 335.9 362.6 318.7 362.6 L 33.8 362.6 C 16.6 362.6 18 391 19.8 405.3 C 20.3 409.6 23.1 416.1 25.6 419.7 Z\"/>\n            </g>\n          </svg>\n        </div>\n      </ng-template>\n    </div>\n    <div class=\"tbw-dse-focus-indicator\" [class.tbw-dse-show-focus]=\"floating && hasFocus\">&nbsp;</div>\n    <div #emSizer class=\"tbw-dse-em-sizer\">&nbsp;</div>\n  </div>\n  <div *ngIf=\"floating\" class=\"tbw-dse-footer\">\n    <ng-container *ngFor=\"let btn of buttons; let last = last\">\n      <span\n          class=\"tbw-dse-button\"\n          tabindex=\"-1\"\n          (click)=\"onButtonKey(btn.key)\"\n          (focus)=\"!showPasteInput && onButtonFocus()\"><div [innerHTML]=\"btn.html || htmlEscape(btn.label)\"></div></span>\n      <span *ngIf=\"!last\" class=\"tbw-dse-button-spacer\">&nbsp;</span>\n    </ng-container>\n    <div class=\"tbw-dse-paste-hint\" [style.display]=\"showPasteInput ? 'block' : 'none'\">paste here</div>\n    <input name='paste-input'\n        type=\"text\"\n        (input)=\"onPasteInput()\"\n        (blur)=\"onPasteBlur()\"\n        [style.display]=\"showPasteInput ? 'inline-block' : 'none'\">\n  </div>\n</div>\n", styles: [":host{display:inline-block}.tbw-dse-draggable{position:relative;touch-action:manipulation}.tbw-dse-draggable-extra{display:inline-block;height:0;max-height:0;overflow:visible;position:relative;touch-action:manipulation;white-space:nowrap}.tbw-dse-sizer{background-color:transparent;color:transparent;display:inline-block;height:.1px;margin-bottom:-.1px;overflow:hidden;pointer-events:none;position:relative;touch-action:manipulation;-webkit-user-select:none;user-select:none;white-space:pre}.tbw-dse-backgrounder{height:100%;position:absolute;touch-action:manipulation;width:100%}.tbw-dse-sized{display:inline-block;overflow:visible;text-decoration:inherit;touch-action:manipulation;white-space:nowrap}.tbw-dse-dse-rtl{direction:rtl}.tbw-dse-dse-rtl .tbw-dse-spinner{margin-left:-.1em;margin-right:0}.tbw-dse-em-sizer{background-color:transparent;color:transparent;height:.1px;pointer-events:none;position:absolute;touch-action:manipulation;width:1em}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], animations: [BACKGROUND_ANIMATIONS] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: AngleEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tbw-angle-editor', animations: [BACKGROUND_ANIMATIONS], providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AngleEditorComponent), multi: true },
                        { provide: NG_VALIDATORS, useExisting: forwardRef(() => AngleEditorComponent), multi: true }], template: "<div class=\"tbw-dse-palette\"\n    [class.tbw-dse-floating]=\"floating\"\n    [style.left]=\"(this.headerX + this.headerDx) + 'px'\"\n    [style.top]=\"(this.headerY + this.headerDy) + 'px'\"\n    [style.z-index]=\"floating ? floatZ : 'auto'\">\n  <div *ngIf=\"floating\"\n      class=\"tbw-dse-header\"\n      (mousedown)=\"headerDragStart($event)\"\n      (touchstart)=\"headerDragStart($event)\">&nbsp;\n    <span *ngIf=\"showCloser\" class=\"tbw-dse-closer\"\n        (click)=\"doCloseAction()\" (touchstart)=\"doCloseAction()\">\n      <svg viewBox=\"6 6 12 12\" fill=\"currentColor\" stroke=\"currentColor\" width=\"0.4em\" height=\"0.4em\"\n          stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\">\n        <line x1=\"18\" y1=\"6\" x2=\"6\" y2=\"18\"></line>\n        <line x1=\"6\" y1=\"6\" x2=\"18\" y2=\"18\"></line>\n      </svg>\n    </span>\n  </div>\n  <div #wrapper class=\"tbw-dse-wrapper\"\n      [class.tbw-normal-background]=\"!disabled && !viewOnly\"\n      [class.tbw-disabled-background]=\"disabled\"\n      [class.tbw-view-only-background]=\"viewOnly\"\n      [class.tbw-dse-no-focus]=\"disabled\"\n      [class.tbw-dse-explicit-focus-outline]=\"hasFocus && !floating\"\n      [class.tbw-dse-clear-focus-outline]=\"!hasFocus\"\n      [class.tbw-dse-dse-rtl]=\"rtl\"\n      [class.tbw-dse-floating]=\"floating\"\n      [tabindex]=\"inputOff || hasHiddenInput ? '-1' : tabindex\"\n      [attr.contenteditable]=\"!inputOff && !disableContentEditable\"\n      [attr.inputmode]=\"inputOff ? 'none' : fakeUrl ? 'url' : 'decimal'\"\n      autocomplete=\"off\" autocapitalize=\"off\" autocorrect=\"off\"\n      (keydown)=\"onKeyDown($event)\" (keyup)=\"onKeyUp()\" (keypress)=\"onKeyPress($event)\"\n      (focus)=\"onFocus(true, $event)\" (blur)=\"onFocus(false, $event)\"\n      [ngClass]=\"getStaticBackgroundColor()\"\n      (mouseleave)=\"onMouseLeave()\"\n      (mouseup)=\"onMouseUp($event)\"\n      (touchmove)=\"onTouchMove($event)\"\n      (touchend)=\"onTouchEnd($event)\">\n    <div class=\"tbw-dse-state-indicator\" [@displayState]=\"displayState\">&nbsp;</div>\n    <div *ngFor=\"let item of displayItems\"\n        class=\"tbw-dse-digit dse-item-{{item.index}}\"\n        [ngClass]=\"(getClassForItem(item) || '') + ' ' + (getColorForItem(item) || '')\"\n        [class.tbw-dse-spinner]=\"item.spinner\"\n        contenteditable=\"false\"\n        [style.align-items]=\"getAlignmentForItem(item)\"\n        [style.color]=\"blank && (item.editable || item.indicator) ? 'transparent' : undefined\"\n        [style.cursor]=\"(item.editable || item.spinner) && !disabled && !viewOnly ? 'pointer' : 'default'\"\n        [style.line-height]=\"lineHeight\"\n        [class.tbw-dse-fade]=\"item.fade\"\n        [style.top]=\"item.divider || item.spinner ? undefined : baselineShift\"\n        [ngStyle]=\"item.width ? { width: item.width, 'white-space': 'nowrap', overflow: 'visible' } : null\"\n        (mousedown)=\"onMouseDown(item.index, $event)\"\n        (mouseup)=\"onMouseUp($event)\"\n        (contextmenu)=\"returnFalse()\"\n        (touchstart)=\"onTouchStart(item.index, $event)\">\n      <div class=\"tbw-dse-backgrounder\"\n          [ngClass]=\"getBackgroundColorForItem(item)\"\n          [style.bottom]=\"item.spinner || item.divider ? '0' : baselineShift\">&nbsp;</div>\n      <span *ngIf=\"swipeable(item, 1)\"\n          class=\"tbw-dse-draggable-extra\"\n          [ngStyle]=\"getStyleForItem(item, -digitHeight)\"\n      >{{(item.alt_swipeAbove || item.swipeAbove) ?? '\\u00A0'}}</span>\n      <span *ngIf=\"swipeable(item, -1)\"\n          class=\"tbw-dse-draggable-extra\"\n          [ngStyle]=\"getStyleForItem(item, digitHeight)\"\n      >{{(item.alt_swipeBelow || item.swipeBelow) ?? '\\u00A0'}}</span>\n      <span *ngIf=\"!item.spinner && !item.divider; else dividerOrSpinner\"\n          class=\"tbw-dse-draggable dse-item-{{item.index}} {{getCursorMode(item)}}\"\n          [style.animation-duration]=\"isSelected(item) && cursorRate\"\n          [class.tbw-dse-sized]=\"!!item.sizer\"\n          [ngStyle]=\"getStyleForItem(item, 0)\"\n      >{{filterDisplayChars(item.alt_value || item.value)}}</span>\n      <span *ngIf=\"item.sizer\"\n          class=\"tbw-dse-sizer\"\n      >{{item.sizer}}</span>\n      <ng-template #dividerOrSpinner>\n        <div *ngIf=\"item.divider\" class=\"tbw-dse-divider\">&nbsp;</div>\n        <div *ngIf=\"item.spinner\"\n            class=\"tbw-dse-spinner-layout\"\n            [class.tbw-dse-wide-spinner]=\"wideSpinner\"\n            (mousedown)=\"onMouseDown(item.index, $event)\"\n            (touchstart)=\"onTouchStart(item.index, $event)\">\n          <svg width=\"0.574em\" height=\"0.5em\" viewBox=\"0 0 353 307.3\">\n            <g>\n              <path (mousedown)=\"onMouseDown(SPIN_UP, $event)\"\n                  (touchstart)=\"onTouchStart(SPIN_UP, $event)\"\n                  [ngClass]=\"(getBackgroundColorForItem(null, SPIN_UP) || '') + ' ' + (getColorForItem(null, SPIN_UP) || '')\"\n                  d=\"M 34.1 250.8 L 318.4 250.8 C 336 250.8 334.5 222.4 332.7 208.5 C 332.1 204.2 329.2 197.5 326.6 193.8 L 196.7 28.9 C 185.5 8.6 166.8 8.6 155.9 28.9 L 25.7 193.8 C 23.1 197.5 20.4 204.1 19.9 208.5 C 17.9 222.4 16.5 250.8 34.1 250.8 Z\"/>\n            </g>\n          </svg>\n          <svg width=\"0.574em\" height=\"0.5em\" viewBox=\"0 307.3 353 307.3\">\n            <g>\n              <path (mousedown)=\"onMouseDown(SPIN_DOWN, $event)\"\n                  (touchstart)=\"onTouchStart(SPIN_DOWN, $event)\"\n                  [ngClass]=\"(getBackgroundColorForItem(null, SPIN_DOWN) || '') + ' ' + (getColorForItem(null, SPIN_DOWN) || '')\"\n                  d=\"M 25.6 419.7 L 155.6 585.1 C 167 605 185.5 605 196.9 585.1 L 327 419.7 C 329.5 416.1 332.2 409.6 332.8 405.3 C 334.6 391 335.9 362.6 318.7 362.6 L 33.8 362.6 C 16.6 362.6 18 391 19.8 405.3 C 20.3 409.6 23.1 416.1 25.6 419.7 Z\"/>\n            </g>\n          </svg>\n        </div>\n      </ng-template>\n    </div>\n    <div class=\"tbw-dse-focus-indicator\" [class.tbw-dse-show-focus]=\"floating && hasFocus\">&nbsp;</div>\n    <div #emSizer class=\"tbw-dse-em-sizer\">&nbsp;</div>\n  </div>\n  <div *ngIf=\"floating\" class=\"tbw-dse-footer\">\n    <ng-container *ngFor=\"let btn of buttons; let last = last\">\n      <span\n          class=\"tbw-dse-button\"\n          tabindex=\"-1\"\n          (click)=\"onButtonKey(btn.key)\"\n          (focus)=\"!showPasteInput && onButtonFocus()\"><div [innerHTML]=\"btn.html || htmlEscape(btn.label)\"></div></span>\n      <span *ngIf=\"!last\" class=\"tbw-dse-button-spacer\">&nbsp;</span>\n    </ng-container>\n    <div class=\"tbw-dse-paste-hint\" [style.display]=\"showPasteInput ? 'block' : 'none'\">paste here</div>\n    <input name='paste-input'\n        type=\"text\"\n        (input)=\"onPasteInput()\"\n        (blur)=\"onPasteBlur()\"\n        [style.display]=\"showPasteInput ? 'inline-block' : 'none'\">\n  </div>\n</div>\n", styles: [":host{display:inline-block}.tbw-dse-draggable{position:relative;touch-action:manipulation}.tbw-dse-draggable-extra{display:inline-block;height:0;max-height:0;overflow:visible;position:relative;touch-action:manipulation;white-space:nowrap}.tbw-dse-sizer{background-color:transparent;color:transparent;display:inline-block;height:.1px;margin-bottom:-.1px;overflow:hidden;pointer-events:none;position:relative;touch-action:manipulation;-webkit-user-select:none;user-select:none;white-space:pre}.tbw-dse-backgrounder{height:100%;position:absolute;touch-action:manipulation;width:100%}.tbw-dse-sized{display:inline-block;overflow:visible;text-decoration:inherit;touch-action:manipulation;white-space:nowrap}.tbw-dse-dse-rtl{direction:rtl}.tbw-dse-dse-rtl .tbw-dse-spinner{margin-left:-.1em;margin-right:0}.tbw-dse-em-sizer{background-color:transparent;color:transparent;height:.1px;pointer-events:none;position:absolute;touch-action:manipulation;width:1em}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: i0.ChangeDetectorRef }, { type: i1.DomSanitizer }]; }, propDecorators: { options: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }] } });

const CLICK_REPEAT_DELAY = 500;
const CLICK_REPEAT_RATE = 100;
// noinspection JSUnusedGlobalSymbols
var SelectMode;
(function (SelectMode) {
    SelectMode[SelectMode["DAY"] = 0] = "DAY";
    SelectMode[SelectMode["MONTH"] = 1] = "MONTH";
    SelectMode[SelectMode["YEAR"] = 2] = "YEAR";
    SelectMode[SelectMode["DECADE"] = 3] = "DECADE";
    SelectMode[SelectMode["CENTURY"] = 4] = "CENTURY";
    SelectMode[SelectMode["MILLENNIUM"] = 5] = "MILLENNIUM";
    SelectMode[SelectMode["MODE_COUNT"] = 6] = "MODE_COUNT";
})(SelectMode || (SelectMode = {}));
const multiplier = [0, 1, 1, 10, 100, 1000];
class CalendarPanelComponent {
    constructor() {
        this.baseValue = [0, 0, 0];
        this.dateTime = new DateTime();
        this.digitBase = '0';
        this._firstDay = getStartOfWeek(defaultLocale);
        this._maxYear = 9999;
        this._minYear = 1;
        this.onChangeCallback = noop;
        this.onTouchedCallback = noop;
        this.pendingDelta = 0;
        this.pendingEvent = null;
        this._showDst = false;
        this._weekDayFormat = 'ddd';
        this._yearMonthFormat = 'MMM~Y~';
        this.ymd = { y: 2021, m: 1, d: 1 };
        this.calendar = [];
        this.cols = 4;
        this.daysOfWeek = [];
        this.highlightItem = '';
        this.inTransition = false;
        this.modeCount = SelectMode.MODE_COUNT;
        this.months = [];
        this.rows = 3;
        this.selectMode = SelectMode.DAY;
        this.title = ['', '', ''];
        this.dayClick = new EventEmitter();
        this.updateDayHeadings();
        const base = [];
        convertDigitsToAscii(this.dateTime.format('D'), base);
        this.digitBase = base[0];
    }
    ngOnDestroy() {
        this.stopTimer();
    }
    get value() { return this.ymd; }
    set value(newYMD) {
        if (!isEqual(this.ymd, newYMD)) {
            this.ymd = newYMD;
            this.updateCalendar();
            this.onChangeCallback(newYMD);
        }
    }
    writeValue(newYMD) {
        if (!isEqual(this.ymd, newYMD)) {
            this.ymd = newYMD;
            this.updateCalendar();
        }
    }
    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }
    get timezone() { return this.dateTime.timezone; }
    set timezone(newZone) {
        if (this.dateTime.timezone !== newZone) {
            this.dateTime.timezone = newZone;
            this.updateCalendar();
        }
    }
    get locale() { return this.dateTime.locale; }
    set locale(value) {
        if (!isEqual(this.dateTime.locale, value)) {
            this.dateTime.locale = value;
            const base = [];
            convertDigitsToAscii(this.dateTime.format('D'), base);
            this.digitBase = base[0];
            this.updateDayHeadings();
            this.updateCalendar();
        }
    }
    get textDirection() {
        const locale = isArray(this.locale) ? first(this.locale) : this.locale;
        return /^(ar|arc|dv|fa|ha|he|khw|ks|ku|ps|ur|yi)\b/i.test(locale) ? -1 : 1;
    }
    get gregorianChangeDate() { return this._gregorianChange; }
    set gregorianChangeDate(newChange) {
        if (!isEqual(this._gregorianChange, newChange)) {
            this._gregorianChange = newChange;
            if (isObject(newChange) || isString(newChange))
                this.dateTime.setGregorianChange(newChange);
            else if (newChange === CalendarType.PURE_GREGORIAN)
                this.dateTime.setPureGregorian(true);
            else if (newChange === CalendarType.PURE_JULIAN)
                this.dateTime.setPureJulian(true);
            else
                this.dateTime.setGregorianChange(1582, 10, 15);
            this.updateCalendar();
        }
    }
    get showDst() { return this._showDst; }
    set showDst(newValue) {
        if (isString(newValue))
            newValue = toBoolean(newValue, false, true);
        if (this._showDst !== newValue) {
            this._showDst = newValue;
            this.updateCalendar();
        }
    }
    get minYear() { return this._minYear; }
    set minYear(year) {
        if (isString(year))
            year = toNumber(year);
        if (this._minYear !== year) {
            this._minYear = year;
        }
    }
    get maxYear() { return this._maxYear; }
    set maxYear(year) {
        if (isString(year))
            year = toNumber(year);
        if (this._maxYear !== year) {
            this._maxYear = year;
        }
    }
    get firstDay() { return this._firstDay; }
    set firstDay(dayOfWeek) {
        if (this._firstDay !== dayOfWeek) {
            this._firstDay = dayOfWeek;
            this.updateDayHeadings();
            this.updateCalendar();
        }
    }
    get weekDayFormat() { return this._weekDayFormat; }
    set weekDayFormat(value) {
        if (this._weekDayFormat !== value) {
            this._weekDayFormat = value;
            this.updateDayHeadings();
        }
    }
    get yearMonthFormat() { return this._yearMonthFormat; }
    set yearMonthFormat(value) {
        if (this._yearMonthFormat !== value) {
            this._yearMonthFormat = value;
            this.updateTitle();
        }
    }
    updateDayHeadings() {
        // Produce calendar day-of-week header using arbitrary days which start on the given first day of the week.
        this.daysOfWeek = [];
        for (let d = 1; d <= 7; ++d)
            this.daysOfWeek.push(new DateTime({ y: 2017, m: 1, d: d + this._firstDay, hrs: 12 }, 'UTC', this.dateTime.locale).format(this._weekDayFormat));
    }
    updateCalendar() {
        const year = this.ymd?.y ?? 2021;
        const month = this.ymd?.m ?? 1;
        const day = this.ymd?.d ?? 1;
        const calendar = this.dateTime.getCalendarMonth(year, month, this._firstDay);
        this.calendar = [];
        calendar.forEach((date, index) => {
            const dayLength = this.dateTime.getMinutesInDay(date.y, date.m, Math.abs(date.d));
            const row = Math.floor(index / 7);
            const col = index % 7;
            date.dayLength = dayLength;
            date.text = convertDigits(String(date.d), this.digitBase);
            date.otherMonth = (date.m !== month);
            date.highlight = (date.m === month && date.d === day);
            if (date.y < this._minYear || date.y > this._maxYear) {
                date.d = 0;
                date.text = '\u2022'; // bullet
                date.voidDay = true;
            }
            else if (dayLength === 0) {
                date.d = 0;
                date.text = '\u2716'; // heavy x
                date.voidDay = true;
            }
            else if (this._showDst && dayLength < 1440) {
                date.shortDay = true;
            }
            else if (this._showDst && dayLength > 1440) {
                date.longDay = true;
            }
            if (col === 0)
                this.calendar[row] = [];
            this.calendar[row][col] = date;
        });
        this.updateTitle();
        this.updateAltTable();
    }
    updateTitle() {
        this.title[0] = new DateTime({ y: this.ymd?.y ?? 2021, m: this.ymd?.m ?? 1 }, 'UTC', this.dateTime.locale).format(this._yearMonthFormat);
    }
    reset() {
        this.selectMode = SelectMode.DAY;
        this.modeTransition();
        this.updateCalendar();
    }
    stopTimer() {
        if (this.timerSubscription) {
            this.timerSubscription.unsubscribe();
            this.timerSubscription = undefined;
            if (this.pendingDelta) {
                this.onClick(this.pendingEvent, this.pendingDelta);
                this.pendingEvent = null;
                this.pendingDelta = 0;
            }
        }
    }
    onTouchStart(evt, delta) {
        if (evt.cancelable)
            evt.preventDefault();
        this.onMouseDown(null, delta, true);
    }
    onMouseDown(event, delta, fromTouch = false) {
        if (!this.timerSubscription && (!event || event.button === 0)) {
            this.pendingEvent = event;
            this.pendingDelta = fromTouch ? delta : 0;
            this.timerSubscription = timer(CLICK_REPEAT_DELAY, CLICK_REPEAT_RATE).subscribe(() => {
                this.pendingEvent = null;
                this.pendingDelta = fromTouch ? 0 : delta;
                this.onClick(event, delta);
            });
        }
    }
    onClick(event, delta) {
        const date = clone(this.ymd);
        if (this.selectMode === SelectMode.DAY) {
            if (event?.altKey)
                date.y += delta * 10;
            else if (event?.shiftKey)
                date.y += delta;
            else
                date.m += delta;
        }
        else if (this.selectMode === SelectMode.MONTH)
            date.y += delta;
        else
            date.y += delta * min(multiplier[this.selectMode] * 10, 1000);
        if (date.y < this._minYear || date.y === this._minYear && date.m < 1) {
            date.y = this._minYear;
            date.m = 1;
            date.d = 1;
        }
        else if (date.y > this._maxYear || date.y === this._maxYear && date.m > 12) {
            date.y = this._maxYear;
            date.m = 12;
            date.d = 31;
        }
        this.value = this.dateTime.normalizeDate(date);
    }
    onDayClick(dateInfo) {
        if (dateInfo.d > 0) {
            this.value = { y: dateInfo.y, m: dateInfo.m, d: dateInfo.d };
            this.dayClick.emit(dateInfo.d);
        }
    }
    onAltCellClick(value) {
        const date = clone(this.ymd);
        const month = this.months.indexOf(value);
        if (month > 0)
            date.m = month;
        else
            date.y = toNumber(value);
        const newDate = this.dateTime.normalizeDate(date);
        newDate.y = min(max(newDate.y, this._minYear), this._maxYear);
        this.value = newDate;
        --this.selectMode;
        this.modeTransition();
        this.updateAltTable();
    }
    onTitleClick() {
        this.selectMode = (this.selectMode + 1) % SelectMode.MODE_COUNT;
        this.modeTransition();
        this.updateAltTable();
    }
    counter(length) {
        return [...Array(length)].map((a, i) => i);
    }
    getTableValue(row, col, mode) {
        if (mode === SelectMode.DAY)
            return '';
        else if (mode === SelectMode.MONTH) {
            const m = row * 4 + col + 1;
            return (this.months[m] = new DateTime({ y: 4000, m, hrs: 12 }, 'UTC', this.dateTime.locale).format('MMM'));
        }
        let index = row * this.cols + col;
        if (index === 8 || index === 11)
            return '';
        index -= +(index > 7);
        const value = this.baseValue[mode] + index * multiplier[mode];
        const minn = div_rd(this._minYear, multiplier[mode]) * multiplier[mode];
        const maxx = (div_rd(this._maxYear - 1, multiplier[mode]) + 1) * multiplier[mode];
        if ((minn <= value && value <= maxx))
            return convertDigits(min(max(value, this._minYear), this._maxYear).toString(), this.digitBase);
        else
            return '';
    }
    updateAltTable() {
        const mode = this.selectMode;
        if (mode !== SelectMode.DAY) {
            this.title[mode] = min(max(div_rd(this.ymd.y, multiplier[mode]) * multiplier[mode], this._minYear), this._maxYear).toString();
            this.baseValue[mode] = div_rd(this.ymd.y, multiplier[mode] * 10) * multiplier[mode] * 10;
            if (mode === SelectMode.MONTH)
                this.highlightItem = this.months[this.ymd.m];
            else
                this.highlightItem = this.title[mode];
        }
    }
    getDayCellBackgroundContent(dateInfo) {
        if (this.backgroundDecorator)
            return this.backgroundDecorator(dateInfo);
        else
            return '';
    }
    getDayCellForegroundContent(dateInfo) {
        if (this.foregroundDecorator)
            return this.foregroundDecorator(dateInfo);
        else
            return '';
    }
    modeTransition() {
        this.inTransition = true;
        setTimeout(() => this.inTransition = false, 500);
    }
}
CalendarPanelComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: CalendarPanelComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
CalendarPanelComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.3", type: CalendarPanelComponent, selector: "tbw-calendar", inputs: { backgroundDecorator: "backgroundDecorator", foregroundDecorator: "foregroundDecorator", timezone: "timezone", locale: "locale", gregorianChangeDate: "gregorianChangeDate", showDst: "showDst", minYear: "minYear", maxYear: "maxYear", firstDay: "firstDay", weekDayFormat: "weekDayFormat", yearMonthFormat: "yearMonthFormat" }, outputs: { dayClick: "dayClick" }, providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CalendarPanelComponent), multi: true }], ngImport: i0, template: "<div class=\"tbw-cal-header\">\n  <div class=\"tbw-cal-arrow\" (touchstart)=\"onTouchStart($event, -textDirection)\" (touchend)=\"stopTimer()\"\n    (mousedown)=\"onMouseDown($event, -textDirection)\" (mouseup)=\"stopTimer()\" (mouseleave)=\"stopTimer()\"\n    (click)=\"onClick($event, -textDirection)\">\n    <svg viewBox=\"0 0 320 512\" style=\"height: 0.625em; width: 1em\">\n      <path fill=\"currentColor\" d=\"M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37\n        33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02\n        154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57\n        9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z\"></path>\n    </svg>\n  </div>\n  <div class=\"tbw-cal-title\" (click)=\"onTitleClick()\">&nbsp;\n    <div *ngFor=\"let n of counter(modeCount)\" [class.tbw-cal-inactive]=\"selectMode !== n\">{{title[n]}}</div>\n  </div>\n  <div class=\"tbw-cal-arrow\" (touchstart)=\"onTouchStart($event, textDirection)\" (touchend)=\"stopTimer()\"\n    (mousedown)=\"onMouseDown($event, textDirection)\" (mouseup)=\"stopTimer()\" (mouseleave)=\"stopTimer()\"\n    (click)=\"onClick($event,  textDirection)\">\n    <svg viewBox=\"0 0 320 512\" style=\"height: 0.625em; width: 1em\">\n      <path fill=\"currentColor\" d=\"M285.476 272.971L91.132 467.314c-9.373\n        9.373-24.569 9.373-33.941\n        0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484\n        101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373\n        24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001\n        33.941z\"></path>\n    </svg>\n  </div>\n</div>\n<div class=\"tbw-cal-table-wrapper\">\n  <table class=\"tbw-cal-table\" [class.tbw-cal-inactive]=\"selectMode !== 0\"\n      [style.direction]=\"textDirection < 0 ? 'rtl' : 'ltr'\">\n    <tr class=\"tbw-cal-day-header\">\n      <th *ngFor=\"let d of daysOfWeek\">{{d}}</th>\n    </tr>\n    <tr *ngFor=\"let week of calendar\">\n      <td *ngFor=\"let day of week\"\n          class=\"tbw-cal-day-cell\"\n          [ngClass]=\"{\n            'tbw-cal-highlight':   day.highlight,\n            'tbw-cal-short-day':   day.shortDay,\n            'tbw-cal-long-day':    day.longDay,\n            'tbw-cal-other-month': day.otherMonth,\n            'tbw-cal-void-day':    day.voidDay\n          }\"\n          (click)=\"onDayClick(day)\">\n        <div class=\"tbw-cal-day-cell-back\" [innerHTML]=\"getDayCellBackgroundContent(day)\"></div>\n        <div>{{day.text}}</div>\n        <div class=\"tbw-cal-day-cell-fore\" [innerHTML]=\"getDayCellForegroundContent(day)\"></div>\n      </td>\n    </tr>\n  </table>\n  <ng-container *ngFor=\"let n of counter(modeCount)\" [ngTemplateOutlet]=\"altTable\"\n      [ngTemplateOutletContext]=\"{ mode: n }\"></ng-container>\n  <ng-template #altTable let-mode=\"mode\">\n    <table class=\"tbw-cal-alt-table\" [class.tbw-cal-inactive]=\"selectMode === 0 || mode !== selectMode\"\n        [style.visibility]=\"inTransition ? 'visible' : null\">\n      <tr *ngFor=\"let row of counter(rows)\">\n        <td *ngFor=\"let col of counter(cols)\">\n          <ng-container [ngTemplateOutlet]=\"textItem\"\n              [ngTemplateOutletContext]=\"{ text: getTableValue(row, col, mode) }\"></ng-container>\n        </td>\n      </tr>\n    </table>\n  </ng-template>\n  <ng-template #textItem let-text=\"text\">\n    <div class=\"tbw-cal-alt-cell\" [class.tbw-cal-highlight]=\"text === highlightItem\"\n        [style.visibility]=\"inTransition ? 'visible' : null\"\n        (click)=\"onAltCellClick(text)\">\n      <span [style]=\"text.length < 5 ? undefined : 'font-size: ' + (4 / text.length) +'em'\">{{text}}</span>\n    </div>\n  </ng-template>\n</div>\n", styles: [":host{display:inline-block}.tbw-cal-inactive{opacity:0!important;pointer-events:none;visibility:hidden}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: CalendarPanelComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tbw-calendar', providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CalendarPanelComponent), multi: true }], template: "<div class=\"tbw-cal-header\">\n  <div class=\"tbw-cal-arrow\" (touchstart)=\"onTouchStart($event, -textDirection)\" (touchend)=\"stopTimer()\"\n    (mousedown)=\"onMouseDown($event, -textDirection)\" (mouseup)=\"stopTimer()\" (mouseleave)=\"stopTimer()\"\n    (click)=\"onClick($event, -textDirection)\">\n    <svg viewBox=\"0 0 320 512\" style=\"height: 0.625em; width: 1em\">\n      <path fill=\"currentColor\" d=\"M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37\n        33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02\n        154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57\n        9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z\"></path>\n    </svg>\n  </div>\n  <div class=\"tbw-cal-title\" (click)=\"onTitleClick()\">&nbsp;\n    <div *ngFor=\"let n of counter(modeCount)\" [class.tbw-cal-inactive]=\"selectMode !== n\">{{title[n]}}</div>\n  </div>\n  <div class=\"tbw-cal-arrow\" (touchstart)=\"onTouchStart($event, textDirection)\" (touchend)=\"stopTimer()\"\n    (mousedown)=\"onMouseDown($event, textDirection)\" (mouseup)=\"stopTimer()\" (mouseleave)=\"stopTimer()\"\n    (click)=\"onClick($event,  textDirection)\">\n    <svg viewBox=\"0 0 320 512\" style=\"height: 0.625em; width: 1em\">\n      <path fill=\"currentColor\" d=\"M285.476 272.971L91.132 467.314c-9.373\n        9.373-24.569 9.373-33.941\n        0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484\n        101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373\n        24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001\n        33.941z\"></path>\n    </svg>\n  </div>\n</div>\n<div class=\"tbw-cal-table-wrapper\">\n  <table class=\"tbw-cal-table\" [class.tbw-cal-inactive]=\"selectMode !== 0\"\n      [style.direction]=\"textDirection < 0 ? 'rtl' : 'ltr'\">\n    <tr class=\"tbw-cal-day-header\">\n      <th *ngFor=\"let d of daysOfWeek\">{{d}}</th>\n    </tr>\n    <tr *ngFor=\"let week of calendar\">\n      <td *ngFor=\"let day of week\"\n          class=\"tbw-cal-day-cell\"\n          [ngClass]=\"{\n            'tbw-cal-highlight':   day.highlight,\n            'tbw-cal-short-day':   day.shortDay,\n            'tbw-cal-long-day':    day.longDay,\n            'tbw-cal-other-month': day.otherMonth,\n            'tbw-cal-void-day':    day.voidDay\n          }\"\n          (click)=\"onDayClick(day)\">\n        <div class=\"tbw-cal-day-cell-back\" [innerHTML]=\"getDayCellBackgroundContent(day)\"></div>\n        <div>{{day.text}}</div>\n        <div class=\"tbw-cal-day-cell-fore\" [innerHTML]=\"getDayCellForegroundContent(day)\"></div>\n      </td>\n    </tr>\n  </table>\n  <ng-container *ngFor=\"let n of counter(modeCount)\" [ngTemplateOutlet]=\"altTable\"\n      [ngTemplateOutletContext]=\"{ mode: n }\"></ng-container>\n  <ng-template #altTable let-mode=\"mode\">\n    <table class=\"tbw-cal-alt-table\" [class.tbw-cal-inactive]=\"selectMode === 0 || mode !== selectMode\"\n        [style.visibility]=\"inTransition ? 'visible' : null\">\n      <tr *ngFor=\"let row of counter(rows)\">\n        <td *ngFor=\"let col of counter(cols)\">\n          <ng-container [ngTemplateOutlet]=\"textItem\"\n              [ngTemplateOutletContext]=\"{ text: getTableValue(row, col, mode) }\"></ng-container>\n        </td>\n      </tr>\n    </table>\n  </ng-template>\n  <ng-template #textItem let-text=\"text\">\n    <div class=\"tbw-cal-alt-cell\" [class.tbw-cal-highlight]=\"text === highlightItem\"\n        [style.visibility]=\"inTransition ? 'visible' : null\"\n        (click)=\"onAltCellClick(text)\">\n      <span [style]=\"text.length < 5 ? undefined : 'font-size: ' + (4 / text.length) +'em'\">{{text}}</span>\n    </div>\n  </ng-template>\n</div>\n", styles: [":host{display:inline-block}.tbw-cal-inactive{opacity:0!important;pointer-events:none;visibility:hidden}\n"] }]
        }], ctorParameters: function () { return []; }, propDecorators: { backgroundDecorator: [{
                type: Input
            }], foregroundDecorator: [{
                type: Input
            }], dayClick: [{
                type: Output
            }], timezone: [{
                type: Input
            }], locale: [{
                type: Input
            }], gregorianChangeDate: [{
                type: Input
            }], showDst: [{
                type: Input
            }], minYear: [{
                type: Input
            }], maxYear: [{
                type: Input
            }], firstDay: [{
                type: Input
            }], weekDayFormat: [{
                type: Input
            }], yearMonthFormat: [{
                type: Input
            }] } });

/* eslint-disable quote-props */
let This;
// @dynamic
class FormErrorDisplayComponent {
    shouldShowErrors() {
        return this.control &&
            this.control.errors &&
            (this.control.dirty || this.control.touched);
    }
    listOfErrors() {
        if (this.shouldShowErrors())
            return Object.keys(this.control.errors).map(field => This.getMessage(field, this.control.errors[field]));
        return [];
    }
    static getMessage(type, params) {
        if (params.message)
            return params.message;
        const errorFunction = FormErrorDisplayComponent.errorMessages[type];
        if (errorFunction)
            return errorFunction(params);
        else
            return `Unknown validation error "${type}"`;
    }
}
FormErrorDisplayComponent.errorMessages = {
    'required': () => 'This field is required',
    'invalid': (params) => params.message ? params.message : 'This field contains an invalid value',
    'minlength': (params) => `The min. allowed number of characters is ${params.requiredLength}`,
    'maxlength': (params) => `The max. allowed number of characters is ${params.requiredLength}`,
    'pattern': (params) => `The required pattern is: ${params.requiredPattern}`,
    'min': (params) => `The minimum allowed value is ${params.min}`,
    'minNumber': (params) => `The minimum allowed value is ${params.min}`,
    'max': (params) => `The maximum allowed value is ${params.max}`,
    'maxNumber': (params) => `The maximum allowed value is ${params.max}`,
};
FormErrorDisplayComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FormErrorDisplayComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FormErrorDisplayComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.3", type: FormErrorDisplayComponent, selector: "tbw-form-error-display", inputs: { control: "control" }, ngImport: i0, template: "<ul class=\"tbw-fed-error-list\"\n  *ngIf=\"shouldShowErrors()\">\n  <li class=\"tbw-warning-text tbw-fed-error\"\n    *ngFor=\"let error of listOfErrors()\">{{error}}</li>\n</ul>\n", styles: [""], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FormErrorDisplayComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tbw-form-error-display', template: "<ul class=\"tbw-fed-error-list\"\n  *ngIf=\"shouldShowErrors()\">\n  <li class=\"tbw-warning-text tbw-fed-error\"\n    *ngFor=\"let error of listOfErrors()\">{{error}}</li>\n</ul>\n" }]
        }], propDecorators: { control: [{
                type: Input
            }] } });
This = FormErrorDisplayComponent;

const docElem = document.documentElement;
const DEFAULT_MIN = 0.75;
// This component fails to work (sometimes in a very dramatic fashion, with the content it's
// supposed to be showing doing a dramatic animated dive off the screen!) on the original
// non-Chromium version of Microsoft Edge. Therefore we want to disable it for that browser.
// At the time of this writing, the user agent string for the original Edge has the word
// "Edge" fully spelled out, while the beta Chromium Edge simply has "Edg". If that changes
// in the future, the test for Edge below will have to be updated.
//
// It also doesn't work on IE.
const NOT_SUPPORTED = / Edge\//.test(navigator.userAgent) ||
    /(?:\b(MS)?IE\s+|\bTrident\/7\.0;.*\s+rv:)(\d+)/.test(navigator.userAgent);
class ShrinkWrapComponent {
    constructor() {
        this.afterInit = false;
        this._boundingElement = docElem;
        this._minScale = DEFAULT_MIN;
        this.lastWidth = 0;
        this.lastHeight = 0;
        this.lastSizerWidth = 0;
        this.innerStyle = {};
        this.marginX = 0;
        this.marginY = 0;
        this.outerStyle = {};
        this.scale = 1;
        this.thresholdStyle = {};
        this.scaleChange = new EventEmitter();
        this.onResize = debounce(() => {
            const innerWidth = this.inner.clientWidth - this.marginX * 2;
            const innerHeight = this.inner.clientHeight - this.marginY;
            const boundingWidth = this.getBoundingWidth();
            let sizerWidth = this.sizer.clientWidth;
            this.outerStyle = this.outerStyle.padding ? { padding: this.outerStyle.padding } : {};
            if (this._boundingElement === docElem)
                sizerWidth = Math.min(sizerWidth, boundingWidth);
            else {
                sizerWidth = boundingWidth;
                this.outerStyle['max-width'] = boundingWidth + 'px';
            }
            if (Math.abs(sizerWidth - this.lastSizerWidth) <= 1 &&
                Math.abs(innerWidth - this.lastWidth) <= 1 &&
                Math.abs(innerHeight - this.lastHeight) <= 1) {
                return;
            }
            let scalingWidth = innerWidth;
            if (this.thresholdStyle.width)
                scalingWidth = this.thresholdSizer.getBoundingClientRect().width;
            else if (!this.thresholdWidth && scalingWidth > sizerWidth)
                this.thresholdWidth = scalingWidth;
            else if (this.thresholdWidth)
                scalingWidth = this.thresholdWidth;
            // Compensation, if needed, for the 0.05px padding used to prevent margin collapse.
            const sizerAdjust = (sizerWidth < scalingWidth) ? 0.1 : 0;
            this.scale = Math.min(Math.max((sizerWidth - sizerAdjust) / scalingWidth, this.minScale), 1);
            const scaledWidth = scalingWidth * this.scale;
            const scaledHeight = scaledWidth * innerHeight / innerWidth;
            this.marginX = this.scale === 1 ? 0 : Math.ceil((scaledWidth - innerWidth) / 2);
            this.marginY = this.scale === 1 ? 0 : Math.ceil(scaledHeight - innerHeight);
            this.lastWidth = innerWidth;
            this.lastHeight = innerHeight;
            this.lastSizerWidth = sizerWidth;
            if (this.scale === 1) {
                this.innerStyle = {};
                delete this.outerStyle.padding;
            }
            else {
                this.innerStyle = {
                    transform: `scale(${this.scale})`,
                    'transform-origin': 'top center',
                    margin: `0 ${this.marginX}px ${this.marginY}px ${this.marginX}px`,
                    'max-width': `${scalingWidth}px` // eslint-disable-line @typescript-eslint/naming-convention
                };
                this.outerStyle.padding = '0.05px'; // prevents margin collapse
            }
            this.scaleChange.emit(this.scale);
        }, 10);
    }
    get minScale() { return this._minScale; }
    set minScale(newValue) {
        if (isString(newValue)) {
            const $ = /([\d.]+)(%)?/.exec(newValue);
            if ($) {
                newValue = Number($[1]);
                if ($[2])
                    newValue /= 100;
            }
        }
        if (isNaN(newValue) || !newValue)
            this._minScale = DEFAULT_MIN;
        else
            this._minScale = Math.min(Math.max(newValue, 0.01), 1);
    }
    set boundingElement(newValue) {
        if (!newValue)
            newValue = docElem;
        else if (isString(newValue))
            newValue = document.getElementById(newValue);
        if (this._boundingElement !== newValue) {
            this.removeResizeListener(this._boundingElement);
            this._boundingElement = newValue;
            if (!NOT_SUPPORTED) {
                this.addResizeListener(this._boundingElement);
                if (this.afterInit)
                    setTimeout(() => this.onResize());
            }
        }
    }
    set threshold(newValue) {
        let changed = false;
        if (isNumber(newValue) || !isNaN(Number(newValue))) {
            newValue = Number(newValue);
            if (this.thresholdWidth !== newValue) {
                this.thresholdWidth = newValue;
                this.thresholdStyle = {};
                changed = true;
            }
        }
        else if (this.thresholdStyle.width !== newValue) {
            this.thresholdStyle = newValue && !NOT_SUPPORTED ? { width: newValue } : {};
            this.thresholdWidth = undefined;
            changed = true;
        }
        if (changed) {
            this.lastSizerWidth = 0;
            if (this.afterInit && !NOT_SUPPORTED)
                setTimeout(() => this.onResize());
        }
    }
    ngOnInit() {
        this.inner = this.innerRef.nativeElement;
        this.sizer = this.sizerRef.nativeElement;
        this.thresholdSizer = this.thresholdSizerRef.nativeElement;
        if (NOT_SUPPORTED)
            return;
        this.addResizeListener(this.inner);
        this.addResizeListener(this.sizer);
        this.addResizeListener(this.thresholdSizer);
        this.addResizeListener(this._boundingElement);
        window.addEventListener('resize', this.onResize);
    }
    ngAfterViewInit() {
        this.afterInit = true;
        if (NOT_SUPPORTED)
            return;
        setTimeout(() => this.onResize());
    }
    ngOnDestroy() {
        if (NOT_SUPPORTED)
            return;
        this.removeResizeListener(this.inner);
        this.removeResizeListener(this.sizer);
        this.removeResizeListener(this.thresholdSizer);
        this.removeResizeListener(this._boundingElement);
        window.removeEventListener('resize', this.onResize);
    }
    getBoundingWidth() {
        let elem = this._boundingElement;
        let width = elem.clientWidth;
        const isDocElem = (elem === docElem);
        if (isDocElem)
            elem = document.body;
        const style = window.getComputedStyle(elem, null);
        const contentBox = style.getPropertyValue('box-sizing') === 'content-box';
        if (isDocElem)
            width -= parseFloat(style.getPropertyValue('margin-left') || '0') +
                parseFloat(style.getPropertyValue('margin-right') || '0');
        if (contentBox)
            width -= parseFloat(style.getPropertyValue('border-left-width') || '0') +
                parseFloat(style.getPropertyValue('border-right-width') || '0');
        return width;
    }
    addResizeListener(elem) {
        if (elem !== docElem)
            addResizeListener(elem, this.onResize);
    }
    removeResizeListener(elem) {
        if (elem !== docElem)
            removeResizeListener(elem, this.onResize);
    }
}
ShrinkWrapComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: ShrinkWrapComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
ShrinkWrapComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.3", type: ShrinkWrapComponent, selector: "tbw-shrink-wrap", inputs: { minScale: "minScale", boundingElement: "boundingElement", threshold: "threshold" }, outputs: { scaleChange: "scaleChange" }, viewQueries: [{ propertyName: "innerRef", first: true, predicate: ["inner"], descendants: true, static: true }, { propertyName: "sizerRef", first: true, predicate: ["sizer"], descendants: true, static: true }, { propertyName: "thresholdSizerRef", first: true, predicate: ["thresholdSizer"], descendants: true, static: true }], ngImport: i0, template: "<div class=\"outer-wrapper\" [ngStyle]=\"outerStyle\">\n  <div #sizer class=\"sizer\">&nbsp;</div>\n  <div #thresholdSizer class=\"threshold-sizer\"\n      [ngStyle]=\"thresholdStyle\">&nbsp;\n  </div>\n  <div #inner class=\"inner-wrapper\">\n    <div [ngStyle]=\"innerStyle\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [".outer-wrapper{position:relative}.sizer,.threshold-sizer{height:1px;opacity:0;position:absolute;width:100%;z-index:-1000}.inner-wrapper{position:relative;width:-moz-fit-content;width:fit-content}\n"], dependencies: [{ kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: ShrinkWrapComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tbw-shrink-wrap', template: "<div class=\"outer-wrapper\" [ngStyle]=\"outerStyle\">\n  <div #sizer class=\"sizer\">&nbsp;</div>\n  <div #thresholdSizer class=\"threshold-sizer\"\n      [ngStyle]=\"thresholdStyle\">&nbsp;\n  </div>\n  <div #inner class=\"inner-wrapper\">\n    <div [ngStyle]=\"innerStyle\">\n      <ng-content></ng-content>\n    </div>\n  </div>\n</div>\n", styles: [".outer-wrapper{position:relative}.sizer,.threshold-sizer{height:1px;opacity:0;position:absolute;width:100%;z-index:-1000}.inner-wrapper{position:relative;width:-moz-fit-content;width:fit-content}\n"] }]
        }], propDecorators: { innerRef: [{
                type: ViewChild,
                args: ['inner', { static: true }]
            }], sizerRef: [{
                type: ViewChild,
                args: ['sizer', { static: true }]
            }], thresholdSizerRef: [{
                type: ViewChild,
                args: ['thresholdSizer', { static: true }]
            }], minScale: [{
                type: Input
            }], boundingElement: [{
                type: Input
            }], threshold: [{
                type: Input
            }], scaleChange: [{
                type: Output
            }] } });

class TimeEditorLimit {
    constructor(limit, asLow = false, asTai = false) {
        this.asLow = asLow;
        if (isString(limit) && !/\d-\d/.test(limit) && limit.includes(':'))
            limit = '1970-01-01 ' + limit;
        if (limit == null || limit === '')
            limit = asLow ? '-9999' : '9999';
        else if (isNumber(limit) && abs(limit) < 10000)
            limit = limit.toString();
        else if (isDate(limit)) {
            asTai = false;
            limit = limit.getTime();
        }
        else if (isString(limit) && /^[-+]?\d{5,}$/.test(limit.trim()))
            limit = toNumber(limit);
        if (isNumber(limit)) {
            let dt;
            if (asTai) {
                this.tai = limit;
                this.utc = (dt = new DateTime({ tai: limit })).utcMillis;
            }
            else {
                this.tai = (dt = new DateTime(limit)).taiMillis;
                this.utc = limit;
            }
            this.text = dt.format(limit % 1000 === 0 ? ttime.DATETIME_LOCAL_SECONDS : ttime.DATETIME_LOCAL_MS);
            this.year = new Date(this.utc).getFullYear();
        }
        else {
            this.text = limit = limit.trim().replace(/[-:]$/, '');
            const dateTime = new DateTime(limit);
            if (!dateTime.valid)
                throw new Error(`Time limit "${limit}" not valid`);
            if (/[_a-z]$/i.test(limit)) {
                this.tai = dateTime.taiMillis;
                this.utc = dateTime.utcMillis;
                this.year = dateTime.wallTime.y;
            }
            else if (/^[\d]+$/.test(limit)) {
                this.year = dateTime.wallTime.y;
                this.wallTime = { y: this.year };
            }
            else {
                this.wallTime = dateTime.wallTimeSparse;
                if (!limit.includes(':')) {
                    if (!/-[^-]+-/.test(limit))
                        this.wallTime.d = undefined;
                    else if (!/[\sT]\d+$/i.test(limit))
                        this.wallTime.hrs = undefined;
                    else
                        this.wallTime.min = undefined;
                }
                else if (!/:[^:]+:/.test(limit))
                    this.wallTime.sec = undefined;
                else if (!limit.includes('.'))
                    this.wallTime.millis = undefined;
                this.year = this.wallTime.y;
            }
            if (!this.wallTime)
                this.wallTime = { y: this.year };
        }
    }
    compare(dateTime) {
        if (this.wallTime == null)
            return sign(this.tai - dateTime.taiMillis);
        const wt = dateTime.wallTime;
        const diffs = [
            this.wallTime.y - wt.y,
            this.wallTime.m - wt.m,
            this.wallTime.d - wt.d,
            this.wallTime.hrs - wt.hrs,
            this.wallTime.min - wt.min,
            this.wallTime.sec - wt.sec,
            this.wallTime.millis - wt.millis,
        ];
        for (const diff of diffs) {
            if (isNaN(diff))
                return 0;
            else if (diff !== 0)
                return sign(diff);
        }
        return 0;
    }
    getWallTime(dateTime) {
        if (this.wallTime == null)
            return dateTime.isTai() ? new DateTime({ tai: this.tai }).wallTimeSparse :
                dateTime.clone().setUtcMillis(this.utc).wallTimeSparse;
        dateTime = new DateTime(this.wallTime, Timezone.ZONELESS);
        if (!this.asLow) {
            if (this.wallTime.m == null)
                dateTime = dateTime.endOf(DateTimeField.YEAR);
            else if (this.wallTime.d == null)
                dateTime = dateTime.endOf(DateTimeField.MONTH);
            else if (this.wallTime.hrs == null)
                dateTime = dateTime.endOf(DateTimeField.DAY);
            else if (this.wallTime.min == null)
                dateTime = dateTime.endOf(DateTimeField.HOUR);
            else if (this.wallTime.sec == null)
                dateTime = dateTime.endOf(DateTimeField.MINUTE);
            else if (this.wallTime.millis == null)
                dateTime = dateTime.endOf(DateTimeField.SECOND);
        }
        return dateTime.wallTimeSparse;
    }
}

var parse = ttime.parse;
var DateFieldOrder;
(function (DateFieldOrder) {
    DateFieldOrder[DateFieldOrder["PER_LOCALE"] = 0] = "PER_LOCALE";
    DateFieldOrder[DateFieldOrder["YMD"] = 1] = "YMD";
    DateFieldOrder[DateFieldOrder["DMY"] = 2] = "DMY";
    DateFieldOrder[DateFieldOrder["MDY"] = 3] = "MDY";
})(DateFieldOrder || (DateFieldOrder = {}));
var DateTimeStyle;
(function (DateTimeStyle) {
    DateTimeStyle[DateTimeStyle["DATE_AND_TIME"] = 0] = "DATE_AND_TIME";
    DateTimeStyle[DateTimeStyle["DATE_ONLY"] = 1] = "DATE_ONLY";
    DateTimeStyle[DateTimeStyle["TIME_ONLY"] = 2] = "TIME_ONLY";
})(DateTimeStyle || (DateTimeStyle = {}));
var HourStyle;
(function (HourStyle) {
    HourStyle[HourStyle["PER_LOCALE"] = 0] = "PER_LOCALE";
    HourStyle[HourStyle["HOURS_24"] = 1] = "HOURS_24";
    HourStyle[HourStyle["AM_PM"] = 2] = "AM_PM";
})(HourStyle || (HourStyle = {}));
var MeridiemStyle;
(function (MeridiemStyle) {
    MeridiemStyle[MeridiemStyle["PER_LOCALE"] = 0] = "PER_LOCALE";
    MeridiemStyle[MeridiemStyle["TRAILING"] = 1] = "TRAILING";
    MeridiemStyle[MeridiemStyle["LEADING"] = 2] = "LEADING";
})(MeridiemStyle || (MeridiemStyle = {}));
var YearStyle;
(function (YearStyle) {
    YearStyle[YearStyle["POSITIVE_ONLY"] = 0] = "POSITIVE_ONLY";
    YearStyle[YearStyle["AD_BC"] = 1] = "AD_BC";
    YearStyle[YearStyle["SIGNED"] = 2] = "SIGNED";
})(YearStyle || (YearStyle = {}));
const OCC2 = '\u20092\u2009';
const ISO_T = '\u200AT\u200A';
const NO_BREAK_SPACE = '\u00A0';
const platformNativeDateTime = (isIOS() || (isAndroid() && isChrome()));
const RTL_CHECK = /[\u0590-\u07BF\u0860-\u08FF\u200F\u2067\u202B\u202E\uFB1D-\uFDCF\uFDF0-\uFDFF\uFE70-\uFEFF]/;
const DAY_MILLIS = 86400000;
const OPTIONS_DATE_ONLY = {
    dateTimeStyle: DateTimeStyle.DATE_ONLY,
};
const OPTIONS_ISO = {
    hourStyle: HourStyle.HOURS_24,
    dateFieldOrder: DateFieldOrder.YMD,
    dateFieldSeparator: '-',
    dateTimeSeparator: ISO_T,
    dateTimeStyle: DateTimeStyle.DATE_AND_TIME,
    decimal: '.',
    numbering: 'latn',
    showSeconds: true,
    timeFieldSeparator: ':',
    twoDigitYear: false
};
const OPTIONS_ISO_DATE = {
    dateFieldOrder: DateFieldOrder.YMD,
    dateFieldSeparator: '-',
    dateTimeStyle: DateTimeStyle.DATE_ONLY,
    numbering: 'latn',
    twoDigitYear: false
};
const OPTIONS_ISO_TIME = {
    dateTimeStyle: DateTimeStyle.TIME_ONLY,
    decimal: '.',
    hourStyle: HourStyle.HOURS_24,
    numbering: 'latn',
    showSeconds: true,
    timeFieldSeparator: ':',
};
const namedOptions = {
    date_only: OPTIONS_DATE_ONLY,
    iso: OPTIONS_ISO,
    iso_date: OPTIONS_ISO_DATE,
    iso_time: OPTIONS_ISO_TIME
};
// @dynamic
class TimeEditorComponent extends DigitSequenceEditorDirective {
    constructor(zone, cdr, sanitizer) {
        super(zone, cdr, sanitizer);
        this.amPmKeys = ['a', 'p'];
        this.amPmStrings = ['AM', 'PM'];
        this.baseDigit = '0';
        this.centuryBase = DateTime.getDefaultCenturyBase();
        this.dateTime = new DateTime();
        this.eraKeys = ['b', 'a'];
        this.eraStrings = ['BC', 'AD'];
        this.explicitMinYear = false;
        this.firstTouch = true;
        this.hasLocalTimeFocus = false;
        this._gregorianChangeDate = '1582-10-15';
        this.maxLimit = new TimeEditorLimit('9999');
        this.minLimit = new TimeEditorLimit('-9999');
        this._nativeDateTime = false;
        this._options = {};
        this.outOfRange = false;
        this.originalMinYear = this.minLimit.year;
        this.rtlMark = false;
        this.sizerDigit = '0';
        this._tai = false;
        this.twoDigitYear = false;
        this.yearDigits = 4;
        this.eraIndex = -1;
        this.signIndex = -1;
        this.yearIndex = -1;
        this.monthIndex = -1;
        this.dayIndex = -1;
        this.hourIndex = -1;
        this.minuteIndex = -1;
        this.secondIndex = -1;
        this.millisIndex = -1;
        this.amPmIndex = -1;
        this.occIndex = -1;
        this.offsetIndex = -1;
        this.dstIndex = -1;
        this.localTimeFormat = 'datetime-local';
        this.timeStep = '60';
        this.dateChange = new EventEmitter();
        this.useAlternateTouchHandling = false;
    }
    static get supportsNativeDateTime() { return platformNativeDateTime; }
    get value() { return this._tai ? this.dateTime.taiMillis : this.dateTime.utcMillis; }
    set value(newValue) {
        this.setValue(newValue, true);
    }
    get date() {
        if (!this._date)
            this._date = this.dateTime.toIsoString(10);
        return this._date;
    }
    set date(newValue) {
        const currDate = this.dateTime.wallTimeSparse;
        newValue = new DateTime(newValue, Timezone.ZONELESS).wallTimeSparse;
        if (newValue && !newValue.error &&
            (currDate.y !== newValue.y || currDate.m !== newValue.m || currDate.d !== newValue.d)) {
            const newDate = this.dateTime.clone();
            newDate.set('year', newValue.y);
            newDate.set('month', newValue.m);
            newDate.set('day', newValue.d);
            this.setValue(this._tai ? newDate.taiMillis : newDate.utcMillis, true);
        }
    }
    validateImpl(_value, _control) {
        if (this.outOfRange) {
            const year = this.dateTime.wallTime.year;
            if (year < this.minYear())
                return { min: { message: `Year must be at least ${this.minYear()}` } };
            else if (year > this.maxYear())
                return { max: { message: `Year must not be after ${this.maxYear()}` } };
            else if (this.minLimit.compare(this.dateTime) > 0)
                return { min: { message: `Date/time must be on or after ${this.minLimit.text}` } };
            else if (this.maxLimit.compare(this.dateTime) < 0)
                return { max: { message: `Date/time must be on or after ${this.maxLimit.text}` } };
            return { invalid: true };
        }
        return null;
    }
    applyPastedText(text) {
        const parsed = this.parseText(text);
        if (parsed == null) {
            this.errorFlash();
            return;
        }
        else if (isNumber(parsed)) {
            this.dateTime.taiMillis = parsed;
            this.outOfRange = false;
            this.reportValueChange();
            this.updateDigits();
        }
        else {
            this.dateTime.wallTime = parsed;
            this.outOfRange = false;
            this.reportValueChange();
            this.updateDigits();
        }
        if (this.outOfRange)
            this.errorFlash();
        else
            this.confirmFlash();
    }
    getClipboardText() {
        return this.getValueAsText();
    }
    get options() { return this._options; }
    set options(newValue) {
        if (isArray(newValue)) {
            const orig = newValue;
            if (isString(orig[0]))
                newValue = clone(namedOptions[orig[0].toLowerCase()] ?? {});
            else
                newValue = clone(orig[0]);
            orig.forEach((opt, index) => {
                if (index > 0) {
                    if (isString(opt))
                        opt = namedOptions[opt.toLowerCase()] ?? {};
                    Object.assign(newValue, opt);
                }
            });
        }
        else if (isString(newValue))
            newValue = namedOptions[newValue.toLowerCase()] ?? {};
        if (!isEqual(this._options, newValue)) {
            const wasTimeOnly = (this._options.dateTimeStyle === DateTimeStyle.TIME_ONLY);
            this._options = clone(newValue);
            this.createDigits();
            this.createDisplayOrder();
            if (!wasTimeOnly && this._options.dateTimeStyle === DateTimeStyle.TIME_ONLY &&
                (this.value < 0 || this.value >= DAY_MILLIS)) {
                setTimeout(() => this.value = mod(this.value, DAY_MILLIS));
            }
        }
    }
    get tai() { return this._tai; }
    set tai(newValue) {
        if (isString(newValue))
            newValue = toBoolean(newValue, false, true);
        if (this._tai !== newValue) {
            this._tai = newValue;
            this.reportValueChange();
        }
    }
    onLocalTimeChange() {
        const newValue = this.localTime.value;
        if (this.localTimeValue !== newValue) {
            this.localTimeValue = newValue;
            let newTime;
            if (newValue) {
                const w = this.dateTime.wallTime;
                let $;
                if (($ = /(\d\d\d\d)-(\d\d)-(\d\d)(?:T(\d\d):(\d\d))?/.exec(newValue))) {
                    const d = $.slice(1).map(n => Number(n));
                    if ($[4] == null) {
                        d[3] = w.hrs;
                        d[4] = w.min;
                    }
                    newTime = new DateTime({ y: d[0], m: d[1], d: d[2], hrs: d[3], min: d[4], sec: 0 }, this.timezone, this._gregorianChangeDate).utcMillis;
                }
                else if (($ = /(\d\d):(\d\d)(?:(\d\d))?/.exec(newValue))) {
                    const t = $.slice(1).map(n => Number(n));
                    newTime = new DateTime({ y: w.y, m: w.m, d: w.d, hrs: t[0], min: t[1], sec: t[2] ?? 0 }, this.timezone, this._gregorianChangeDate).utcMillis;
                }
            }
            else
                newTime = Date.now();
            if (newTime !== undefined && !isNaN(newTime))
                this.value = !!this.tai ? utToTaiMillis(newTime, true) : newTime;
            if (!this.localTimeValue)
                setTimeout(() => this.updateLocalTime());
        }
    }
    ngOnInit() {
        super.ngOnInit();
        this.createLocalTimeInput();
        this.localTime?.setAttribute('tabindex', this.useAlternateTouchHandling ? this.tabindex : '-1');
    }
    onLocalTimeFocus(value) {
        if (value && this.viewOnly || this.initialNativeDateTimePrompt())
            return;
        if (this.hasLocalTimeFocus !== value) {
            this.hasLocalTimeFocus = value;
            this.checkFocus();
        }
    }
    createLocalTimeInput() {
        if (!platformNativeDateTime)
            return;
        this.localTime = document.createElement('input');
        this.localTime.type = this.localTimeFormat;
        this.localTime.autocomplete = 'off';
        this.localTime.setAttribute('autocapitalize', 'off');
        this.localTime.setAttribute('autocomplete', 'off');
        this.localTime.setAttribute('autocorrect', 'off');
        this.localTime.setAttribute('tabindex', this.disabled ? '-1' : this.tabindex);
        this.localTime.setAttribute('min', this.localTimeMin);
        this.localTime.setAttribute('max', this.localTimeMax);
        this.localTime.setAttribute('step', this.timeStep);
        this.localTime.disabled = this.disableMobileKeyboard;
        this.localTime.style.position = 'absolute';
        this.localTime.style.opacity = '0';
        this.localTime.style['caret-color'] = 'transparent';
        this.localTime.style['pointer-events'] = 'none';
        this.localTime.style.left = '0';
        this.localTime.style.top = '-6px';
        this.localTime.addEventListener('focus', () => this.onLocalTimeFocus(true));
        this.localTime.addEventListener('blur', () => this.onLocalTimeFocus(false));
        this.localTime.addEventListener('input', () => this.onLocalTimeChange());
        this.wrapper.parentElement.appendChild(this.localTime);
        this.wrapper.setAttribute('tabindex', '-1');
    }
    hasAComponentInFocus() {
        return super.hasAComponentInFocus() || this.hasLocalTimeFocus;
    }
    checkFocus() {
        if (this.initialNativeDateTimePrompt())
            return;
        super.checkFocus();
    }
    gainedFocus() {
        if (this.initialNativeDateTimePrompt())
            return;
        if (!this.hasLocalTimeFocus && this.isNativeDateTimeActive() && performance.now() > this.lastTabTime + FORWARD_TAB_DELAY)
            this.localTime?.focus();
    }
    lostFocus() {
        this.touched();
    }
    adjustState() {
        super.adjustState();
        if (this.localTime) {
            const disabled = (this._disabled || this._viewOnly || this._floating || this._disableMobileKeyboard ||
                !this.useAlternateTouchHandling);
            this.localTime.disabled = disabled;
            this.localTime.setAttribute('tabindex', disabled ? '-1' : this.tabindex);
        }
    }
    onTouchStart(index, evt) {
        if (!this.initialNativeDateTimePrompt(evt))
            super.onTouchStart(index, evt);
    }
    onTouchMove(evt) {
        if (!this.nativeDateTime)
            super.onTouchMove(evt);
    }
    initialNativeDateTimePrompt(evt) {
        if (TimeEditorComponent.supportsNativeDateTime && this.promptForNative &&
            !this.disabled && !this.viewOnly && this.firstTouch) {
            this.firstTouch = false;
            if (this.promptForNative && this.promptForNative()) {
                if (evt)
                    evt.preventDefault();
                return true;
            }
        }
        return false;
    }
    onTouchStartAlternate(_index, _event) {
        const style = this._options.dateTimeStyle;
        const format = (style === DateTimeStyle.TIME_ONLY ? 'time' :
            (style === DateTimeStyle.DATE_ONLY ? 'date' : 'datetime-local'));
        const timeStep = this._options.showSeconds ? '1' : '60';
        if (this.localTimeFormat !== format || this.timeStep !== timeStep) {
            // Changing the format of the input (using the "type" attribute) sets off a number of updates
            // that don't stabilize very well if we leave it up to Angular's change detection process to do
            // all the updating, so we'll update all the changing input attributes and input value
            // directly, all in one go.
            this.localTimeFormat = format;
            this.timeStep = timeStep;
            this.adjustLocalTimeMin();
            this.adjustLocalTimeMax();
            this.updateLocalTime();
            this.localTime.type = format;
            this.localTime.min = this.localTimeMin;
            this.localTime.max = this.localTimeMax;
            this.localTime.setAttribute('step', this.timeStep);
            this.localTime.value = this.localTimeValue;
            this.cdr.detectChanges();
        }
        this.localTime.focus();
        setTimeout(() => this.localTime.click(), 250);
    }
    writeValue(newValue) {
        this.setValue(newValue);
    }
    setValue(newValue, doCallback = false) {
        if (newValue == null)
            return;
        let tai = this._tai;
        if (newValue instanceof DateTime) {
            tai = true;
            newValue = newValue.taiMillis;
        }
        else if (newValue instanceof Date) {
            tai = false;
            newValue = newValue.getTime();
        }
        else if (isString(newValue)) {
            tai = true;
            newValue = new DateTime(newValue, this.dateTime.timezone, this.dateTime.locale, this.dateTime.getGregorianChange()).taiMillis;
        }
        if ((tai && this.dateTime.taiMillis !== newValue) || (!tai && this.dateTime.utcMillis !== newValue)) {
            if (this._options.dateTimeStyle === DateTimeStyle.TIME_ONLY)
                newValue = mod(newValue, DAY_MILLIS);
            if (tai)
                this.dateTime.taiMillis = newValue;
            else
                this.dateTime.utcMillis = newValue;
            const wasOutOfRange = this.outOfRange;
            this.updateDigits();
            this._date = undefined;
            if (doCallback || (this.validateAll && !wasOutOfRange && this.outOfRange))
                this.reportValueChange();
        }
    }
    get wallTime() { return this.dateTime.wallTime; }
    get min() { return this._min; }
    set min(value) {
        if (this._min !== value) {
            const before = this.validateImpl(this.value);
            this._min = value;
            this.minLimit = new TimeEditorLimit(value, true, this.tai);
            if (this.minLimit.wallTime == null && value != null && value !== '') {
                this.explicitMinYear = true;
                this.centuryBase = this.minLimit.year;
            }
            else
                this.explicitMinYear = false;
            this.adjustLocalTimeMin();
            this.outOfRange = false;
            setTimeout(() => {
                this.updateDigits();
                const after = this.validateImpl(this.value);
                if (!isEqual(before, after))
                    this.valueHasChanged(true);
            });
        }
    }
    get max() { return this._max; }
    set max(value) {
        if (this._max !== value) {
            const before = this.validateImpl(this.value);
            this._max = value;
            this.maxLimit = new TimeEditorLimit(value, false, this.tai);
            this.adjustLocalTimeMax();
            this.outOfRange = false;
            setTimeout(() => {
                this.updateDigits();
                const after = this.validateImpl(this.value);
                if (!isEqual(before, after))
                    this.valueHasChanged(true);
            });
        }
    }
    adjustLocalTimeMin() {
        if (this.localTimeFormat === 'time' || (this.minLimit.year == null && this.minLimit.wallTime == null))
            this.localTimeMin = null;
        else if (this.minLimit.year != null)
            this.localTimeMin = padLeft(max(this.minLimit.year, 1), 4, '0') + '-01-01' + (this.localTimeFormat === 'date' ? '' : 'T00:00');
        else if (this.minLimit.year >= 1)
            this.localTimeMin = getISOFormatDate(this.minLimit.year, this.minLimit.wallTime.m, this.minLimit.wallTime.d);
        else
            this.localTimeMin = '0000-01-01';
        this.localTime?.setAttribute('min', this.localTimeMin);
    }
    adjustLocalTimeMax() {
        if (this.localTimeFormat === 'time')
            this.localTimeMax = null;
        else if (this.maxLimit.year != null)
            this.localTimeMax = padLeft(this.maxLimit.year, 4, '0') + '-12-31' + (this.localTimeFormat === 'date' ? '' : 'T23:59');
        else if (this.maxLimit.year >= 1)
            this.localTimeMax = getISOFormatDate(this.maxLimit.year, this.maxLimit.wallTime.m, this.maxLimit.wallTime.d);
        else
            this.localTimeMax = '0000-01-01';
        this.localTime?.setAttribute('max', this.localTimeMax);
    }
    get timezone() { return this.dateTime.timezone; }
    set timezone(newZone) {
        if (this.dateTime.timezone !== newZone) {
            this.dateTime.timezone = newZone;
            this.updateDigits();
        }
    }
    // eslint-disable-next-line accessor-pairs
    set gregorianChangeDate(value) {
        if (this._gregorianChangeDate !== value) {
            this._gregorianChangeDate = value;
            this.dateTime.setGregorianChange(value);
            this.updateDigits();
        }
    }
    get nativeDateTime() { return this._nativeDateTime; }
    set nativeDateTime(newValue) {
        if (this._nativeDateTime !== newValue) {
            this._nativeDateTime = newValue;
            this.useAlternateTouchHandling = this.selectionHidden = newValue && TimeEditorComponent.supportsNativeDateTime;
            if (this.hiddenInput)
                this.hiddenInput.disabled = !!this.disabled || this.useAlternateTouchHandling;
            if (this.localTime && TimeEditorComponent.supportsNativeDateTime)
                this.localTime.setAttribute('tabindex', newValue ? '0' : '-1');
            if (newValue) {
                let wallTime = this.dateTime.wallTime;
                this.min = max(this.originalMinYear, 1).toString();
                if (wallTime.y < this.minLimit.year) {
                    wallTime = { y: this.minLimit.year, m: 1, d: 1, hrs: 0, min: 0, sec: 0 };
                    this.dateTime.wallTime = wallTime;
                    this.reportValueChange();
                    this.updateDigits();
                }
            }
            else
                this.min = this.originalMinYear.toString();
            this.cdr.detectChanges();
        }
    }
    isNativeDateTimeActive() {
        return DigitSequenceEditorDirective.touchHasOccurred && this.nativeDateTime && TimeEditorComponent.supportsNativeDateTime;
    }
    createHiddenInput() {
        super.createHiddenInput();
        if (this.hiddenInput)
            this.hiddenInput.disabled = !!this.disabled || this.useAlternateTouchHandling;
    }
    createDigits() {
        this.items.length = 0;
        this.eraIndex = -1;
        this.signIndex = -1;
        this.yearIndex = -1;
        this.monthIndex = -1;
        this.dayIndex = -1;
        this.hourIndex = -1;
        this.minuteIndex = -1;
        this.secondIndex = -1;
        this.millisIndex = -1;
        this.amPmIndex = -1;
        this.occIndex = -1;
        this.offsetIndex = -1;
        this.dstIndex = -1;
        this.rtlMark = false;
        const steps = [];
        const dateSteps = [];
        const timeSteps = [];
        const opts = this._options;
        const hasDate = (opts.dateTimeStyle !== DateTimeStyle.TIME_ONLY);
        const hasTime = (opts.dateTimeStyle !== DateTimeStyle.DATE_ONLY);
        const locale = opts.locale || defaultLocale;
        const extendLocale = (l) => l + '-u-ca-gregory' + (opts.numbering ? '-nu-' + opts.numbering : '');
        const localeExt = isArray(locale) ? locale.map(l => extendLocale(l)) : (locale && extendLocale(locale));
        const decimal = opts.decimal ||
            (hasIntlDateTime && convertDigitsToAscii(Intl.NumberFormat(locale).format(1.2)).replace(/\d/g, '').charAt(0)) || '.';
        let es = opts.eraSeparator ?? NO_BREAK_SPACE;
        let ds = opts.dateFieldSeparator ?? '/';
        let ts = opts.timeFieldSeparator ?? ':';
        let dts = opts.dateTimeSeparator ?? NO_BREAK_SPACE;
        let leadingMeridian = opts.meridiemStyle === MeridiemStyle.LEADING;
        const baseDigit = [];
        this.rtl = RTL_CHECK.test(new Date().toLocaleString(locale, { month: 'long' }));
        if (hasDate) {
            let sampleDate = new DateTime('3333-11-22Z', 'UTC', locale).format('IS');
            let dfo = opts.dateFieldOrder ?? DateFieldOrder.PER_LOCALE;
            this.twoDigitYear = opts.yearStyle !== YearStyle.AD_BC && opts.yearStyle !== YearStyle.SIGNED &&
                (opts.twoDigitYear ?? !sampleDate.includes('3333'));
            this.yearDigits = (this.twoDigitYear ? 2 : 4);
            this.rtl = this.rtl || RTL_CHECK.test(sampleDate);
            this.rtlMark = this.rtl && sampleDate.includes('\u200F');
            ds = opts.dateFieldSeparator ||
                (/\d(\u200F?\D)\d/.exec(convertDigitsToAscii(sampleDate, baseDigit)) ?? [])[1] || '/';
            sampleDate = convertDigitsToAscii(sampleDate);
            if (dfo === DateFieldOrder.PER_LOCALE) {
                if (/3.*1.*2/.test(sampleDate))
                    dfo = DateFieldOrder.YMD;
                else if (/2.*1.*3/.test(sampleDate))
                    dfo = DateFieldOrder.DMY;
                else
                    dfo = DateFieldOrder.MDY;
            }
            switch (dfo) {
                case DateFieldOrder.YMD:
                    dateSteps.push('year', 'ds', 'month', 'ds', 'day');
                    break;
                case DateFieldOrder.DMY:
                    dateSteps.push('day', 'ds', 'month', 'ds', 'year');
                    break;
                default: dateSteps.push('month', 'ds', 'day', 'ds', 'year');
            }
            if (opts.yearStyle === YearStyle.SIGNED)
                dateSteps.splice(dateSteps.indexOf('year'), 0, 'sign');
            else if (opts.yearStyle === YearStyle.AD_BC || isArray(opts.yearStyle)) {
                dateSteps.push('era');
                if (hasIntlDateTime && opts.eraSeparator == null) {
                    const era = convertDigitsToAscii(newDateTimeFormat(localeExt, { era: 'short' }).format(0));
                    const sample = convertDigitsToAscii(newDateTimeFormat(localeExt, { year: 'numeric', era: 'short' }).format(0)).replace(era, 'xxx');
                    es = (/\d([\Dx]+)xxx/.exec(sample) ?? ['', NO_BREAK_SPACE])[1].replace(/\s+/g, NO_BREAK_SPACE);
                }
                if (isArray(opts.yearStyle))
                    this.eraStrings = clone(opts.yearStyle);
                else {
                    const bc = new DateTime('-0001-01-01', 'UTC', locale).format('N');
                    const ad = new DateTime(0, 'UTC', locale).format('N');
                    this.eraStrings = [bc, ad];
                }
                this.eraKeys = ['b', 'a'];
                const eras = this.eraStrings;
                for (let i = 0; i < eras[0].length && eras[1].length; ++i) {
                    if (eras[0].charAt(i) !== eras[1].charAt(i)) {
                        this.eraKeys = [eras[0].charAt(i).toLocaleLowerCase(locale), eras[1].charAt(i).toLocaleLowerCase(locale)];
                        break;
                    }
                }
            }
        }
        let dateRtl = false;
        if (hasTime) {
            const sampleTime = new DateTime(0, 'UTC', locale).format('IxS');
            dateRtl = RTL_CHECK.test(sampleTime);
            this.rtl = this.rtl || dateRtl;
            ts = opts.timeFieldSeparator ||
                convertDigitsToAscii(sampleTime, baseDigit).replace(/(^\D+)|[\d\s\u2000-\u200F]/g, '').charAt(0) || ':';
            timeSteps.push('hour', 'ts', 'minute');
            if (opts.showSeconds || opts.millisDigits > 0)
                timeSteps.push('ts', 'second');
            if (opts.millisDigits > 0)
                timeSteps.push('millis');
            let amPm = false;
            if (opts.hourStyle == null || opts.hourStyle === HourStyle.PER_LOCALE ||
                opts.hourStyle === HourStyle.AM_PM) {
                const am = new DateTime(0, 'UTC', locale).format('A');
                if (opts.hourStyle === HourStyle.AM_PM || sampleTime.includes(am)) {
                    amPm = true;
                    this.amPmStrings = [am, new DateTime('1970-01-01T13:00', 'UTC', locale).format('A')];
                }
                if (opts.meridiemStyle == null || opts.meridiemStyle === MeridiemStyle.PER_LOCALE) {
                    const sample = new DateTime(0, 'UTC', locale).format('IxS');
                    const $ = /\d/.exec(sample);
                    const digitIndex = $?.index ?? -1;
                    const amIndex = sample.indexOf(this.amPmStrings[0]);
                    if (amIndex >= 0 && amIndex < digitIndex)
                        leadingMeridian = true;
                }
            }
            else if (isArray(opts.hourStyle)) {
                amPm = true;
                this.amPmStrings = clone(opts.hourStyle ?? ['AM', 'PM']);
            }
            if (amPm) {
                if (leadingMeridian)
                    timeSteps.splice(0, 0, 'amPm');
                else
                    timeSteps.push('aps', 'amPm');
                this.amPmKeys = [];
                const aps = this.amPmStrings;
                for (let i = 0; i < aps[0].length && aps[1].length; ++i) {
                    if (aps[0].charAt(i) !== aps[1].charAt(i)) {
                        this.amPmKeys = [aps[0].charAt(i).toLocaleLowerCase(locale), aps[1].charAt(i).toLocaleLowerCase(locale)];
                        break;
                    }
                }
            }
            if (opts.showOccurrence)
                timeSteps.push('occ');
            if (opts.showUtcOffset)
                timeSteps.push('off');
            if (opts.showDstSymbol)
                timeSteps.push('dst');
        }
        if (this.rtlMark) {
            const hasEra = dateSteps.includes('era');
            if (hasEra)
                dateSteps.splice(-1, 1);
            dateSteps.reverse();
            if (hasEra)
                dateSteps.push('era');
        }
        if (hasIntlDateTime && hasTime && hasDate && opts.dateTimeSeparator == null) {
            const sample = convertDigitsToAscii(newDateTimeFormat(localeExt, { day: 'numeric', hour: 'numeric', hour12: false, hourCycle: 'h23' }).format(0));
            dts = (/\d(\D+)\d/.exec(sample) ?? ['', NO_BREAK_SPACE])[1];
            if (!new DateTime(0, 'UTC', locale).format('ISS').includes(dts))
                dts = NO_BREAK_SPACE;
            else
                dts = dts.replace(/\s+/g, NO_BREAK_SPACE);
        }
        if (opts.timeFirst && hasTime && hasDate)
            steps.push(...timeSteps, 'dts', ...dateSteps);
        else {
            if (hasDate)
                steps.push(...dateSteps);
            if (hasTime) {
                if (hasDate)
                    steps.push('dts');
                steps.push(...timeSteps);
            }
        }
        const addDigits = (n, format) => repeat(n, () => this.items.push({ value: 0, digit: true, editable: true, format }));
        for (const step of steps) {
            const i = this.items.length;
            switch (step) {
                case 'year':
                    this.yearIndex = i;
                    addDigits(this.yearDigits);
                    break;
                case 'era':
                    this.items.push({ value: es, static: true, width: '0.25em' });
                    this.eraIndex = i + 1;
                    this.items.push({ value: this.eraStrings[1], editable: true, format: 'N', sizer: this.eraStrings.join('\n') });
                    break;
                case 'sign':
                    this.signIndex = i;
                    this.items.push({ value: NO_BREAK_SPACE, editable: true, monospaced: true, sign: true, sizer: '-' });
                    break;
                case 'ds':
                    this.items.push({ value: ds, bidi: true, static: true });
                    break;
                case 'month':
                    this.monthIndex = i;
                    addDigits(2, 'M');
                    break;
                case 'day':
                    this.dayIndex = i;
                    addDigits(2, 'D');
                    break;
                case 'dts':
                    this.items.push({ value: dts, static: true,
                        fade: dts === ISO_T,
                        width: dts === NO_BREAK_SPACE ? '0.6em' : undefined });
                    break;
                case 'hour':
                    this.hourIndex = i;
                    addDigits(2, steps.includes('amPm') ? 'h' : 'H');
                    break;
                case 'aps':
                    this.items.push({ value: NO_BREAK_SPACE, static: true, width: '0.25em' });
                    break;
                case 'amPm':
                    this.amPmIndex = i;
                    this.items.push({ value: this.amPmStrings[0], editable: true, format: 'A', sizer: this.amPmStrings.join('\n') });
                    break;
                case 'ts':
                    this.items.push({ value: ts, bidi: true, static: true });
                    break;
                case 'minute':
                    this.minuteIndex = i;
                    addDigits(2, 'm');
                    break;
                case 'second':
                    this.secondIndex = i;
                    addDigits(2, 's');
                    break;
                case 'millis':
                    this.items.push({ value: decimal, static: true });
                    this.millisIndex = i + 1;
                    addDigits(min(opts.millisDigits, 3), 'S');
                    break;
                case 'occ':
                    this.occIndex = i;
                    this.items.push({ value: NO_BREAK_SPACE, format: 'r', indicator: true, sizer: OCC2, name: '2occ', deltaY: 0.2 });
                    break;
                case 'off':
                    this.offsetIndex = i;
                    this.items.push({ value: '+00:00', format: 'Z', indicator: true, monospaced: true, sizer: '+00:00', maxChars: 6 });
                    break;
                case 'dst':
                    this.dstIndex = i;
                    this.items.push({ value: NO_BREAK_SPACE, format: 'v', indicator: true, sizer: '^\n§\n#\n~\n?\n\u2744' });
                    break;
            }
        }
        if (this.yearIndex >= 0) {
            if (this.twoDigitYear) {
                this.items[this.yearIndex].format = 'YY';
                if (!this.explicitMinYear)
                    this.centuryBase = DateTime.getDefaultCenturyBase();
                else
                    this.centuryBase = this.minLimit.year;
            }
            else if (this.eraIndex >= 0)
                this.items[this.yearIndex].format = 'y';
            else
                this.items[this.yearIndex].format = 'Y';
        }
        if (steps.includes('second'))
            this.selection = this.secondIndex + 1;
        else if (steps.includes('minute'))
            this.selection = this.minuteIndex + 1;
        else if (steps.includes('day'))
            this.selection = this.dayIndex + 1;
        this.items.push({ divider: true });
        this.items.push({ spinner: true });
        if (opts.numbering)
            convertDigitsToAscii(new Intl.NumberFormat(locale + '-u-nu-' + opts.numbering).format(0), baseDigit);
        this.baseDigit = baseDigit[0] ?? '0';
        this.findSizerDigitAndHeight(this.items[this.amPmIndex]?.sizer, this.items[this.eraIndex]?.sizer);
        this.updateDigits();
    }
    findSizerDigitAndHeight(...textItems) {
        this.baselineShift = '0';
        this.lineHeight = undefined;
        this.sizerDigit = this.baseDigit;
        if (!this.wrapper || !this.emSizer)
            return;
        const baseCode = this.baseDigit.charCodeAt(0);
        let maxWidth = getTextWidth(this.sizerDigit, this.wrapper);
        // eslint-disable-next-line prefer-const
        let { lineHeight, fullAscent, descent, extraAscent, extraDescent } = getFontMetrics(this.wrapper, this.baseDigit);
        for (let i = 1; i <= 9; ++i) {
            const digit = String.fromCodePoint(baseCode + i);
            const width = getTextWidth(digit, this.wrapper);
            const metrics = getFontMetrics(this.wrapper, digit);
            if (maxWidth < width) {
                maxWidth = width;
                this.sizerDigit = digit;
            }
            extraAscent = max(metrics.extraAscent, extraAscent);
            extraDescent = max(metrics.extraDescent, extraDescent);
        }
        const textChars = (textItems ?? []).join('').replace(/[\s\xA0\u2000-\u200F]/g, '').split('');
        for (const ch of textChars) {
            const metrics = getFontMetrics(this.wrapper, ch);
            extraAscent = max(metrics.extraAscent, extraAscent);
            extraDescent = max(metrics.extraDescent, extraDescent);
        }
        const extraLineHeight = lineHeight + (extraAscent - fullAscent) + (extraDescent - descent);
        if (extraLineHeight > lineHeight) {
            this.lineHeight = ceil(extraLineHeight / lineHeight * 122) + '%';
            const em = this.emSizer.getBoundingClientRect().width;
            let baselineShift;
            if (extraAscent > fullAscent && extraDescent > descent)
                baselineShift = (extraAscent - fullAscent) - (extraDescent - descent);
            else if (extraAscent > fullAscent)
                baselineShift = extraAscent - fullAscent;
            else
                baselineShift = descent - extraDescent;
            this.baselineShift = (baselineShift / em / 2) + 'em';
        }
    }
    getAlignmentForItem(item) {
        if (this.rtl && ((this.amPmIndex >= 0 && item.index === this.amPmIndex) ||
            (this.eraIndex >= 0 && item.index === this.eraIndex))) {
            const displayIndex = this.displayItems.findIndex(i => item === i);
            if (displayIndex >= 0 && this.displayItems[displayIndex + 1]?.digit)
                return 'flex-end';
        }
        return super.getAlignmentForItem(item);
    }
    getClassForItem(item) {
        let qlass;
        if (item?.name === '2occ')
            qlass = 'tbw-te-subscript';
        else
            qlass = super.getClassForItem(item) ?? '';
        const y = this.dateTime.wallTime.y;
        if ((this.outOfRange && item.editable) ||
            (this.timezone.error &&
                ((this.offsetIndex >= 0 && item.index === this.offsetIndex) ||
                    (this.dstIndex >= 0 && item.index === this.dstIndex))) ||
            (this.yearIndex >= 0 && this.yearIndex <= item.index && item.index < this.yearIndex + this.yearDigits && !this.yearInRange(y)))
            qlass += ' tbw-dse-bad-value';
        return qlass?.trim() || null;
    }
    maxYear() {
        const twoDigitMax = this.twoDigitYear ? this.centuryBase + 99 : Number.MAX_SAFE_INTEGER;
        return min(twoDigitMax, this.maxLimit.year);
    }
    minYear() {
        const eraMin = this.eraIndex < 0 ? (this.signIndex < 0 ? 1 : Number.MIN_SAFE_INTEGER) : Number.MIN_SAFE_INTEGER;
        const twoDigitMin = this.twoDigitYear ? this.centuryBase : Number.MIN_SAFE_INTEGER;
        return max(eraMin, twoDigitMin, this.minLimit.year);
    }
    yearInRange(y = this.dateTime.wallTime.y) {
        return this.minYear() <= y && y <= this.maxYear();
    }
    updateDigits(dateTime, delta = 0, selection = -1) {
        const programmatic = (dateTime === undefined);
        dateTime = (dateTime === undefined ? this.dateTime : dateTime);
        const i = this.items;
        const field = delta === 0 ? 'value' : delta < 0 ? 'swipeBelow' : 'swipeAbove';
        const alt_field = 'alt_' + field;
        if (!dateTime?.valid) {
            if (delta !== 0 && selection >= 0)
                i[selection][field] = i[selection][alt_field] = NO_BREAK_SPACE;
            return;
        }
        let wallTime = dateTime.wallTimeSparse;
        let reUpdate = false;
        let outOfRange = false;
        if (this.minLimit.compare(dateTime) > 0) {
            outOfRange = true;
            if (!programmatic) {
                wallTime = this.minLimit.getWallTime(dateTime);
                reUpdate = true;
            }
        }
        else if (this.maxLimit.compare(dateTime) < 0) {
            outOfRange = true;
            if (!programmatic) {
                wallTime = this.maxLimit.getWallTime(dateTime);
                reUpdate = true;
            }
        }
        if (delta === 0)
            this.outOfRange = outOfRange;
        if (reUpdate && delta === 0) {
            timer(0).subscribe(() => {
                this.errorFlash();
                dateTime.wallTime = wallTime;
                this.reportValueChange();
                this.updateDigits();
            });
            return;
        }
        let y = abs(wallTime.y);
        if (this.eraIndex >= 0)
            i[this.eraIndex][field] = this.eraStrings[wallTime.y < 1 ? 0 : 1];
        else if (this.signIndex >= 0)
            i[this.signIndex][field] = (wallTime.y < 0 ? '-' : this.signIndex > 0 ? '+' : NO_BREAK_SPACE);
        if (this.yearIndex >= 0) {
            if (this.signIndex < 0 && wallTime.y < 1)
                y = 1 - wallTime.y;
            if (this.twoDigitYear)
                y %= 100;
            this.setDigits(this.yearIndex, this.yearDigits, y, field);
        }
        this.setDigits(this.monthIndex, 2, wallTime.m, field);
        this.setDigits(this.dayIndex, 2, wallTime.d, field);
        if (this.hourIndex >= 0) {
            let h = wallTime.hrs;
            if (this.amPmIndex >= 0) {
                i[this.amPmIndex][field] = this.amPmStrings[h < 12 ? 0 : 1];
                h = (h === 0 ? 12 : h <= 12 ? h : h - 12);
            }
            this.setDigits(this.hourIndex, 2, h, field);
        }
        this.setDigits(this.minuteIndex, 2, wallTime.min, field);
        this.setDigits(this.secondIndex, 2, wallTime.sec, field);
        this.setDigits(this.millisIndex, this._options.millisDigits, wallTime.millis / 10 ** (3 - this._options.millisDigits), field);
        if (this.occIndex >= 0)
            i[this.occIndex][field] = (dateTime.wallTime.occurrence === 2 ? OCC2 : NO_BREAK_SPACE);
        if (this.offsetIndex >= 0)
            i[this.offsetIndex][field] = dateTime.timezone.getFormattedOffset(dateTime.utcMillis);
        if (this.dstIndex >= 0) {
            if (this.timezone.error)
                i[this.dstIndex][field] = '?';
            else if (!dateTime.dstOffsetSeconds)
                i[this.dstIndex][field] = NO_BREAK_SPACE;
            else {
                i[this.dstIndex][field] = Timezone.getDstSymbol(dateTime.dstOffsetSeconds);
            }
        }
        this.items.forEach(item => {
            if (item.editable && isNumber(item.value)) {
                item.sizer = this.sizerDigit;
                if (this.baseDigit === '0')
                    item[alt_field] = undefined;
                else
                    item[alt_field] = convertDigits(item[field].toString(), this.baseDigit);
            }
        });
        if (delta === 0)
            this.updateLocalTime();
    }
    updateLocalTime() {
        const w = this.dateTime.wallTime;
        let year = w.y;
        if (this.isNativeDateTimeActive() && year < 1)
            year = 1;
        if (this.localTimeFormat === 'time')
            this.localTimeValue = `${padLeft(w.hrs, 2, '0')}:${padLeft(w.min, 2, '0')}`;
        else
            this.localTimeValue = `${padLeft(year, 4, '0')}-${padLeft(w.m, 2, '0')}-${padLeft(w.d, 2, '0')}` +
                (this.localTimeFormat === 'date' ? '' : `T${padLeft(w.hrs, 2, '0')}:${padLeft(w.min, 2, '0')}`);
        if (this.localTime)
            this.localTime.value = this.localTimeValue;
    }
    getWallTimeFromDigits() {
        const wt = this.dateTime.wallTime;
        const is = this.items;
        const yi = this.yearIndex;
        let year = this.getDigits(yi, this.yearDigits, wt.y);
        if (this.twoDigitYear) {
            year = mod(year, 100);
            year = year - this.centuryBase % 100 + this.centuryBase + (year < this.centuryBase % 100 ? 100 : 0);
        }
        if (yi >= 0 && this.eraIndex >= 0 && is[this.eraIndex].value === this.eraStrings[0])
            year = 1 - year;
        else if (yi >= 0 && this.signIndex >= 0 && is[this.signIndex].value === '-')
            year *= -1;
        const month = this.getDigits(this.monthIndex, 2, wt.m);
        const date = this.getDigits(this.dayIndex, 2, wt.d);
        let hour = this.getDigits(this.hourIndex, 2, wt.hrs);
        const minute = this.getDigits(this.minuteIndex, 2, wt.min);
        const second = this.getDigits(this.secondIndex, 2, wt.sec);
        const millis = this.getDigits(this.millisIndex, this._options.millisDigits, wt.millis);
        if (this.hourIndex >= 0 && this.amPmIndex >= 0) {
            if (is[this.amPmIndex].value === this.amPmStrings[0])
                hour = (hour === 12 ? 0 : min(hour, 12));
            else if (hour !== 12)
                hour = min(hour + 12, 23);
        }
        return {
            y: year, m: month, d: date, hrs: hour, min: minute, sec: second, millis,
            occurrence: this.dateTime.wallTime.occurrence
        };
    }
    roll(sign, sel = this.selection, updateTime = true) {
        const dateTime = this.dateTime.clone();
        const origDate = this._date;
        const tai = this._tai;
        let change = 0;
        let field = DateTimeField.YEAR;
        const wallTime = this.dateTime.wallTime;
        let newWallTime;
        const wasNegative = (this.items[this.signIndex]?.value === '-');
        const mDigits = this._options.millisDigits;
        const minYear = this.minYear();
        const maxYear = this.maxYear();
        const wasOutOfRange = this.outOfRange;
        if (wasOutOfRange && this.minLimit.compare(dateTime) > 0)
            newWallTime = this.minLimit.wallTime;
        else if (wasOutOfRange && this.maxLimit.compare(dateTime) < 0)
            newWallTime = this.maxLimit.wallTime;
        else if (wallTime.y < minYear)
            newWallTime = Object.assign(this.dateTime.wallTimeSparse, { y: minYear });
        else if (wallTime.y > maxYear)
            newWallTime = Object.assign(this.dateTime.wallTimeSparse, { y: maxYear });
        else if (this.eraIndex >= 0 && sel === this.eraIndex) {
            const newYear = 1 - wallTime.y;
            if (newYear < minYear || newYear > maxYear) {
                if (updateTime)
                    this.errorFlash();
                return;
            }
            change = sign * (newYear - wallTime.y);
        }
        else if (this.signIndex >= 0 && sel === this.signIndex) { // Sign of year
            if (-wallTime.y < minYear || -wallTime.y > maxYear) {
                if (updateTime)
                    this.errorFlash();
                return;
            }
            change = -sign * wallTime.y * 2;
        }
        else if (this.millisIndex >= 0 && this.millisIndex <= sel && sel < this.millisIndex + mDigits) {
            field = tai ? DateTimeField.MILLI_TAI : DateTimeField.MILLI;
            change = 10 ** (2 + this.millisIndex - sel);
        }
        else if (this.secondIndex >= 0 && (sel === this.secondIndex || sel === this.secondIndex + 1)) {
            field = tai ? DateTimeField.SECOND_TAI : DateTimeField.SECOND;
            change = (sel === this.secondIndex ? 10 : 1);
        }
        else if (this.minuteIndex >= 0 && (sel === this.minuteIndex || sel === this.minuteIndex + 1)) {
            field = tai ? DateTimeField.MINUTE_TAI : DateTimeField.MINUTE;
            change = (sel === this.minuteIndex ? 10 : 1);
        }
        else if (this.hourIndex >= 0 && (sel === this.hourIndex || sel === this.hourIndex + 1)) {
            field = tai ? DateTimeField.HOUR_TAI : DateTimeField.HOUR;
            change = (sel === this.hourIndex ? 10 : 1);
        }
        else if (this.amPmIndex >= 0 && sel === this.amPmIndex) {
            field = DateTimeField.HOUR;
            change = (wallTime.hrs < 12 ? 12 : -12) * sign;
        }
        else if (this.dayIndex >= 0 && (sel === this.dayIndex || sel === this.dayIndex + 1)) {
            field = tai ? DateTimeField.DAY_TAI : DateTimeField.DAY;
            change = (sel === this.dayIndex ? 10 : 1);
        }
        else if (this.monthIndex >= 0 && (sel === this.monthIndex || sel === this.monthIndex + 1)) {
            field = DateTimeField.MONTH;
            change = (sel === this.monthIndex ? 10 : 1);
        }
        else if (this.yearIndex >= 0 && this.yearIndex <= sel && sel < this.yearIndex + this.yearDigits) {
            field = DateTimeField.YEAR;
            change = 10 ** (this.yearIndex + this.yearDigits - sel - 1);
        }
        if (updateTime)
            this.outOfRange = false;
        if (change === 0 && !newWallTime)
            return;
        if (newWallTime)
            dateTime.wallTime = newWallTime;
        else
            dateTime.add(field, change * sign);
        if (this.minLimit.compare(dateTime) > 0 || this.maxLimit.compare(dateTime) < 0 ||
            dateTime.wallTime.y < minYear || dateTime.wallTime.y > maxYear) {
            if (updateTime)
                this.errorFlash();
            else
                this.updateDigits(null, sign, sel);
            if (!updateTime || !wasOutOfRange)
                return;
        }
        if (updateTime) {
            if (tai)
                this.dateTime.taiMillis = dateTime.taiMillis;
            else
                this.dateTime.utcMillis = dateTime.utcMillis;
            this.reportValueChange();
            this.updateDigits(this.dateTime);
            this._date = this.dateTime.toIsoString(10);
            if (this._date !== origDate)
                this.dateChange.emit(this._date);
            if (sel === this.signIndex && this.dateTime.wallTime.y === 0)
                this.items[sel].value = (wasNegative ? (sel > 0 ? '+' : NO_BREAK_SPACE) : '-');
        }
        else
            this.updateDigits(dateTime, sign);
    }
    onKey(key) {
        const keyLc = key.toLocaleLowerCase(this._options.locale);
        const editable = !this.disabled && !this.viewOnly;
        if (editable &&
            ((this.selection === this.eraIndex && (key === '1' || key === '2' || this.eraKeys.includes(keyLc))) ||
                (this.selection === this.signIndex && ' -+='.includes(key)) ||
                (this.selection === this.amPmIndex && (key === '1' || key === '2' || this.amPmKeys.includes(keyLc)))))
            this.digitTyped(keyLc.charCodeAt(0), keyLc);
        else
            super.onKey(key);
    }
    digitTyped(charCode, key) {
        const i = this.items;
        const origDay = this.dayIndex >= 0 ?
            i[this.dayIndex].value * 10 + i[this.dayIndex + 1].value : 0;
        const sel = this.selection;
        const origValue = i[sel].value;
        const origDate = this._date;
        let newValue = origValue;
        if (sel === this.eraIndex) {
            if (key === '1' || key === this.eraKeys[1])
                newValue = this.eraStrings[1];
            else if (key === '2' || key === this.eraKeys[0])
                newValue = this.eraStrings[0];
            else {
                if ('12'.indexOf(key) < 0 && !this.eraKeys.includes(key))
                    this.errorFlash();
                return;
            }
        }
        else if (sel === this.signIndex) {
            if (key === ' ' || key === '+' || key === '=')
                newValue = sel > 0 ? '+' : NO_BREAK_SPACE;
            else if (key === '-')
                newValue = '-';
            else {
                if (' +=-'.indexOf(key) < 0)
                    this.errorFlash();
                return;
            }
        }
        else if (sel === this.amPmIndex) {
            if (key === '1' || key === this.amPmKeys[0])
                newValue = this.amPmStrings[0];
            else if (key === '2' || key === this.amPmKeys[1])
                newValue = this.amPmStrings[1];
            else {
                if ('12'.indexOf(key) < 0 && !this.amPmKeys.includes(key))
                    this.errorFlash();
                return;
            }
        }
        else if (48 <= charCode && charCode < 58) {
            newValue = charCode - 48;
            if (this.amPmIndex >= 0 &&
                ((sel === this.hourIndex && newValue > 1) ||
                    (sel === this.hourIndex + 1 && i[sel - 1].value * 10 + newValue > 12))) {
                this.errorFlash();
                return;
            }
        }
        else {
            this.errorFlash();
            return;
        }
        if (newValue !== origValue || this.outOfRange) {
            i[sel].value = newValue;
            let wallTime = this.getWallTimeFromDigits();
            if (this.yearIndex >= 0 &&
                (sel === this.signIndex || (this.yearIndex <= sel && sel < this.yearIndex + this.yearDigits))) {
                const yi = this.yearIndex;
                const yd = this.yearDigits;
                const len = sel - yi + 2;
                const yv = this.getDigits(yi, yd);
                const yp = ((yv >= 0 ? '+' : '-') + abs(yv).toString().padStart(yd, '0')).substring(0, len);
                let yearTarget;
                let timeTarget;
                if (this.minLimit.year != null && wallTime.y < this.minLimit.year) {
                    yearTarget = this.minLimit.year;
                    timeTarget = this.minLimit.wallTime ?? this.minLimit.utc;
                }
                else if (this.maxLimit.year != null && wallTime.y > this.maxLimit.year) {
                    yearTarget = this.maxLimit.year;
                    timeTarget = this.maxLimit.wallTime ?? this.maxLimit.utc;
                }
                if (yearTarget != null) {
                    const tp = ((yearTarget >= 0 ? '+' : '-') + abs(yearTarget).toString().padStart(yd, '0')).substring(0, len);
                    if (yp === tp) {
                        this.updateDigits(new DateTime(timeTarget, this.timezone));
                        wallTime = this.getWallTimeFromDigits();
                    }
                }
            }
            const wasLeap = (this._tai && wallTime.sec >= 60);
            let extraSec = (wasLeap ? 1 : 0);
            if (sel === this.secondIndex && newValue === 6 && this._tai) {
                const testTime = clone(wallTime);
                testTime.sec = 60;
                if (new DateTime(testTime, this.dateTime.timezone).wallTime.sec === 60)
                    extraSec = 10;
            }
            if ((this.minLimit.year != null && wallTime.y < this.minLimit.year) ||
                (this.maxLimit.year != null && wallTime.y > this.maxLimit.year) ||
                wallTime.m > 19 || wallTime.d > 39 ||
                wallTime.hrs > 29 || wallTime.min > 59 || wallTime.sec > 59 + extraSec || wallTime.millis > 999) {
                i[sel].value = origValue;
                if (!this.outOfRange) {
                    this.errorFlash();
                    return;
                }
            }
            if (sel === this.monthIndex)
                wallTime.m = min(max(wallTime.m, 1), 12);
            if (sel === this.dayIndex)
                wallTime.d = min(max(wallTime.d, 1), 31);
            if (sel === this.hourIndex)
                wallTime.hrs = min(wallTime.hrs, 23);
            if (sel === this.secondIndex && extraSec)
                wallTime.sec = min(wallTime.sec, 60);
            if (wallTime.m === 0 || wallTime.m > 12 || wallTime.d === 0 || wallTime.hrs > 23) {
                i[sel].value = origValue;
                if (!this.outOfRange) {
                    this.errorFlash();
                    return;
                }
            }
            else if (!this.dateTime.isValidDate(wallTime)) {
                const lastDate = this.dateTime.getLastDateInMonth(wallTime.y, wallTime.m);
                // Check for date gaps caused by Julian-to-Gregorian transition, e.g. October 1582
                // having no 5th-14h, with 10/04 followed immediately by 10/15.
                const gap = this.dateTime.getMissingDateRange(wallTime.y, wallTime.m);
                if (gap && gap[0] <= wallTime.d && wallTime.d <= gap[1]) // Mind the gap! Step to either side of it.
                    wallTime.d = (origDay > wallTime.d && gap[0] !== 1 ? gap[0] - 1 : min(gap[1] + 1, lastDate));
                if (origDay > 0 && wallTime.d > lastDate) {
                    if ((lastDate < 30 && wallTime.d >= 30 && sel === this.dayIndex) ||
                        (wallTime.d > lastDate && sel === this.dayIndex + 1)) {
                        i[sel].value = origValue;
                        if (!this.outOfRange) {
                            this.errorFlash();
                            return;
                        }
                    }
                    wallTime.d = lastDate;
                }
            }
            this.dateTime.wallTime = wallTime;
            if (wasLeap && this.dateTime.wallTime.sec === 0 && sel !== this.secondIndex && sel !== this.secondIndex + 1)
                this.dateTime.subtract(DateTimeField.SECOND_TAI, 1);
            if (this.minLimit.compare(this.dateTime) > 0) {
                this.dateTime.wallTime = this.minLimit.getWallTime(this.dateTime);
                this.warningFlash();
            }
            else if (this.maxLimit.compare(this.dateTime) < 0) {
                const max = this.maxLimit.getWallTime(this.dateTime);
                const md = this._options.millisDigits;
                if (!this._options.showSeconds) {
                    max.sec = 0;
                    max.millis = 0;
                }
                else if (this._options.millisDigits == null || md < 3)
                    max.millis = floor(max.millis / 10 ** (3 - md)) * 10 ** (3 - md);
                this.dateTime.wallTime = max;
                this.warningFlash();
            }
            this.outOfRange = false;
            this.reportValueChange();
            this.updateDigits();
            this._date = this.dateTime.toIsoString(10);
            if (this._date !== origDate)
                this.dateChange.emit(this._date);
            if (this.outOfRange)
                setTimeout(() => this.outOfRange = false);
            if (sel === this.signIndex && this.dateTime.wallTime.y === 0)
                this.items[sel].value = newValue;
        }
        this.cursorForward();
    }
    getValueAsText() {
        const opts = this._options;
        const style = opts.dateTimeStyle;
        const hasDate = style !== DateTimeStyle.TIME_ONLY;
        const hasTime = style !== DateTimeStyle.DATE_ONLY;
        if (this.outOfRange || !this.dateTime.valid)
            return 'Invalid ' + (hasDate && hasTime ? 'date/time' : hasDate ? 'date' : 'time');
        return convertDigits(this.dateTime.format(this.getFormat(), opts.locale), this.baseDigit);
    }
    parseText(s) {
        s = s.replace(/[§#~?\u2744\u200F]/g, '').trim();
        let zone;
        const $ = /(Z|\bTAI|\bUTC?|[+-]\d[0-9:]*)$/i.exec(s);
        if ($) {
            zone = $[1];
            s = s.slice(0, -zone.length).trim();
            if (zone === 'Z')
                zone = 'UTC';
        }
        try {
            if (!zone)
                return parseISODateTime(s, true);
            const dt = new DateTime(s, zone);
            if (dt.valid)
                return dt.taiMillis;
        }
        catch { }
        zone = zone || this.dateTime.timezone;
        const opts = this._options;
        const style = opts.dateTimeStyle;
        const hasDate = style !== DateTimeStyle.TIME_ONLY;
        const hasTime = style !== DateTimeStyle.DATE_ONLY;
        const formats = [this.getFormat().replace(/[rv\u200F]/gi, '')];
        // If parsing with a zone offset doesn't work, try without the offset.
        if (formats[0].includes('Z'))
            formats.push(formats[0].replace('Z', ''));
        if (hasDate && hasTime)
            formats.push('IMM', 'IMS', 'ISM', 'ISS');
        else if (hasDate)
            formats.push('IM', 'IS');
        else
            formats.push('IxM', 'IxS');
        for (const format of formats) {
            try {
                const dt = parse(s, format, zone, opts.locale, true);
                if (dt.valid)
                    return dt.taiMillis;
            }
            catch { }
        }
        return null;
    }
    getFormat() {
        let format = '';
        for (const item of this.items) {
            if (item.hidden)
                continue;
            if ((item.editable || item.indicator) && item.format)
                format += item.format;
            else if (item.static && item.value != null)
                format += item.value.toString().replace(OCC2, '\u2082').replace(/([a-z])/gi, '[$1]').replace(/\xA0/g, ' ');
        }
        // Restore year/month/day order which might have been previously flipped to deal with right-to-left (RTL) markers.
        const origFormat = format;
        format = format.replace(/([yY]+)(\u200F[^a-zA-Z]+)(M+)(\u200F[^a-zA-Z]+)(D+)/, '$5$4$3$2$1');
        if (format === origFormat)
            format = format.replace(/(D+)(\u200F[^a-zA-Z]+)(M+)(\u200F[^a-zA-Z]+)([yY]+)/, '$5$4$3$2$1');
        if (format === origFormat)
            format = format.replace(/(M+)(\u200F[^a-zA-Z]+)(D+)(\u200F[^a-zA-Z]+)([yY]+)/, '$5$4$3$2$1');
        format = format.replace('rZ', 'RZ').replace(/[\u2004-\u200D]/g, '').replace(/[\u00A0\u2000-\u2003]/g, ' ');
        return format;
    }
}
TimeEditorComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: TimeEditorComponent, deps: [{ token: i0.NgZone }, { token: i0.ChangeDetectorRef }, { token: i1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Component });
TimeEditorComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.2.3", type: TimeEditorComponent, selector: "tbw-time-editor", inputs: { date: "date", options: "options", tai: "tai", min: "min", max: "max", timezone: "timezone", gregorianChangeDate: "gregorianChangeDate", promptForNative: "promptForNative", nativeDateTime: "nativeDateTime" }, outputs: { dateChange: "dateChange" }, providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TimeEditorComponent), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => TimeEditorComponent), multi: true }], usesInheritance: true, ngImport: i0, template: "<div class=\"tbw-dse-palette\"\n    [class.tbw-dse-floating]=\"floating\"\n    [style.left]=\"(this.headerX + this.headerDx) + 'px'\"\n    [style.top]=\"(this.headerY + this.headerDy) + 'px'\"\n    [style.z-index]=\"floating ? floatZ : 'auto'\">\n  <div *ngIf=\"floating\"\n      class=\"tbw-dse-header\"\n      (mousedown)=\"headerDragStart($event)\"\n      (touchstart)=\"headerDragStart($event)\">&nbsp;\n    <span *ngIf=\"showCloser\" class=\"tbw-dse-closer\"\n        (click)=\"doCloseAction()\" (touchstart)=\"doCloseAction()\">\n      <svg viewBox=\"6 6 12 12\" fill=\"currentColor\" stroke=\"currentColor\" width=\"0.4em\" height=\"0.4em\"\n          stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\">\n        <line x1=\"18\" y1=\"6\" x2=\"6\" y2=\"18\"></line>\n        <line x1=\"6\" y1=\"6\" x2=\"18\" y2=\"18\"></line>\n      </svg>\n    </span>\n  </div>\n  <div #wrapper class=\"tbw-dse-wrapper\"\n      [class.tbw-normal-background]=\"!disabled && !viewOnly\"\n      [class.tbw-disabled-background]=\"disabled\"\n      [class.tbw-view-only-background]=\"viewOnly\"\n      [class.tbw-dse-no-focus]=\"disabled\"\n      [class.tbw-dse-explicit-focus-outline]=\"hasFocus && !floating\"\n      [class.tbw-dse-clear-focus-outline]=\"!hasFocus\"\n      [class.tbw-dse-dse-rtl]=\"rtl\"\n      [class.tbw-dse-floating]=\"floating\"\n      [tabindex]=\"inputOff || hasHiddenInput ? '-1' : tabindex\"\n      [attr.contenteditable]=\"!inputOff && !disableContentEditable\"\n      [attr.inputmode]=\"inputOff ? 'none' : fakeUrl ? 'url' : 'decimal'\"\n      autocomplete=\"off\" autocapitalize=\"off\" autocorrect=\"off\"\n      (keydown)=\"onKeyDown($event)\" (keyup)=\"onKeyUp()\" (keypress)=\"onKeyPress($event)\"\n      (focus)=\"onFocus(true, $event)\" (blur)=\"onFocus(false, $event)\"\n      [ngClass]=\"getStaticBackgroundColor()\"\n      (mouseleave)=\"onMouseLeave()\"\n      (mouseup)=\"onMouseUp($event)\"\n      (touchmove)=\"onTouchMove($event)\"\n      (touchend)=\"onTouchEnd($event)\">\n    <div class=\"tbw-dse-state-indicator\" [@displayState]=\"displayState\">&nbsp;</div>\n    <div *ngFor=\"let item of displayItems\"\n        class=\"tbw-dse-digit dse-item-{{item.index}}\"\n        [ngClass]=\"(getClassForItem(item) || '') + ' ' + (getColorForItem(item) || '')\"\n        [class.tbw-dse-spinner]=\"item.spinner\"\n        contenteditable=\"false\"\n        [style.align-items]=\"getAlignmentForItem(item)\"\n        [style.color]=\"blank && (item.editable || item.indicator) ? 'transparent' : undefined\"\n        [style.cursor]=\"(item.editable || item.spinner) && !disabled && !viewOnly ? 'pointer' : 'default'\"\n        [style.line-height]=\"lineHeight\"\n        [class.tbw-dse-fade]=\"item.fade\"\n        [style.top]=\"item.divider || item.spinner ? undefined : baselineShift\"\n        [ngStyle]=\"item.width ? { width: item.width, 'white-space': 'nowrap', overflow: 'visible' } : null\"\n        (mousedown)=\"onMouseDown(item.index, $event)\"\n        (mouseup)=\"onMouseUp($event)\"\n        (contextmenu)=\"returnFalse()\"\n        (touchstart)=\"onTouchStart(item.index, $event)\">\n      <div class=\"tbw-dse-backgrounder\"\n          [ngClass]=\"getBackgroundColorForItem(item)\"\n          [style.bottom]=\"item.spinner || item.divider ? '0' : baselineShift\">&nbsp;</div>\n      <span *ngIf=\"swipeable(item, 1)\"\n          class=\"tbw-dse-draggable-extra\"\n          [ngStyle]=\"getStyleForItem(item, -digitHeight)\"\n      >{{(item.alt_swipeAbove || item.swipeAbove) ?? '\\u00A0'}}</span>\n      <span *ngIf=\"swipeable(item, -1)\"\n          class=\"tbw-dse-draggable-extra\"\n          [ngStyle]=\"getStyleForItem(item, digitHeight)\"\n      >{{(item.alt_swipeBelow || item.swipeBelow) ?? '\\u00A0'}}</span>\n      <span *ngIf=\"!item.spinner && !item.divider; else dividerOrSpinner\"\n          class=\"tbw-dse-draggable dse-item-{{item.index}} {{getCursorMode(item)}}\"\n          [style.animation-duration]=\"isSelected(item) && cursorRate\"\n          [class.tbw-dse-sized]=\"!!item.sizer\"\n          [ngStyle]=\"getStyleForItem(item, 0)\"\n      >{{filterDisplayChars(item.alt_value || item.value)}}</span>\n      <span *ngIf=\"item.sizer\"\n          class=\"tbw-dse-sizer\"\n      >{{item.sizer}}</span>\n      <ng-template #dividerOrSpinner>\n        <div *ngIf=\"item.divider\" class=\"tbw-dse-divider\">&nbsp;</div>\n        <div *ngIf=\"item.spinner\"\n            class=\"tbw-dse-spinner-layout\"\n            [class.tbw-dse-wide-spinner]=\"wideSpinner\"\n            (mousedown)=\"onMouseDown(item.index, $event)\"\n            (touchstart)=\"onTouchStart(item.index, $event)\">\n          <svg width=\"0.574em\" height=\"0.5em\" viewBox=\"0 0 353 307.3\">\n            <g>\n              <path (mousedown)=\"onMouseDown(SPIN_UP, $event)\"\n                  (touchstart)=\"onTouchStart(SPIN_UP, $event)\"\n                  [ngClass]=\"(getBackgroundColorForItem(null, SPIN_UP) || '') + ' ' + (getColorForItem(null, SPIN_UP) || '')\"\n                  d=\"M 34.1 250.8 L 318.4 250.8 C 336 250.8 334.5 222.4 332.7 208.5 C 332.1 204.2 329.2 197.5 326.6 193.8 L 196.7 28.9 C 185.5 8.6 166.8 8.6 155.9 28.9 L 25.7 193.8 C 23.1 197.5 20.4 204.1 19.9 208.5 C 17.9 222.4 16.5 250.8 34.1 250.8 Z\"/>\n            </g>\n          </svg>\n          <svg width=\"0.574em\" height=\"0.5em\" viewBox=\"0 307.3 353 307.3\">\n            <g>\n              <path (mousedown)=\"onMouseDown(SPIN_DOWN, $event)\"\n                  (touchstart)=\"onTouchStart(SPIN_DOWN, $event)\"\n                  [ngClass]=\"(getBackgroundColorForItem(null, SPIN_DOWN) || '') + ' ' + (getColorForItem(null, SPIN_DOWN) || '')\"\n                  d=\"M 25.6 419.7 L 155.6 585.1 C 167 605 185.5 605 196.9 585.1 L 327 419.7 C 329.5 416.1 332.2 409.6 332.8 405.3 C 334.6 391 335.9 362.6 318.7 362.6 L 33.8 362.6 C 16.6 362.6 18 391 19.8 405.3 C 20.3 409.6 23.1 416.1 25.6 419.7 Z\"/>\n            </g>\n          </svg>\n        </div>\n      </ng-template>\n    </div>\n    <div class=\"tbw-dse-focus-indicator\" [class.tbw-dse-show-focus]=\"floating && hasFocus\">&nbsp;</div>\n    <div #emSizer class=\"tbw-dse-em-sizer\">&nbsp;</div>\n  </div>\n  <div *ngIf=\"floating\" class=\"tbw-dse-footer\">\n    <ng-container *ngFor=\"let btn of buttons; let last = last\">\n      <span\n          class=\"tbw-dse-button\"\n          tabindex=\"-1\"\n          (click)=\"onButtonKey(btn.key)\"\n          (focus)=\"!showPasteInput && onButtonFocus()\"><div [innerHTML]=\"btn.html || htmlEscape(btn.label)\"></div></span>\n      <span *ngIf=\"!last\" class=\"tbw-dse-button-spacer\">&nbsp;</span>\n    </ng-container>\n    <div class=\"tbw-dse-paste-hint\" [style.display]=\"showPasteInput ? 'block' : 'none'\">paste here</div>\n    <input name='paste-input'\n        type=\"text\"\n        (input)=\"onPasteInput()\"\n        (blur)=\"onPasteBlur()\"\n        [style.display]=\"showPasteInput ? 'inline-block' : 'none'\">\n  </div>\n</div>\n", styles: [":host{display:inline-block}.tbw-dse-draggable{position:relative;touch-action:manipulation}.tbw-dse-draggable-extra{display:inline-block;height:0;max-height:0;overflow:visible;position:relative;touch-action:manipulation;white-space:nowrap}.tbw-dse-sizer{background-color:transparent;color:transparent;display:inline-block;height:.1px;margin-bottom:-.1px;overflow:hidden;pointer-events:none;position:relative;touch-action:manipulation;-webkit-user-select:none;user-select:none;white-space:pre}.tbw-dse-backgrounder{height:100%;position:absolute;touch-action:manipulation;width:100%}.tbw-dse-sized{display:inline-block;overflow:visible;text-decoration:inherit;touch-action:manipulation;white-space:nowrap}.tbw-dse-dse-rtl{direction:rtl}.tbw-dse-dse-rtl .tbw-dse-spinner{margin-left:-.1em;margin-right:0}.tbw-dse-em-sizer{background-color:transparent;color:transparent;height:.1px;pointer-events:none;position:absolute;touch-action:manipulation;width:1em}\n", ".tbw-te-subscript{font-size:.66em;font-family:sans-serif;position:relative}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], animations: [BACKGROUND_ANIMATIONS] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: TimeEditorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'tbw-time-editor', animations: [BACKGROUND_ANIMATIONS], providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TimeEditorComponent), multi: true },
                        { provide: NG_VALIDATORS, useExisting: forwardRef(() => TimeEditorComponent), multi: true }], template: "<div class=\"tbw-dse-palette\"\n    [class.tbw-dse-floating]=\"floating\"\n    [style.left]=\"(this.headerX + this.headerDx) + 'px'\"\n    [style.top]=\"(this.headerY + this.headerDy) + 'px'\"\n    [style.z-index]=\"floating ? floatZ : 'auto'\">\n  <div *ngIf=\"floating\"\n      class=\"tbw-dse-header\"\n      (mousedown)=\"headerDragStart($event)\"\n      (touchstart)=\"headerDragStart($event)\">&nbsp;\n    <span *ngIf=\"showCloser\" class=\"tbw-dse-closer\"\n        (click)=\"doCloseAction()\" (touchstart)=\"doCloseAction()\">\n      <svg viewBox=\"6 6 12 12\" fill=\"currentColor\" stroke=\"currentColor\" width=\"0.4em\" height=\"0.4em\"\n          stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\">\n        <line x1=\"18\" y1=\"6\" x2=\"6\" y2=\"18\"></line>\n        <line x1=\"6\" y1=\"6\" x2=\"18\" y2=\"18\"></line>\n      </svg>\n    </span>\n  </div>\n  <div #wrapper class=\"tbw-dse-wrapper\"\n      [class.tbw-normal-background]=\"!disabled && !viewOnly\"\n      [class.tbw-disabled-background]=\"disabled\"\n      [class.tbw-view-only-background]=\"viewOnly\"\n      [class.tbw-dse-no-focus]=\"disabled\"\n      [class.tbw-dse-explicit-focus-outline]=\"hasFocus && !floating\"\n      [class.tbw-dse-clear-focus-outline]=\"!hasFocus\"\n      [class.tbw-dse-dse-rtl]=\"rtl\"\n      [class.tbw-dse-floating]=\"floating\"\n      [tabindex]=\"inputOff || hasHiddenInput ? '-1' : tabindex\"\n      [attr.contenteditable]=\"!inputOff && !disableContentEditable\"\n      [attr.inputmode]=\"inputOff ? 'none' : fakeUrl ? 'url' : 'decimal'\"\n      autocomplete=\"off\" autocapitalize=\"off\" autocorrect=\"off\"\n      (keydown)=\"onKeyDown($event)\" (keyup)=\"onKeyUp()\" (keypress)=\"onKeyPress($event)\"\n      (focus)=\"onFocus(true, $event)\" (blur)=\"onFocus(false, $event)\"\n      [ngClass]=\"getStaticBackgroundColor()\"\n      (mouseleave)=\"onMouseLeave()\"\n      (mouseup)=\"onMouseUp($event)\"\n      (touchmove)=\"onTouchMove($event)\"\n      (touchend)=\"onTouchEnd($event)\">\n    <div class=\"tbw-dse-state-indicator\" [@displayState]=\"displayState\">&nbsp;</div>\n    <div *ngFor=\"let item of displayItems\"\n        class=\"tbw-dse-digit dse-item-{{item.index}}\"\n        [ngClass]=\"(getClassForItem(item) || '') + ' ' + (getColorForItem(item) || '')\"\n        [class.tbw-dse-spinner]=\"item.spinner\"\n        contenteditable=\"false\"\n        [style.align-items]=\"getAlignmentForItem(item)\"\n        [style.color]=\"blank && (item.editable || item.indicator) ? 'transparent' : undefined\"\n        [style.cursor]=\"(item.editable || item.spinner) && !disabled && !viewOnly ? 'pointer' : 'default'\"\n        [style.line-height]=\"lineHeight\"\n        [class.tbw-dse-fade]=\"item.fade\"\n        [style.top]=\"item.divider || item.spinner ? undefined : baselineShift\"\n        [ngStyle]=\"item.width ? { width: item.width, 'white-space': 'nowrap', overflow: 'visible' } : null\"\n        (mousedown)=\"onMouseDown(item.index, $event)\"\n        (mouseup)=\"onMouseUp($event)\"\n        (contextmenu)=\"returnFalse()\"\n        (touchstart)=\"onTouchStart(item.index, $event)\">\n      <div class=\"tbw-dse-backgrounder\"\n          [ngClass]=\"getBackgroundColorForItem(item)\"\n          [style.bottom]=\"item.spinner || item.divider ? '0' : baselineShift\">&nbsp;</div>\n      <span *ngIf=\"swipeable(item, 1)\"\n          class=\"tbw-dse-draggable-extra\"\n          [ngStyle]=\"getStyleForItem(item, -digitHeight)\"\n      >{{(item.alt_swipeAbove || item.swipeAbove) ?? '\\u00A0'}}</span>\n      <span *ngIf=\"swipeable(item, -1)\"\n          class=\"tbw-dse-draggable-extra\"\n          [ngStyle]=\"getStyleForItem(item, digitHeight)\"\n      >{{(item.alt_swipeBelow || item.swipeBelow) ?? '\\u00A0'}}</span>\n      <span *ngIf=\"!item.spinner && !item.divider; else dividerOrSpinner\"\n          class=\"tbw-dse-draggable dse-item-{{item.index}} {{getCursorMode(item)}}\"\n          [style.animation-duration]=\"isSelected(item) && cursorRate\"\n          [class.tbw-dse-sized]=\"!!item.sizer\"\n          [ngStyle]=\"getStyleForItem(item, 0)\"\n      >{{filterDisplayChars(item.alt_value || item.value)}}</span>\n      <span *ngIf=\"item.sizer\"\n          class=\"tbw-dse-sizer\"\n      >{{item.sizer}}</span>\n      <ng-template #dividerOrSpinner>\n        <div *ngIf=\"item.divider\" class=\"tbw-dse-divider\">&nbsp;</div>\n        <div *ngIf=\"item.spinner\"\n            class=\"tbw-dse-spinner-layout\"\n            [class.tbw-dse-wide-spinner]=\"wideSpinner\"\n            (mousedown)=\"onMouseDown(item.index, $event)\"\n            (touchstart)=\"onTouchStart(item.index, $event)\">\n          <svg width=\"0.574em\" height=\"0.5em\" viewBox=\"0 0 353 307.3\">\n            <g>\n              <path (mousedown)=\"onMouseDown(SPIN_UP, $event)\"\n                  (touchstart)=\"onTouchStart(SPIN_UP, $event)\"\n                  [ngClass]=\"(getBackgroundColorForItem(null, SPIN_UP) || '') + ' ' + (getColorForItem(null, SPIN_UP) || '')\"\n                  d=\"M 34.1 250.8 L 318.4 250.8 C 336 250.8 334.5 222.4 332.7 208.5 C 332.1 204.2 329.2 197.5 326.6 193.8 L 196.7 28.9 C 185.5 8.6 166.8 8.6 155.9 28.9 L 25.7 193.8 C 23.1 197.5 20.4 204.1 19.9 208.5 C 17.9 222.4 16.5 250.8 34.1 250.8 Z\"/>\n            </g>\n          </svg>\n          <svg width=\"0.574em\" height=\"0.5em\" viewBox=\"0 307.3 353 307.3\">\n            <g>\n              <path (mousedown)=\"onMouseDown(SPIN_DOWN, $event)\"\n                  (touchstart)=\"onTouchStart(SPIN_DOWN, $event)\"\n                  [ngClass]=\"(getBackgroundColorForItem(null, SPIN_DOWN) || '') + ' ' + (getColorForItem(null, SPIN_DOWN) || '')\"\n                  d=\"M 25.6 419.7 L 155.6 585.1 C 167 605 185.5 605 196.9 585.1 L 327 419.7 C 329.5 416.1 332.2 409.6 332.8 405.3 C 334.6 391 335.9 362.6 318.7 362.6 L 33.8 362.6 C 16.6 362.6 18 391 19.8 405.3 C 20.3 409.6 23.1 416.1 25.6 419.7 Z\"/>\n            </g>\n          </svg>\n        </div>\n      </ng-template>\n    </div>\n    <div class=\"tbw-dse-focus-indicator\" [class.tbw-dse-show-focus]=\"floating && hasFocus\">&nbsp;</div>\n    <div #emSizer class=\"tbw-dse-em-sizer\">&nbsp;</div>\n  </div>\n  <div *ngIf=\"floating\" class=\"tbw-dse-footer\">\n    <ng-container *ngFor=\"let btn of buttons; let last = last\">\n      <span\n          class=\"tbw-dse-button\"\n          tabindex=\"-1\"\n          (click)=\"onButtonKey(btn.key)\"\n          (focus)=\"!showPasteInput && onButtonFocus()\"><div [innerHTML]=\"btn.html || htmlEscape(btn.label)\"></div></span>\n      <span *ngIf=\"!last\" class=\"tbw-dse-button-spacer\">&nbsp;</span>\n    </ng-container>\n    <div class=\"tbw-dse-paste-hint\" [style.display]=\"showPasteInput ? 'block' : 'none'\">paste here</div>\n    <input name='paste-input'\n        type=\"text\"\n        (input)=\"onPasteInput()\"\n        (blur)=\"onPasteBlur()\"\n        [style.display]=\"showPasteInput ? 'inline-block' : 'none'\">\n  </div>\n</div>\n", styles: [":host{display:inline-block}.tbw-dse-draggable{position:relative;touch-action:manipulation}.tbw-dse-draggable-extra{display:inline-block;height:0;max-height:0;overflow:visible;position:relative;touch-action:manipulation;white-space:nowrap}.tbw-dse-sizer{background-color:transparent;color:transparent;display:inline-block;height:.1px;margin-bottom:-.1px;overflow:hidden;pointer-events:none;position:relative;touch-action:manipulation;-webkit-user-select:none;user-select:none;white-space:pre}.tbw-dse-backgrounder{height:100%;position:absolute;touch-action:manipulation;width:100%}.tbw-dse-sized{display:inline-block;overflow:visible;text-decoration:inherit;touch-action:manipulation;white-space:nowrap}.tbw-dse-dse-rtl{direction:rtl}.tbw-dse-dse-rtl .tbw-dse-spinner{margin-left:-.1em;margin-right:0}.tbw-dse-em-sizer{background-color:transparent;color:transparent;height:.1px;pointer-events:none;position:absolute;touch-action:manipulation;width:1em}\n", ".tbw-te-subscript{font-size:.66em;font-family:sans-serif;position:relative}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.NgZone }, { type: i0.ChangeDetectorRef }, { type: i1.DomSanitizer }]; }, propDecorators: { dateChange: [{
                type: Output
            }], date: [{
                type: Input
            }], options: [{
                type: Input
            }], tai: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], timezone: [{
                type: Input
            }], gregorianChangeDate: [{
                type: Input
            }], promptForNative: [{
                type: Input
            }], nativeDateTime: [{
                type: Input
            }] } });

class TubularNgWidgetsModule {
}
TubularNgWidgetsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: TubularNgWidgetsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TubularNgWidgetsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.3", ngImport: i0, type: TubularNgWidgetsModule, declarations: [AngleEditorComponent,
        CalendarPanelComponent,
        FormErrorDisplayComponent,
        ShrinkWrapComponent,
        TimeEditorComponent], imports: [BrowserModule,
        BrowserAnimationsModule,
        CommonModule], exports: [AngleEditorComponent,
        CalendarPanelComponent,
        FormErrorDisplayComponent,
        ShrinkWrapComponent,
        TimeEditorComponent] });
TubularNgWidgetsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: TubularNgWidgetsModule, imports: [BrowserModule,
        BrowserAnimationsModule,
        CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: TubularNgWidgetsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        AngleEditorComponent,
                        CalendarPanelComponent,
                        FormErrorDisplayComponent,
                        ShrinkWrapComponent,
                        TimeEditorComponent
                    ],
                    imports: [
                        BrowserModule,
                        BrowserAnimationsModule,
                        CommonModule
                    ],
                    exports: [
                        AngleEditorComponent,
                        CalendarPanelComponent,
                        FormErrorDisplayComponent,
                        ShrinkWrapComponent,
                        TimeEditorComponent
                    ]
                }]
        }] });

/*
 * Public API Surface of tubular-ng-widgets
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AngleEditorComponent, AngleStyle, BACKGROUND_ANIMATIONS, CalendarPanelComponent, DateFieldOrder, DateTimeStyle, DigitSequenceEditorDirective, FORWARD_TAB_DELAY, FormErrorDisplayComponent, HourStyle, MeridiemStyle, OPTIONS_DATE_ONLY, OPTIONS_ISO, OPTIONS_ISO_DATE, OPTIONS_ISO_TIME, ShrinkWrapComponent, TimeEditorComponent, TimeEditorLimit, TubularNgWidgetsModule, YearStyle, getThePoint, isNilOrBlank };
